import { HEALTH_RECORDS_ENABLED } from '../../../util/healthRecords';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ClinicalRecordModal from './ClinicalRecordModal';
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog, faUsers, faSearch, faCheck, faTag } from "@fortawesome/free-solid-svg-icons";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import { DatesSetArg, EventClickArg, EventInput } from "@fullcalendar/core";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";
import { DateClickArg } from "@fullcalendar/interaction";
import { useAuth0 } from '../../../auth/useAuth0';
import { useBusinessContext } from "../../../context/BusinessContext";
import { useAlert } from "../../../context/alert/AlertContext";
import { useConfirm } from "../../../context/confirm/ConfirmContext";
import { useNavigation } from "../../../context/NavigationContext";
import { MENU } from "../../util/Utils";
import Drawer from "../../commons/drawer/Drawer";
import PayoutInfoHint from '../../commons/payout-hint/PayoutInfoHint';
import {
    Appointment,
    AppointmentPaymentType,
    RescheduleStatus,
    RecurrenceFreq,
    RecurrenceInput,
} from "../../../model/appointment/Appointment";
import { getBusinessHealthPlans } from "../../../service/business/health-plan-service";
import { HealthPlan } from "../../../model/business/HealthPlan";
import {
    AppointmentPaymentStatus,
    APPOINTMENT_PAYMENT_LABEL,
} from "../../../model/appointment/AppointmentPaymentStatus";
import {
    APPOINTMENT_STATUS_OPTIONS,
    AppointmentStatus,
    STATUS_COLOR,
    HIDDEN_FROM_CALENDAR,
} from "../../../model/appointment/AppointmentStatus";
import { AppointmentInput } from "../../../service/appointment/AppointmentInput";
import {
    deleteAppointment,
    getAppointmentById,
    getAppointmentHistory,
    getAppointmentsByBusinessAndRange,
    getAppointmentsByClient,
    getAppointmentsBySeries,
    proposeReschedule,
    saveAppointment,
    createRecurringAppointment,
    respondSeries,
    deleteSeries,
} from "../../../service/appointment/appointment-service";
import { AppointmentHistoryEvent } from "../../../model/appointment/AppointmentHistoryEvent";
import {
    createStripeCheckoutSession,
    recordManualPayment,
    getTransactionsByAppointment,
} from "../../../service/payment/transaction-service";
import {
    captureClientHold,
    chargeClientCard,
    cancelClientHold,
} from "../../../service/payment/payment-service";
import {
    PaymentMethod,
    parseAcceptedMethods,
    Transaction,
    TransactionStatus,
} from "../../../model/payment/Transaction";
import { Service, effectiveRepasse } from "../../../model/service/Service";
import { TeamMember } from "../../../model/teammember/TeamMember";
import { useTimeFormat, fcTimeFormat, formatClock, formatHHmm } from "../../../util/timeFormat";
import DateTimeField from "../../commons/datetime/DateTimeField";
import TimeSelect from "../../commons/datetime/TimeSelect";
import EventDrawer from "./EventDrawer";
import { getEventsByBusinessAndRange } from "../../../service/event/event-service";
import { Event as GroupEvent } from "../../../model/event/Event";
import { BusinessHours } from "../../../model/business/BusinessHours";
import { Client } from "../../../model/client/Client";
import { getServicesByBusinessId } from "../../../service/service/service-service";
import { TimeBlock, TimeBlockInput } from "../../../model/timeblock/TimeBlock";
import {
    getTimeBlocksByBusinessAndRange,
    saveTimeBlock,
    createRecurringTimeBlock,
    deleteTimeBlock,
    deleteTimeBlockSeries,
} from "../../../service/timeblock/time-block-service";
import { getTeamMembersByBusinessId, getTeamMemberCommission } from "../../../service/teammember/team-member-service";
import { findBusinessDetailsByBusinessId } from "../../../service/business/business-service";
import { CommissionConfig } from "../../../model/teammember/Commission";
import { getMyPermissionGrant, getMyMaxDiscountRate, PermissionGrant } from "../../../service/permission/permission-service";
import {
    resolveTeamMemberColor,
    readableTextColor,
} from "../../../util/teamMemberColor";
import {
    findBusinessHoursByBusinessId,
    findBusinessSettingByBusinessId,
} from "../../../service/business/business-service";
import { BusinessSettings } from "../../../model/business/BusinessSettings";
import {
    getAllClientsByBusinessId,
    saveClient,
    setClientFavorite,
    bookingClientLookup,
    BookingClient,
} from "../../../service/client/client-service";
import { currencySymbol, currencyCode } from "../../../util/money";
import { formatPhone, isPhoneComplete, resolvePhoneCountry } from "../../../util/phone";
import MoneyInput from "../../commons/money-input/MoneyInput";
import "./CalendarComponent.css";

/**
 * Canonical day-of-week strings used across the whole project: UPPER full.
 * The backend (DayOfWeekNormalizer) and the BusinessCalendar/TeamMember
 * components only write these values, so no normalization is needed here.
 */
/** Max length for the appointment notes field. The DB column is TEXT (unbounded),
 *  so we cap here to avoid unbounded growth; kept in sync with the backend guard. */
const NOTES_MAX = 1000;

const DAYS_OF_WEEK = [
    "SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY",
];

const DOW_TO_FC: Record<string, number> = {
    SUNDAY: 0, MONDAY: 1, TUESDAY: 2, WEDNESDAY: 3,
    THURSDAY: 4, FRIDAY: 5, SATURDAY: 6,
};

const dayOfWeekOf = (d: Date) => DAYS_OF_WEEK[d.getDay()];

/** Accepts "HH:mm" or "HH:mm:ss"; returns minutes since midnight. */
const timeToMinutes = (t: string | null | undefined): number => {
    if (!t) return 0;
    const [h, m] = t.split(":");
    return (parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0);
};

const minutesOfDay = (d: Date) => d.getHours() * 60 + d.getMinutes();

const hhmm = (t: string) => (t || "00:00").slice(0, 5);

/** Minutes-since-midnight → "HH:mm" (FullCalendar businessHours format). */
const minutesToHHmm = (mins: number): string => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h < 10 ? "0" : ""}${h}:${m < 10 ? "0" : ""}${m}`;
};

/**
 * The DAY open-windows (in minutes) for a business day: [open, close) split by an
 * optional mid-day break. A midnight close (00:00) counts as end-of-day (24:00).
 * Overnight is handled SEPARATELY by callers because it belongs to the NEXT day.
 */
const dayWindowsForBusinessDay = (b: BusinessHours): Array<{ start: number; end: number }> => {
    const wins: Array<{ start: number; end: number }> = [];
    if (!b.openTime || !b.closeTime) return wins;
    const open = timeToMinutes(b.openTime);
    const closeRaw = timeToMinutes(b.closeTime);
    const close = closeRaw === 0 ? 24 * 60 : closeRaw; // midnight close = end of day
    if (close <= open) return wins;
    const bs = b.breakStart ? timeToMinutes(b.breakStart) : null;
    const be = b.breakEnd ? timeToMinutes(b.breakEnd) : null;
    if (bs !== null && be !== null && bs > open && be < close && bs < be) {
        wins.push({ start: open, end: bs });
        wins.push({ start: be, end: close });
    } else {
        wins.push({ start: open, end: close });
    }
    return wins;
};

/** Minutes of an overnight block end (NEXT day's early morning), or 0 when none. */
const overnightMinutes = (b: BusinessHours): number => (b.overnightEnd ? timeToMinutes(b.overnightEnd) : 0);

const validateBusinessHours = (
    start: Date,
    end: Date,
    businessHours: BusinessHours[],
    t: TFunction
): string | null => {
    if (businessHours.length === 0) return null;
    if (dayOfWeekOf(start) !== dayOfWeekOf(end)) {
        return t('calendar.validation.sameDay');
    }
    const dayKey = dayOfWeekOf(start);
    const day = businessHours.find((b) => b.dayOfWeek === dayKey);
    // Overnight blocks land on the NEXT day's early morning, so this day's bookable
    // windows = its own day windows + the overnight that spilled from the PREVIOUS day.
    const prev = new Date(start);
    prev.setDate(prev.getDate() - 1);
    const prevDay = businessHours.find((b) => b.dayOfWeek === dayOfWeekOf(prev));
    const windows = day ? dayWindowsForBusinessDay(day) : [];
    if (prevDay) {
        const ov = overnightMinutes(prevDay);
        if (ov > 0) windows.push({ start: 0, end: ov });
    }
    if (windows.length === 0) return t('calendar.validation.businessClosed', { day: t('settings.calendar.days.' + dayKey) });
    // The appointment must fit fully inside ONE open window.
    const s = minutesOfDay(start);
    const e = minutesOfDay(end);
    const insideOne = windows.some((w) => s >= w.start && e <= w.end);
    if (!insideOne) {
        return day
            ? t('calendar.validation.outsideHours', { open: hhmm(day.openTime), close: hhmm(day.closeTime) })
            : t('calendar.validation.outsideHoursNoRange');
    }
    return null;
};

/** Add minutes to a datetime-local string ("YYYY-MM-DDTHH:mm"), staying in local
 *  time (no UTC shift). Used to auto-fill the reschedule end from the duration. */
const addMinutesLocal = (local: string, mins: number): string => {
    const [datePart, timePart] = local.split("T");
    if (!datePart || !timePart) return local;
    const [y, m, d] = datePart.split("-").map(Number);
    const [hh, mm] = timePart.split(":").map(Number);
    if ([y, m, d, hh, mm].some((n) => Number.isNaN(n))) return local;
    const dt = new Date(y, m - 1, d, hh, mm);
    dt.setMinutes(dt.getMinutes() + mins);
    const p = (n: number) => String(n).padStart(2, "0");
    return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}T${p(dt.getHours())}:${p(dt.getMinutes())}`;
};

const validateWorkingHours = (
    start: Date,
    end: Date,
    member: TeamMember,
    t: TFunction
): string | null => {
    const whs = member.workingHours ?? [];
    if (whs.length === 0) return null;
    const dayKey = dayOfWeekOf(start);
    const day = whs.find((w) => w.dayOfWeek === dayKey);
    if (!day) return t('calendar.validation.memberNoWork', { name: member.firstName, day: t('settings.calendar.days.' + dayKey) });
    const wStart = timeToMinutes(day.startTime);
    const wEnd = timeToMinutes(day.endTime);
    if (minutesOfDay(start) < wStart || minutesOfDay(end) > wEnd) {
        return t('calendar.validation.memberHours', { name: member.firstName, open: hhmm(day.startTime), close: hhmm(day.endTime) });
    }
    if (!day.allowBookingOnIntervalTime && day.startIntervalTime && day.endIntervalTime) {
        const bStart = timeToMinutes(day.startIntervalTime);
        const bEnd = timeToMinutes(day.endIntervalTime);
        if (minutesOfDay(start) < bEnd && minutesOfDay(end) > bStart) {
            return t('calendar.validation.memberBreak', { name: member.firstName, start: hhmm(day.startIntervalTime), end: hhmm(day.endIntervalTime) });
        }
    }
    return null;
};

const validateServiceMatch = (serviceId: string, member: TeamMember, t: TFunction): string | null => {
    // A member must EXPLICITLY offer the service. No services assigned → performs
    // nothing (the old "empty services = performs everything" fallback is gone).
    const ids = member.serviceIds ?? [];
    if (!ids.includes(serviceId)) {
        return t('calendar.validation.memberNoService', { name: member.firstName });
    }
    return null;
};

type Draft = {
    id?: string;
    /** Linked Client id when picked from autocomplete; null = walk-in. */
    clientId: string | null;
    customerName: string;
    customerPhone: string;
    customerEmail: string;
    serviceId: string;
    teamMemberId: string;
    /** "YYYY-MM-DDTHH:mm" — datetime-local input value. */
    startTime: string;
    endTime: string;
    status: AppointmentStatus;
    /** Persisted payment rollup — read-only from the calendar's POV. */
    paymentStatus?: AppointmentPaymentStatus;
    /** Pre-authorization hold (read-only here); settled on the payment screen. */
    holdPaymentIntentId?: string | null;
    holdAmount?: number | null;
    notes: string;
    /** House-call address (read-only here) — set by the client when booking an external service. */
    externalLocation?: string | null;
    /** Reason captured when this appointment is cancelled. */
    cancellationReason?: string | null;
    /** True when the client cancelled it online (read-only). */
    cancelledByClient?: boolean | null;
    /** A cancellation fee is owed but wasn't auto-charged — staff must collect it. */
    cancellationFeeOutstanding?: boolean | null;
    /** Cancellation fee amount computed at cancel time. */
    cancellationFeeAmount?: number | null;
    /** Open/declined reschedule proposal, surfaced as status hints. */
    rescheduleStatus?: RescheduleStatus | null;
    proposedStartTime?: string | null;
    proposedEndTime?: string | null;
    /** Set when this appointment is one occurrence of a recurring series. */
    seriesId?: string | null;
    recurrenceFreq?: RecurrenceFreq | null;
    /** PARTICULAR (default) or CONVENIO (covered by a health plan). */
    paymentType?: AppointmentPaymentType | null;
    /** health_plan id when paymentType = CONVENIO. */
    healthPlanId?: string | null;
    /** Optional copay (coparticipação) the client pays for a CONVENIO appointment. */
    copayAmount?: number | null;
    /** Optional date (yyyy-MM-dd) to remind the client to rebook (D5). */
    rebookReminderOn?: string | null;
};

const formatClientLabel = (c: Pick<Client, "firstName" | "lastName">) =>
    `${c.firstName}${c.lastName ? " " + c.lastName : ""}`;

/** Recurrence frequency keys (used with t('calendar.freq.*') and t('calendar.freqPt.*')). */
const FREQ_KEYS: RecurrenceFreq[] = ["DAILY", "WEEKLY", "BIWEEKLY", "MONTHLY", "WEEKLY_DAYS"];

/** Weekday picker options (value matches the backend DayOfWeek name). */
const WEEKDAY_OPTIONS: { value: string; label: string }[] = [
    { value: "MONDAY", label: "Seg" },
    { value: "TUESDAY", label: "Ter" },
    { value: "WEDNESDAY", label: "Qua" },
    { value: "THURSDAY", label: "Qui" },
    { value: "FRIDAY", label: "Sex" },
    { value: "SATURDAY", label: "Sáb" },
    { value: "SUNDAY", label: "Dom" },
];

/** Date-only label for an ISO local datetime (e.g. "14 set 2026"). */
const formatDateOnly = (iso?: string | null): string => {
    if (!iso) return "";
    const d = new Date(iso.length === 16 ? `${iso}:00` : iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

/** Strip every non-digit so phone numbers compare regardless of mask. */
const onlyDigits = (s: string | null | undefined): string => (s ?? "").replace(/\D/g, "");

/** True when the user hasn't edited any editable field since the draft loaded. */
const draftUntouched = (cur: Draft | null, base: Draft | null): boolean => {
    if (!cur || !base) return false;
    const keys: (keyof Draft)[] = [
        "customerName", "customerPhone", "customerEmail", "serviceId",
        "teamMemberId", "startTime", "endTime", "status", "notes", "clientId",
    ];
    return keys.every((k) => (cur[k] ?? "") === (base[k] ?? ""));
};

/** Human-friendly label for a proposed ISO local datetime (e.g. "2 out, 15:00"). */
const formatProposedTime = (iso: string): string => {
    const d = new Date(iso.length === 16 ? `${iso}:00` : iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
};

const splitFullName = (full: string): { firstName: string; lastName: string } => {
    const parts = full.trim().split(/\s+/);
    if (parts.length === 0 || parts[0] === "") return { firstName: "", lastName: "" };
    if (parts.length === 1) return { firstName: parts[0], lastName: "" };
    return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
};

const pad = (n: number) => String(n).padStart(2, "0");
const toLocalIso = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const toLocalInput = (iso: string) => (iso?.length >= 16 ? iso.slice(0, 16) : iso ?? "");
const withSeconds = (s: string) => (s.length === 16 ? s + ":00" : s);
/** Local YYYY-MM-DD for a Date. */
const ymdOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
/** Monday 00:00 of the week containing `d`. */
const startOfWeekMon = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    const dow = (x.getDay() + 6) % 7; // 0 = Monday
    x.setDate(x.getDate() - dow);
    return x;
};

const CalendarComponent: React.FC = () => {
    const { t, i18n } = useTranslation();
    const confirmDialog = useConfirm();
    const timeFormat = useTimeFormat();
    const calendarRef = useRef<FullCalendar | null>(null);
    /** The actual scrolling element wrapping FullCalendar. */
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const { getAccessTokenSilently } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const { setAlert } = useAlert();
    const {
        setActiveMenu,
        setPendingClient,
        pendingAppointmentId,
        setPendingAppointmentId,
        pendingEventId,
        setPendingEventId,
    } = useNavigation();

    const businessIdNum = Number(selectedBusiness?.id) || 0;
    const businessIdStr = selectedBusiness?.id ? String(selectedBusiness.id) : "";

    const [appointments, setAppointments] = useState<Appointment[]>([]);
    /** Group events (workshops) shown on the calendar alongside appointments. */
    const [groupEvents, setGroupEvents] = useState<GroupEvent[]>([]);
    const [eventDrawerOpen, setEventDrawerOpen] = useState(false);
    /** null id = create mode. */
    const [eventDrawerId, setEventDrawerId] = useState<string | null>(null);
    const [eventDrawerStart, setEventDrawerStart] = useState<string | null>(null);
    const [services, setServices] = useState<Service[]>([]);
    const eventTypes = useMemo(() => services.filter((sv) => sv.kind === "EVENT"), [services]);
    const [businessCountry, setBusinessCountry] = useState<string | null>(null);
    const [clinicalApptId, setClinicalApptId] = useState<string | null>(null);
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
    const [businessHours, setBusinessHours] = useState<BusinessHours[]>([]);
    const [bizSettings, setBizSettings] = useState<BusinessSettings | null>(null);
    /** True once the services + team-member bundle has loaded. Appointment events
     *  are colored by their team member, so we hold off painting them until this
     *  is ready — otherwise they flash in the fallback status color first and then
     *  snap to the team color when `teamMembers` arrives. */
    const [coreLoaded, setCoreLoaded] = useState(false);
    const [clients, setClients] = useState<Client[]>([]);
    /** Server-side booking matches — used ONLY when the full client list isn't
     *  available (a member without CLIENTS permission): the picker searches by
     *  name via bookingClientLookup (contact-only, CALENDAR-gated). */
    const [bookingMatches, setBookingMatches] = useState<BookingClient[]>([]);
    const [showClientDropdown, setShowClientDropdown] = useState(false);
    const [savingNewClient, setSavingNewClient] = useState(false);
    /** When linked client is missing phone/email that the user typed, also write it back on Save. Default ON. */
    const [updateLinkedClient, setUpdateLinkedClient] = useState(true);
    /** In-flight flag for the "update client phone" action next to a mismatched phone. */
    const [updatingClientPhone, setUpdatingClientPhone] = useState(false);
    /** "Suggest new time" editor (reschedule proposal sent to the customer). */
    const [showReschedule, setShowReschedule] = useState(false);
    const [rescheduleStart, setRescheduleStart] = useState("");
    const [rescheduleEnd, setRescheduleEnd] = useState("");
    const [proposing, setProposing] = useState(false);
    /** Set when the open appointment changed server-side in a way that conflicts
     *  with the editable form (e.g. the customer accepted a reschedule). Offers a
     *  "reload" banner instead of clobbering in-progress edits. */
    const [externalUpdate, setExternalUpdate] = useState<Appointment | null>(null);
    /** Last server-known core fields of the open appointment, to detect outside changes. */
    const syncedRef = useRef<{ startTime?: string; endTime?: string; status?: string } | null>(null);
    /** The draft exactly as loaded — lets the observer tell "user edited" from "untouched". */
    const openSnapshotRef = useRef<Draft | null>(null);
    /** Appointment awaiting a linked-client snapshot refresh once `clients` loads. */
    const pendingClientSyncRef = useRef<Appointment | null>(null);
    const [range, setRange] = useState<{ start: string; end: string } | null>(null);

    // Team members hidden from the calendar (still selectable in the form).
    const [hiddenTeamMembers, setHiddenTeamMembers] = useState<Set<string>>(new Set());
    // Services hidden from the grid (multi-select, mirrors hiddenTeamMembers).
    // Empty = all services visible. Filters the appointment + prep events only.
    const [hiddenServices, setHiddenServices] = useState<Set<string>>(new Set());

    /** The caller's CALENDAR grant — drives own/all scoping of the UI. When
     *  own-scoped (LIMITED), the team picker and the booking/block forms are
     *  restricted to the caller's own team member. */
    const [calGrant, setCalGrant] = useState<PermissionGrant | null>(null);

    /** The caller's CHECKOUT grant — gates the record-payment editor on the
     *  appointment Payment tab. null = not loaded → fail-closed (no editor).
     *  Backend re-checks CHECKOUT on recordManualPayment. */
    const [checkoutGrant, setCheckoutGrant] = useState<PermissionGrant | null>(null);
    const canCheckout = !!checkoutGrant?.canEdit;
    // Issue #60: when the business allows it AND the caller may (admin → short-circuit,
    // or a role holding BOOK_OUTSIDE_HOURS), the working-hours / business-hours guards
    // are relaxed for admin-side booking. Service match still applies. The backend
    // (enforceWorkingHours) is the authority and re-checks the same permission.
    const [bookOutsideHoursGrant, setBookOutsideHoursGrant] = useState<PermissionGrant | null>(null);
    const canBookOutsideHours =
        !!bizSettings?.allowBookingOutsideBusinessHours &&
        !!(bookOutsideHoursGrant?.canEdit || bookOutsideHoursGrant?.canView);

    /** Company default tax rate (%) + the selected member's commission config —
     *  feed the read-only "estimated profit" line on the Payment tab
     *  (profit = price − member commission − resolved tax). Estimate only. */
    const [companyTaxRate, setCompanyTaxRate] = useState<number | null>(null);
    const [memberCommission, setMemberCommission] = useState<CommissionConfig | null>(null);
    useEffect(() => {
        if (!businessIdStr) { setCompanyTaxRate(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const d = await findBusinessDetailsByBusinessId(Number(businessIdStr), token).catch(() => null);
                if (!cancelled) { setCompanyTaxRate(d?.retailsSalesTaxRate ?? null); setBusinessCountry(d?.country ?? null); }
            } catch { /* non-critical */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessIdStr]);

    /** The caller's max discount rate (%) — caps the manual discount they can
     *  apply on the Payment tab. null = not loaded yet (no cap). */
    const [maxDiscountRate, setMaxDiscountRate] = useState<number | null>(null);

    /** Team-member picker (replaces the row of toggle chips). */
    const [teamPickerOpen, setTeamPickerOpen] = useState(false);
    const [teamPickerSearch, setTeamPickerSearch] = useState('');
    const teamPickerRef = useRef<HTMLDivElement | null>(null);

    /** Service filter picker — same searchable dropdown look as the team picker,
     *  but single-select (one service or "all"). */
    const [svcPickerOpen, setSvcPickerOpen] = useState(false);
    const [svcPickerSearch, setSvcPickerSearch] = useState('');
    const svcPickerRef = useRef<HTMLDivElement | null>(null);

    /** Visible-hours override (gear popover). null → derive from business hours. */
    const [slotSettingsOpen, setSlotSettingsOpen] = useState(false);
    const [customSlotMin, setCustomSlotMin] = useState<string | null>(() =>
        localStorage.getItem('gs.calendar.slotMin') || null
    );
    const [customSlotMax, setCustomSlotMax] = useState<string | null>(() =>
        localStorage.getItem('gs.calendar.slotMax') || null
    );
    const slotSettingsRef = useRef<HTMLDivElement | null>(null);

    const [showSidebar, setShowSidebar] = useState(false);
    const [draft, setDraft] = useState<Draft | null>(null);
    // Selected member's commission config (default + per-service overrides) —
    // feeds the estimated-profit line. Re-fetched when the member changes.
    useEffect(() => {
        const memberId = draft?.teamMemberId ? String(draft.teamMemberId) : "";
        if (!businessIdStr || !memberId) { setMemberCommission(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const cfg = await getTeamMemberCommission(businessIdStr, memberId, token).catch(() => null);
                if (!cancelled) setMemberCommission(cfg);
            } catch { /* non-critical */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessIdStr, draft?.teamMemberId]);
    /** Always-current draft, readable from interval/timeout callbacks without stale closures. */
    const draftRef = useRef<Draft | null>(null);
    draftRef.current = draft;
    const [saving, setSaving] = useState(false);
    /** Active tab inside the appointment Drawer (Details vs Payment). */
    const [activeApptTab, setActiveApptTab] = useState<'details' | 'payment' | 'history'>('details');
    /** Audit-log timeline for the open appointment (Details → History tab). */
    const [drawerHistory, setDrawerHistory] = useState<AppointmentHistoryEvent[]>([]);
    // Audit log stores raw English event types + "A → B" enum payloads; present
    // them in the UI language (same rule as the Appointments screen, issue #54).
    const histEventLabel = (eventType: string): string =>
        t(`appointments.drawer.history.event.${eventType}`, { defaultValue: eventType });
    const histPayloadLabel = (eventType: string, payload: string): string => {
        const sep = payload.includes('→') ? '→' : payload.includes('->') ? '->' : null;
        if (!sep || (eventType !== 'STATUS_CHANGED' && eventType !== 'PAYMENT_STATUS_CHANGED')) return payload;
        const map = eventType === 'PAYMENT_STATUS_CHANGED' ? 'paymentStatus' : 'status';
        return payload
            .split(sep)
            .map((v) => t(`appointments.${map}.${v.trim()}`, { defaultValue: v.trim() }))
            .join(' → ');
    };
    /**
     * Transactions already tied to the currently open appointment. Loaded
     * lazily when the Drawer opens an existing appointment so the Payment
     * tab can show "already paid" instead of an empty form, and the tab
     * label can carry a Paid / Partial badge.
     */
    const [apptTransactions, setApptTransactions] = useState<Transaction[]>([]);
    const [apptTransactionsLoading, setApptTransactionsLoading] = useState(false);
    /** Pre-authorization hold settlement (attendant) — busy + resolved-this-session. */
    const [holdBusy, setHoldBusy] = useState(false);
    const [holdResolved, setHoldResolved] = useState(false);
    /**
     * Inline manual discount the user can apply on the Payment tab.
     * Added on top of the client's pre-registered discount; expressed
     * either as a currency amount or as a percentage of the service price.
     */
    /** Extra discount already persisted on the appointment being edited (0 for a new one). */
    const [storedExtraDiscount, setStoredExtraDiscount] = useState<number>(0);
    const [extraDiscount, setExtraDiscount] = useState<{
        kind: 'AMOUNT' | 'PERCENT';
        value: string;
    }>({ kind: 'AMOUNT', value: '' });
    /**
     * Editable price for VARIABLE-priced services (raw money string). For
     * FIXED services this is ignored — the service's own price is used.
     * Seeded from the service's default price and overridable per booking.
     */
    const [priceInput, setPriceInput] = useState<string>('');
    /** Editable copay (coparticipação) for a CONVENIO appointment (raw money string). */
    const [copayInput, setCopayInput] = useState<string>('');
    /** Health plans (convênios) the business accepts — id→name for the form. */
    const [businessHealthPlans, setBusinessHealthPlans] = useState<HealthPlan[]>([]);
    /** Busy flag for recording the convênio receivable / copay. */
    const [convenioBusy, setConvenioBusy] = useState(false);
    /** Method the client uses to pay the copay (coparticipação). */
    const [copayMethod, setCopayMethod] = useState<PaymentMethod>('CASH');
    /** Tracks payment recording (manual + Stripe Checkout creation). */
    const [recordingManual, setRecordingManual] = useState(false);
    /**
     * Split-payment editor state — one input per accepted manual method.
     * Stored as strings so the user can type freely (empty = not used).
     */
    const [manualSplit, setManualSplit] = useState<Partial<Record<PaymentMethod, string>>>({});
    /** Gift-card code typed at charge time (required when the gift-card row has a value). */
    const [giftCardCode, setGiftCardCode] = useState('');

    /**
     * Recurring-booking config for the create form. Only applies when creating a
     * new appointment whose service allows recurrence. `end` selects which end
     * condition (count / months / a specific date) is sent to the backend.
     */
    const [recurEnabled, setRecurEnabled] = useState(false);
    const [recurFreq, setRecurFreq] = useState<RecurrenceFreq>('WEEKLY');
    const [recurEnd, setRecurEnd] = useState<'count' | 'months' | 'date'>('months');
    const [recurCount, setRecurCount] = useState(8);
    const [recurMonths, setRecurMonths] = useState(3);
    const [recurUntil, setRecurUntil] = useState('');
    /** For WEEKLY_DAYS: which weekdays the series repeats on (DayOfWeek names). */
    const [recurWeekdays, setRecurWeekdays] = useState<string[]>([]);
    /** Whole-series accept/decline in progress. */
    const [respondingSeries, setRespondingSeries] = useState(false);
    /** All occurrences of the open appointment's series (for the banner summary). */
    const [seriesOccurrences, setSeriesOccurrences] = useState<Appointment[]>([]);
    /** Availability picker: Monday of the shown week + the selected day (YYYY-MM-DD). */
    const [slotWeekStart, setSlotWeekStart] = useState<Date | null>(null);
    const [slotDay, setSlotDay] = useState<string>("");
    /** Calendar blocks (time-off) in the visible range. */
    const [timeBlocks, setTimeBlocks] = useState<TimeBlock[]>([]);
    /** Block-editor drawer. blockDraft.id present => editing an existing block. */
    const [showBlock, setShowBlock] = useState(false);
    const [savingBlock, setSavingBlock] = useState(false);
    const [blockDraft, setBlockDraft] = useState<{
        id?: string;
        seriesId?: string | null; // set when the block is one occurrence of a series
        teamMemberId: string; // "" = todos (business-wide)
        startTime: string;
        endTime: string;
        title: string;
    } | null>(null);
    /** Recurrence for a NEW block — same rule shape/UI as recurring appointments. */
    const [blockRecurEnabled, setBlockRecurEnabled] = useState(false);
    const [blockRecurFreq, setBlockRecurFreq] = useState<RecurrenceFreq>('WEEKLY');
    const [blockRecurEnd, setBlockRecurEnd] = useState<'count' | 'months' | 'date'>('count');
    const [blockRecurCount, setBlockRecurCount] = useState(4);
    const [blockRecurMonths, setBlockRecurMonths] = useState(1);
    const [blockRecurUntil, setBlockRecurUntil] = useState('');
    const [blockRecurWeekdays, setBlockRecurWeekdays] = useState<string[]>([]);

    // Load services + team members + business hours for form pickers, filter chips, and validation.
    useEffect(() => {
        let cancelled = false;
        // Re-gate on business switch so B's appointments never paint with A's
        // (still-loaded) team colors.
        setCoreLoaded(false);
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                // Business-scoped queries — only this business's services/team
                // members come over the wire (no client-side cross-business filter).
                const [svc, tm, bh, cl, grant, maxDisc, checkout, settings, healthPlans, bookOutside] = await Promise.all([
                    businessIdNum
                        ? getServicesByBusinessId(businessIdNum, token).catch(() => [])
                        : Promise.resolve([] as Service[]),
                    businessIdStr
                        ? getTeamMembersByBusinessId(businessIdStr, token).catch(() => [])
                        : Promise.resolve([] as TeamMember[]),
                    businessIdNum
                        ? findBusinessHoursByBusinessId(businessIdNum, token).catch(() => [])
                        : Promise.resolve([] as BusinessHours[]),
                    businessIdStr
                        ? getAllClientsByBusinessId(businessIdStr, token).catch(() => [])
                        : Promise.resolve([] as Client[]),
                    businessIdStr
                        ? getMyPermissionGrant(businessIdStr, "CALENDAR", token).catch(() => null)
                        : Promise.resolve(null),
                    businessIdStr
                        ? getMyMaxDiscountRate(businessIdStr, token).catch(() => 0)
                        : Promise.resolve(0),
                    businessIdStr
                        ? getMyPermissionGrant(businessIdStr, "CHECKOUT", token).catch(() => null)
                        : Promise.resolve(null),
                    businessIdNum
                        ? findBusinessSettingByBusinessId(businessIdNum, token).catch(() => null)
                        : Promise.resolve(null),
                    businessIdNum
                        ? getBusinessHealthPlans(businessIdNum, token).catch(() => [] as HealthPlan[])
                        : Promise.resolve([] as HealthPlan[]),
                    businessIdStr
                        ? getMyPermissionGrant(businessIdStr, "BOOK_OUTSIDE_HOURS", token).catch(() => null)
                        : Promise.resolve(null),
                ]);
                if (cancelled) return;
                setServices(svc);
                setTeamMembers(tm);
                setBusinessHours(bh);
                setClients(cl);
                setCalGrant(grant);
                setMaxDiscountRate(maxDisc);
                setCheckoutGrant(checkout);
                setBizSettings(settings);
                setBookOutsideHoursGrant(bookOutside);
                setBusinessHealthPlans(healthPlans.filter((p) => p.active !== false));
                // Team + services are in — safe to paint appointment events with
                // their final colors.
                setCoreLoaded(true);
            } catch (e) {
                if (!cancelled) {
                    // Still let events render (in fallback colors) so a token error
                    // doesn't leave the grid permanently empty.
                    setCoreLoaded(true);
                    setAlert(
                        "danger",
                        e instanceof Error ? e.message : t('calendar.alerts.failLoad')
                    );
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [getAccessTokenSilently, setAlert, businessIdNum, businessIdStr]);

    /**
     * Re-fetch just the booking catalog (services + team members) that the
     * appointment form and the "does this member perform this service" check
     * depend on. The main load runs once per business, so a member's service
     * list edited elsewhere (Team screen) would otherwise be stale in memory
     * and wrongly reject a valid booking. Called when the booking drawer opens.
     */
    const refreshCatalog = useCallback(async () => {
        if (!businessIdNum || !businessIdStr) return;
        try {
            const token = await getAccessTokenSilently();
            const [svc, tm] = await Promise.all([
                getServicesByBusinessId(businessIdNum, token).catch(() => null),
                getTeamMembersByBusinessId(businessIdStr, token).catch(() => null),
            ]);
            if (svc) setServices(svc);
            if (tm) setTeamMembers(tm);
        } catch {
            // Soft fail — keep the already-loaded catalog.
        }
    }, [getAccessTokenSilently, businessIdNum, businessIdStr]);

    /** Own-scope (LIMITED) when the caller can view but not across the whole
     *  business. Then the team picker + booking/block forms are limited to the
     *  caller's own team member (matched by the grant's memberId). */
    const restrictOwn = !!calGrant && calGrant.canView && !calGrant.allScope;
    const myMemberId = calGrant?.memberId != null ? String(calGrant.memberId) : null;

    /** Whether the caller may WRITE on the calendar (create/edit/move/delete
     *  appointments, create blocks). View-only members (CALENDAR = VIEW) get a
     *  read-only calendar. Fail-closed: a missing/unloaded grant = no editing.
     *  Backend (calendar-service) re-checks CALENDAR edit on every write. */
    const canEditCalendar = !!calGrant?.canEdit;
    const scopedTeamMembers = useMemo(
        () =>
            restrictOwn && myMemberId
                ? teamMembers.filter((tm) => String(tm.id) === myMemberId)
                : teamMembers,
        [teamMembers, restrictOwn, myMemberId]
    );

    /** Services visible in the toolbar filter. An own-scope member sees only the
     *  services THEY perform (mirrors scopedTeamMembers) — the full catalog is
     *  loaded (membership) for booking, but the filter shouldn't list services the
     *  member has nothing to do with. A member with no assigned services falls back
     *  to the full list (legacy "no serviceIds = does everything"). Owners/managers
     *  (all-scope) always see every service. */
    const scopedServices = useMemo(() => {
        if (!restrictOwn) return services;
        const mine = new Set(scopedTeamMembers.flatMap((tm) => (tm.serviceIds ?? []).map(String)));
        if (mine.size === 0) return services;
        return services.filter((s) => mine.has(String(s.id)));
    }, [services, restrictOwn, scopedTeamMembers]);

    /** Members eligible to be booked in the New/Edit Appointment form: only those
     *  who actually offer at least one service AND are still active. A member with
     *  no services can't perform anything (issue #42); a deactivated one must not
     *  take NEW bookings. The member already assigned to the appointment being
     *  edited is kept selectable below so old (incl. past, inactive-member)
     *  appointments still render correctly. Used solely by the appointment form
     *  combo — the calendar grid still shows every member. */
    const bookableTeamMembers = useMemo(
        () => scopedTeamMembers.filter((tm) => (tm.serviceIds?.length ?? 0) > 0 && tm.active !== false),
        [scopedTeamMembers]
    );

    /** Appointment-form options: bookable members + the appointment's current
     *  member if it's been deactivated (so editing it doesn't drop the value). */
    const appointmentMemberOptions = useMemo(() => {
        const id = draft?.teamMemberId ? String(draft.teamMemberId) : '';
        if (!id || bookableTeamMembers.some((tm) => String(tm.id) === id)) {
            return bookableTeamMembers;
        }
        const current = scopedTeamMembers.find((tm) => String(tm.id) === id);
        return current ? [current, ...bookableTeamMembers] : bookableTeamMembers;
    }, [bookableTeamMembers, scopedTeamMembers, draft?.teamMemberId]);

    /** Convert backend BusinessHours[] to FullCalendar's businessHours config (darkens outside slots). */
    /** The single team member the calendar is scoped to (own-scope, or exactly
     *  one selected in the filter), else null. When set, the grid reflects this
     *  member's working hours instead of the whole-business open window. */
    const singleVisibleMember = useMemo(() => {
        const visible = scopedTeamMembers.filter((tm) => !hiddenTeamMembers.has(String(tm.id)));
        return visible.length === 1 ? visible[0] : null;
    }, [scopedTeamMembers, hiddenTeamMembers]);

    const businessHoursForCalendar = useMemo(() => {
        // When scoped to a single team member, shade THAT member's working hours
        // instead of the whole business hours.
        if (singleVisibleMember && (singleVisibleMember.workingHours?.length ?? 0) > 0) {
            return singleVisibleMember.workingHours
                .filter((w) => w.dayOfWeek && w.startTime && w.endTime)
                .map((w) => ({
                    daysOfWeek: [DOW_TO_FC[w.dayOfWeek] ?? 1],
                    startTime: hhmm(w.startTime),
                    endTime: hhmm(w.endTime),
                }));
        }
        if (businessHours.length === 0) return undefined;
        // One FullCalendar entry per OPEN window. The day windows (around the break)
        // shade on the day itself; the overnight block shades on the NEXT day's
        // early morning (00:00 → overnightEnd), since that is when it actually occurs.
        const entries: Array<{ daysOfWeek: number[]; startTime: string; endTime: string }> = [];
        for (const b of businessHours) {
            if (!b.dayOfWeek || !b.openTime || !b.closeTime) continue;
            const dow = DOW_TO_FC[b.dayOfWeek] ?? 1;
            for (const w of dayWindowsForBusinessDay(b)) {
                entries.push({ daysOfWeek: [dow], startTime: minutesToHHmm(w.start), endTime: minutesToHHmm(w.end) });
            }
            const ov = overnightMinutes(b);
            if (ov > 0) {
                entries.push({ daysOfWeek: [(dow + 1) % 7], startTime: '00:00', endTime: minutesToHHmm(ov) });
            }
        }
        return entries;
    }, [businessHours, singleVisibleMember]);

    /**
     * Shrink the visible time-grid window to ±1h around the configured business
     * hours so the user doesn't have to scroll past dead 12am–8am rows.
     * Falls back to a sensible 8am–8pm if no business hours are configured yet.
     */
    /** Default visible-hours range derived from business hours (open hour − 60min
     *  to close hour + 60min), but always widened to include the current time
     *  so the "now" indicator + scroll-to-now work even early in the morning or
     *  late at night (e.g. it's 7:36am but business opens at 9:00 — without this
     *  the now-line would fall outside the grid and vanish). The user can still
     *  narrow it explicitly via the gear popover. */
    const defaultSlotBounds = useMemo(() => {
        const now = new Date();
        const nowMin = now.getHours() * 60 + now.getMinutes();

        let minM = 8 * 60;
        let maxM = 20 * 60;
        // When scoped to a single team member, frame the grid around THEIR
        // working hours; otherwise use the business hours.
        const memberWh = singleVisibleMember?.workingHours ?? [];
        if (singleVisibleMember && memberWh.length > 0) {
            const starts = memberWh.filter((w) => w.startTime).map((w) => timeToMinutes(w.startTime));
            const ends = memberWh.filter((w) => w.endTime).map((w) => timeToMinutes(w.endTime));
            if (starts.length > 0 && ends.length > 0) {
                minM = Math.max(0, Math.min(...starts) - 60);
                maxM = Math.min(24 * 60, Math.max(...ends) + 60);
            }
        } else if (businessHours.length > 0) {
            const opens = businessHours.map((b) => timeToMinutes(b.openTime));
            // Midnight close (00:00) counts as end-of-day so the grid reaches 24:00.
            const closes = businessHours.map((b) => {
                const c = timeToMinutes(b.closeTime);
                return c === 0 ? 24 * 60 : c;
            });
            minM = Math.max(0, Math.min(...opens) - 60);
            maxM = Math.min(24 * 60, Math.max(...closes) + 60);
            // Any overnight (early-morning) block means the grid must reach 00:00.
            if (businessHours.some((b) => !!b.overnightEnd)) minM = 0;
        }
        // Guarantee "now" sits inside the window (with a little margin).
        minM = Math.min(minM, Math.max(0, nowMin - 60));
        maxM = Math.max(maxM, Math.min(24 * 60, nowMin + 120));

        // Snap to whole hours so the time axis labels land on the hour (and
        // half-hour), instead of being offset by the current minute.
        minM = Math.floor(minM / 60) * 60;
        maxM = Math.min(24 * 60, Math.ceil(maxM / 60) * 60);

        return {
            min: `${pad(Math.floor(minM / 60))}:${pad(minM % 60)}:00`,
            max: `${pad(Math.floor(maxM / 60))}:${pad(maxM % 60)}:00`,
        };
        // businessHours / the scoped member drive this; `now` is read at compute
        // time (re-runs on mount / when hours load, which is when it matters).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessHours, singleVisibleMember]);

    /** Active slot bounds — user override wins over the business-hours default. */
    const slotBounds = useMemo(() => ({
        min: customSlotMin || defaultSlotBounds.min,
        max: customSlotMax || defaultSlotBounds.max,
    }), [customSlotMin, customSlotMax, defaultSlotBounds]);

    /** Plain-language summary of the configured business hours — shown at the
     *  top of the visible-hours popover so the user has context for what
     *  "default" actually means today. */
    const businessHoursByDay = useMemo(() => {
        const order = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
        return order.map((day) => {
            const e = businessHours.find(
                (b) => (b.dayOfWeek || '').toUpperCase() === day && b.openTime && b.closeTime
            );
            return {
                day,
                open: e ? e.openTime.slice(0, 5) : null,
                close: e ? e.closeTime.slice(0, 5) : null,
            };
        });
    }, [businessHours]);
    const hasAnyBusinessHours = businessHours.length > 0;

    /** Persist + apply custom visible-hours overrides. Empty string clears. */
    const applySlotMin = (v: string) => {
        if (!v) {
            setCustomSlotMin(null);
            localStorage.removeItem('gs.calendar.slotMin');
        } else {
            const norm = v.length === 5 ? `${v}:00` : v;
            setCustomSlotMin(norm);
            localStorage.setItem('gs.calendar.slotMin', norm);
        }
    };
    const applySlotMax = (v: string) => {
        if (!v) {
            setCustomSlotMax(null);
            localStorage.removeItem('gs.calendar.slotMax');
        } else {
            const norm = v.length === 5 ? `${v}:00` : v;
            setCustomSlotMax(norm);
            localStorage.setItem('gs.calendar.slotMax', norm);
        }
    };

    /** Updates the now-indicator's pill text with the current time every 30s.
     *  FullCalendar's built-in arrow is replaced via CSS — we just set
     *  `data-time` and the pseudo-element reads it. */
    useEffect(() => {
        const updateNowLabel = () => {
            const arrow = document.querySelector('.fc-timegrid-now-indicator-arrow');
            if (!arrow) return;
            const n = new Date();
            const h = n.getHours();
            const m = String(n.getMinutes()).padStart(2, '0');
            const label = timeFormat === 'AMPM'
                ? `${h % 12 || 12}:${m} ${h >= 12 ? 'pm' : 'am'}`
                : `${String(h).padStart(2, '0')}:${m}`;
            arrow.setAttribute('data-time', label);

            // FullCalendar v6 only draws the now-indicator line INSIDE today's
            // column. We mirror its Y onto two places so we can draw our own:
            //   1) the .fc-day-today frame → the strong 3px segment over today
            //   2) the full-width .fc-timegrid-cols → the thin connector line
            //      that runs across every day at the current time.
            const line = document.querySelector('.fc-timegrid-now-indicator-line') as HTMLElement | null;
            const todayFrame = document.querySelector('.fc-day-today .fc-timegrid-col-frame') as HTMLElement | null;
            const colsTable = document.querySelector('.fc-timegrid-cols') as HTMLElement | null;
            if (line) {
                const top = line.style.top || `${line.offsetTop}px`;
                if (todayFrame) todayFrame.style.setProperty('--gs-now-indicator-top', top);
                if (colsTable) {
                    colsTable.style.setProperty('--gs-now-indicator-top', top);
                    // The cols table's first cell is the time-axis spacer; start the
                    // connector line at the first day column so it never overlaps
                    // the hour labels on the left.
                    const axis = document.querySelector('.fc-timegrid-axis') as HTMLElement | null;
                    const axisW = axis ? axis.getBoundingClientRect().width : 0;
                    colsTable.style.setProperty('--gs-now-indicator-left', `${axisW}px`);
                }
            }
        };
        updateNowLabel();
        const id = setInterval(updateNowLabel, 30 * 1000);
        return () => clearInterval(id);
    });

    /** Click-outside handlers so the team picker / slot-settings popovers
     *  feel like real menus instead of permanent panels. */
    useEffect(() => {
        if (!teamPickerOpen && !slotSettingsOpen && !svcPickerOpen) return;
        const onDocClick = (e: MouseEvent) => {
            const target = e.target as Node;
            if (teamPickerOpen && teamPickerRef.current && !teamPickerRef.current.contains(target)) {
                setTeamPickerOpen(false);
            }
            if (svcPickerOpen && svcPickerRef.current && !svcPickerRef.current.contains(target)) {
                setSvcPickerOpen(false);
            }
            if (slotSettingsOpen && slotSettingsRef.current && !slotSettingsRef.current.contains(target)) {
                setSlotSettingsOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [teamPickerOpen, slotSettingsOpen, svcPickerOpen]);

    /** Label shown on the closed team-picker button.
     *  - all visible (or none hidden) → "All team members"
     *  - exactly one visible → that person's name
     *  - several → "N team members" */
    const teamPickerLabel = useMemo(() => {
        if (scopedTeamMembers.length === 0) return t('calendar.toolbar.noTeam');
        const visible = scopedTeamMembers.filter((tm) => !hiddenTeamMembers.has(String(tm.id)));
        if (visible.length === scopedTeamMembers.length) return t('calendar.toolbar.allTeamMembers');
        if (visible.length === 0) return t('calendar.toolbar.noneVisible');
        if (visible.length === 1) return `${visible[0].firstName ?? ''} ${visible[0].lastName ?? ''}`.trim();
        return t('calendar.toolbar.teamMembersCount', { count: visible.length });
    }, [scopedTeamMembers, hiddenTeamMembers, t]);

    /** Services A→Z for the filter dropdown list. */
    const sortedServicesForFilter = useMemo(
        () => [...scopedServices].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
        [scopedServices]
    );

    /** Label on the closed service-filter button — mirrors the team picker:
     *  all visible → "all"; none → "none"; one → its name; several → "N services". */
    const servicePickerLabel = useMemo(() => {
        if (scopedServices.length === 0) return t('calendar.toolbar.allServices');
        const visible = scopedServices.filter((s) => !hiddenServices.has(String(s.id)));
        if (visible.length === scopedServices.length) return t('calendar.toolbar.allServices');
        if (visible.length === 0) return t('calendar.toolbar.noServices');
        if (visible.length === 1) return visible[0].name || t('calendar.toolbar.allServices');
        return t('calendar.toolbar.servicesCount', { count: visible.length });
    }, [scopedServices, hiddenServices, t]);

    /** Toggle helpers for the team picker. */
    const setAllTeamVisible = (visible: boolean) => {
        if (visible) setHiddenTeamMembers(new Set());
        else setHiddenTeamMembers(new Set(scopedTeamMembers.map((t) => String(t.id))));
    };

    /** Toggle helpers for the service picker (mirror the team picker). */
    const setAllServicesVisible = (visible: boolean) => {
        if (visible) setHiddenServices(new Set());
        else setHiddenServices(new Set(scopedServices.map((s) => String(s.id))));
    };
    const toggleServiceVisibility = (id: string) => {
        setHiddenServices((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const reloadAppointments = useCallback(async () => {
        if (!range || !businessIdStr) {
            setAppointments([]);
            return;
        }
        try {
            const token = await getAccessTokenSilently();
            // Load the visible range PLUS the next 14 days so the
            // "Find next available slot" suggestions never miss a
            // conflict that lives outside the current view. The backend
            // expects a local-time string ("yyyy-MM-dd'T'HH:mm:ss") — no
            // trailing Z — same format used by handleDatesSet.
            const rangeEnd = new Date(range.end);
            const SLOT_LOOKAHEAD_DAYS = 14;
            const lookahead = new Date();
            lookahead.setHours(0, 0, 0, 0);
            lookahead.setDate(lookahead.getDate() + SLOT_LOOKAHEAD_DAYS + 1);
            const effectiveEndDate = lookahead > rangeEnd ? lookahead : rangeEnd;
            const effectiveEnd = toLocalIso(effectiveEndDate) + ":00";
            const [data, blocks, evs] = await Promise.all([
                getAppointmentsByBusinessAndRange(businessIdStr, range.start, effectiveEnd, token),
                getTimeBlocksByBusinessAndRange(businessIdStr, range.start, effectiveEnd, token).catch(() => []),
                getEventsByBusinessAndRange(businessIdStr, range.start, effectiveEnd, token).catch(() => []),
            ]);
            setAppointments(data);
            setTimeBlocks(blocks);
            setGroupEvents(evs);
        } catch (e) {
            setAlert("danger", e instanceof Error ? e.message : "Failed to load appointments");
        }
    }, [range, businessIdStr, getAccessTokenSilently, setAlert]);

    useEffect(() => {
        reloadAppointments();
    }, [reloadAppointments]);

    /**
     * Open-appointment observer: while a saved appointment's drawer is open and
     * the tab is visible, poll it every 15s. Read-only indicators (payment +
     * reschedule status / proposed time) are merged live so the team sees the
     * customer's response without reopening. A server-side change to the core
     * fields (e.g. an accepted reschedule moved the time) surfaces a "reload"
     * banner instead of clobbering any in-progress edits. Cheap: one tiny query
     * per cycle, paused when hidden or while saving/proposing.
     */
    useEffect(() => {
        if (!showSidebar || !draft?.id) return;
        const apptId = draft.id;
        const tick = async () => {
            if (typeof document !== "undefined" && document.hidden) return;
            if (saving || proposing || recordingManual) return;
            try {
                const token = await getAccessTokenSilently();
                const fresh = await getAppointmentById(apptId, token);
                if (!fresh) return;
                // Live-merge the non-editable indicators (never clobbers edits).
                setDraft((prev) => {
                    if (!prev || prev.id !== apptId) return prev;
                    const nextResched = fresh.rescheduleStatus ?? "NONE";
                    const nextPropStart = fresh.proposedStartTime ?? null;
                    const nextPropEnd = fresh.proposedEndTime ?? null;
                    if (
                        prev.paymentStatus === fresh.paymentStatus &&
                        (prev.rescheduleStatus ?? "NONE") === nextResched &&
                        (prev.proposedStartTime ?? null) === nextPropStart &&
                        (prev.proposedEndTime ?? null) === nextPropEnd
                    ) {
                        return prev; // nothing to update → no re-render
                    }
                    return {
                        ...prev,
                        paymentStatus: fresh.paymentStatus ?? prev.paymentStatus,
                        rescheduleStatus: nextResched,
                        proposedStartTime: nextPropStart,
                        proposedEndTime: nextPropEnd,
                    };
                });
                // Detect a core change applied outside this form (e.g. accepted
                // reschedule moved the time / changed status) vs the baseline.
                const base = syncedRef.current;
                const coreChanged =
                    base &&
                    (base.startTime !== fresh.startTime ||
                        base.endTime !== fresh.endTime ||
                        base.status !== fresh.status);
                if (coreChanged) {
                    // Refresh the history timeline so the change is visible there too.
                    getAppointmentHistory(apptId, token).then(setDrawerHistory).catch(() => {});
                    if (draftUntouched(draftRef.current, openSnapshotRef.current)) {
                        // Nothing typed locally — safe to just reload the drawer.
                        openEdit(fresh);
                    } else {
                        // Preserve in-progress edits; offer a reload instead.
                        setExternalUpdate(fresh);
                    }
                }
            } catch {
                /* polling is best-effort — ignore transient errors */
            }
        };
        const interval = setInterval(tick, 15000);
        return () => clearInterval(interval);
        // openEdit intentionally omitted: keeping it out avoids resetting the
        // 15s timer on every render; its closure is stable enough here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showSidebar, draft?.id, saving, proposing, recordingManual, getAccessTokenSilently]);

    /**
     * Grid observer: refresh the visible range every 25s so events that change
     * elsewhere (e.g. a customer accepts a reschedule and the slot moves) update
     * on the calendar without a manual reload. Paused while the tab is hidden or
     * while a save/propose is in flight (those refresh on their own). Updates only
     * the `appointments` array — never the open drawer's editable form.
     */
    useEffect(() => {
        const tick = () => {
            if (typeof document !== "undefined" && document.hidden) return;
            if (saving || proposing || recordingManual) return;
            reloadAppointments();
        };
        const interval = setInterval(tick, 25000);
        return () => clearInterval(interval);
    }, [reloadAppointments, saving, proposing, recordingManual]);

    /**
     * Auto-open the appointment Drawer when the user navigates here from
     * the Payments screen via setPendingAppointmentId. Waits until
     * appointments are loaded so we have the full Appointment object to
     * pass to openEdit.
     */
    useEffect(() => {
        if (!pendingAppointmentId || appointments.length === 0) return;
        const appt = appointments.find((a) => String(a.id) === String(pendingAppointmentId));
        if (appt) {
            openEdit(appt);
            setActiveApptTab('payment');
        }
        setPendingAppointmentId(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingAppointmentId, appointments]);

    /**
     * Bell deep-link for EVENT_* notifications (setPendingEventId): open the
     * EventDrawer straight away — it loads the event by id itself, so there's
     * no need to wait for the calendar's visible-range fetch.
     */
    useEffect(() => {
        if (!pendingEventId) return;
        setEventDrawerId(String(pendingEventId));
        setEventDrawerOpen(true);
        setPendingEventId(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingEventId]);

    /**
     * Scroll the wrapping container so the red "now" indicator sits ~1/3 from
     * the top of the visible area. Uses the actual DOM element FullCalendar
     * renders (`.fc-timegrid-now-indicator-line`) so we don't have to guess
     * the matching slot — its `offsetTop` already encodes the exact pixel
     * position of the current minute on the grid.
     *
     * Falls back to the 10:00 slot row if the now-line isn't rendered yet
     * (FC paints it slightly after the grid).
     */
    const scrollToNow = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) return;
        // Let the browser put "now" in the MIDDLE of the scroller — no manual
        // offset math (which kept landing it under the sticky header). Falls back
        // to the current-hour slot row if the now-indicator isn't rendered yet.
        const now = new Date();
        const el =
            container.querySelector<HTMLElement>('.fc-timegrid-now-indicator-line') ||
            container.querySelector<HTMLElement>(`[data-time="${pad(now.getHours())}:00:00"]`);
        if (!el) return;
        // Idempotent: if "now" is already inside the middle band of the viewport,
        // don't scroll again — the retry ladder + data-arrival re-runs would
        // otherwise produce visible jumps ("blinks") on every FC repaint.
        const c = container.getBoundingClientRect();
        const r = el.getBoundingClientRect();
        const bandTop = c.top + c.height * 0.18;
        const bandBottom = c.top + c.height * 0.82;
        if (r.top >= bandTop && r.top <= bandBottom) return;
        el.scrollIntoView({ block: 'center' });
    }, []);

    // Scroll on mount + when business hours / appointments arrive (FC re-renders
    // and resets scroll). Fire several times to survive async layout.
    useEffect(() => {
        const delays = [80, 350, 900, 1600, 2600];
        const timers = delays.map((d) => window.setTimeout(scrollToNow, d));
        return () => timers.forEach((t) => window.clearTimeout(t));
    }, [scrollToNow, slotBounds.min, slotBounds.max, appointments.length]);

    // Recompute FullCalendar's column widths whenever the calendar container's
    // WIDTH changes — e.g. when the app's left nav is collapsed/expanded. FC only
    // auto-reflows on window `resize`; a sidebar toggle resizes the container but
    // not the window, so the grid kept its old width — leaving a blank gutter on
    // the right (collapsed) or clipping Saturday (expanded). Issue #41.
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        let lastW = Math.round(el.getBoundingClientRect().width);
        let raf = 0;
        const ro = new ResizeObserver((entries) => {
            const w = Math.round(entries[0].contentRect.width);
            if (w === lastW) return; // height-only change (content grows) → ignore
            lastW = w;
            // Coalesce the burst fired during the nav's 0.3s width transition into
            // one update per frame.
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => calendarRef.current?.getApi().updateSize());
        });
        ro.observe(el);
        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
        };
    }, []);

    const handleDatesSet = (info: DatesSetArg) => {
        setRange({
            start: toLocalIso(info.start) + ":00",
            end: toLocalIso(info.end) + ":00",
        });
        // View changes (Month → Week, prev/next, today) reset the timegrid
        // scroll. Re-center "now" so the red indicator stays in sight.
        if (
            info.view.type === 'timeGridWeek' ||
            info.view.type === 'timeGridDay'
        ) {
            window.setTimeout(scrollToNow, 50);
        }
    };

    /** Services the currently-selected team member can perform. */
    const allowedServicesForDraft = useMemo<Service[]>(() => {
        if (!draft) return services;
        // #42: no member chosen yet → no services offered (pick a member first),
        // so the Service box doesn't show a value while the member box is empty.
        if (!draft.teamMemberId) return [];
        const tm = teamMembers.find((t) => String(t.id) === String(draft.teamMemberId));
        if (!tm) return services;
        // A member must EXPLICITLY offer a service. No services assigned → none
        // selectable (the old "empty serviceIds = does everything" fallback is gone).
        const ids = tm.serviceIds ?? [];
        return services.filter((s) => ids.includes(s.id));
    }, [draft, teamMembers, services]);

    /** The service currently chosen in the draft (for recurrence/price rules). */
    const selectedService = useMemo<Service | undefined>(
        () => services.find((s) => s.id === draft?.serviceId),
        [services, draft?.serviceId]
    );
    const selectedServiceAllowsRecurring = !!selectedService?.allowRecurring;
    /** Frequencies this service offers (empty list = all). */
    const serviceRecurrenceFreqs = useMemo<RecurrenceFreq[]>(() => {
        const offered = selectedService?.recurrenceFreqs ?? [];
        const all: RecurrenceFreq[] = ['DAILY', 'WEEKLY', 'BIWEEKLY', 'MONTHLY', 'WEEKLY_DAYS'];
        return offered.length > 0 ? all.filter((f) => offered.includes(f)) : all;
    }, [selectedService]);
    /** Horizon cap (months) the service permits for a series. */
    const serviceMaxMonths = selectedService?.maxRecurrenceMonths ?? 12;

    /** Weekdays the business is open (DayOfWeek names) — used to disable closed
     *  days in the WEEKLY_DAYS picker so a series never lands on a closed day. */
    const openWeekdays = useMemo(
        () =>
            new Set(
                businessHours
                    .filter((b) => b.dayOfWeek && b.openTime && b.closeTime)
                    .map((b) => b.dayOfWeek)
            ),
        [businessHours]
    );

    // Keep the chosen frequency within what the service offers.
    useEffect(() => {
        if (serviceRecurrenceFreqs.length > 0 && !serviceRecurrenceFreqs.includes(recurFreq)) {
            setRecurFreq(serviceRecurrenceFreqs[0]);
        }
    }, [serviceRecurrenceFreqs, recurFreq]);

    /** Human summary of the open appointment's recurring series. */
    const seriesSummary = useMemo(() => {
        if (!draft?.seriesId || seriesOccurrences.length === 0) return null;
        const sorted = [...seriesOccurrences].sort((a, b) =>
            (a.startTime ?? "").localeCompare(b.startTime ?? "")
        );
        const first = sorted[0];
        const last = sorted[sorted.length - 1];
        const active = sorted.filter((o) => o.status !== "CANCELLED");
        const cancelled = sorted.length - active.length;
        return {
            total: sorted.length,
            cancelled,
            firstStart: first.startTime,
            lastStart: last.startTime,
            time: (first.startTime ?? "").slice(11, 16), // HH:mm
        };
    }, [draft?.seriesId, seriesOccurrences]);

    const openCreate = (startISO: string) => {
        if (services.length === 0 || teamMembers.length === 0) {
            setAlert(
                "danger",
                t('calendar.alerts.noServiceOrMember')
            );
            return;
        }
        const start = startISO.slice(0, 16);
        const startDate = new Date(start);
        // Pick the default member first, then default the service to one that
        // member actually performs — otherwise the draft is born inconsistent
        // (service = services[0], member can't do it) and Save fails with
        // "<member> doesn't perform this service" even though the dropdown only
        // lists valid options. (empty serviceIds = member does all services.)
        // Issue #42: don't auto-select a member — the combo opens with no default
        // (the user picks one), and members without services aren't offered at all.
        // The one exception is a self-restricted member, who can only ever book for
        // themselves and so stays pre-selected (when they actually have services).
        const ownIsBookable =
            restrictOwn && myMemberId
                ? teamMembers.some(
                      (tm) => String(tm.id) === String(myMemberId) && (tm.serviceIds?.length ?? 0) > 0,
                  )
                : false;
        const defaultMemberId = ownIsBookable && myMemberId ? myMemberId : "";
        const defaultMember = teamMembers.find((t) => String(t.id) === String(defaultMemberId));
        const memberServiceIds = defaultMember?.serviceIds ?? [];
        // No "empty = all" fallback: a member with no services starts with no
        // service selected (the dropdown shows the "no services" hint + blocks save).
        const allowedForMember = services.filter((s) => memberServiceIds.includes(s.id));
        const defaultService = allowedForMember[0];
        // Seed end from the service's duration when known (else +1h).
        const durationMin = defaultService?.totalDuration && defaultService.totalDuration > 0
            ? defaultService.totalDuration
            : 60;
        const endDate = new Date(startDate.getTime() + durationMin * 60 * 1000);
        setDraft({
            clientId: null,
            customerName: "",
            customerPhone: "",
            customerEmail: "",
            serviceId: defaultService?.id ?? "",
            teamMemberId: defaultMemberId,
            startTime: start,
            endTime: toLocalIso(endDate),
            status: "PENDING",
            notes: "",
            paymentType: 'PARTICULAR',
            healthPlanId: null,
            copayAmount: null,
            rebookReminderOn: null,
        });
        setCopayInput('');
        setShowClientDropdown(false);
        setActiveApptTab('details');
        setManualSplit({});
        setGiftCardCode('');
        // Seed the availability picker from the clicked day.
        setSlotDay(start.slice(0, 10));
        setSlotWeekStart(startOfWeekMon(startDate));
        // Fresh recurrence config for each new booking.
        setRecurEnabled(false);
        setRecurFreq('WEEKLY');
        setRecurEnd('months');
        setRecurCount(8);
        setRecurMonths(3);
        setRecurUntil('');
        // Seed weekdays with the clicked day. Skip it only when we know hours are
        // configured AND the business is closed that day.
        {
            const startDayName = DAYS_OF_WEEK[startDate.getDay()];
            const hasHours = businessHours.some((b) => b.openTime && b.closeTime);
            const openOnStart =
                !hasHours ||
                businessHours.some(
                    (b) => b.dayOfWeek === startDayName && b.openTime && b.closeTime
                );
            setRecurWeekdays(openOnStart ? [startDayName] : []);
        }
        // Brand-new appointment has no transactions.
        setApptTransactions([]);
        setExtraDiscount({ kind: 'AMOUNT', value: '' });
        setStoredExtraDiscount(0);
        setPriceInput(
            defaultService?.price != null ? String(defaultService.price) : ''
        );
        setShowSidebar(true);
        // Pull fresh services + team-member service lists so the perform-service
        // validation reflects edits made on the Team screen since this loaded.
        refreshCatalog();
    };

    /**
     * Fetch all transactions tied to an appointment so the Payment tab can
     * show what was already paid (and the tab label can carry a Paid badge).
     * Fails silently — paid state isn't critical for editing details.
     */
    const loadAppointmentTransactions = async (appointmentId: string) => {
        try {
            setApptTransactionsLoading(true);
            const token = await getAccessTokenSilently();
            const list = await getTransactionsByAppointment(appointmentId, token);
            setApptTransactions(list);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('[Calendar] failed to load appointment transactions:', e);
            setApptTransactions([]);
        } finally {
            setApptTransactionsLoading(false);
        }
    };

    const openEdit = (appt: Appointment) => {
        // If linked to a client, render the client's CURRENT name/contact instead
        // of the snapshot stored on the appointment (which goes stale after a
        // client edit). Falls back to the snapshot when the client list hasn't
        // loaded yet; the background sync below also persists the refreshed value.
        // Not linked (e.g. a public booking) but a client of this business already
        // has the same e-mail → treat it as linked, so the drawer shows the client
        // instead of offering to "save as new client" (which would duplicate it).
        // The link is persisted on the next save (clientId travels with the draft).
        const byEmail = !appt.clientId && appt.customerEmail
            ? clients.find((c) => (c.email ?? '').trim().toLowerCase() === appt.customerEmail!.trim().toLowerCase()) ?? null
            : null;
        const linked = appt.clientId
            ? clients.find((c) => String(c.id) === String(appt.clientId))
            : byEmail;
        const d: Draft = {
            id: appt.id,
            clientId: appt.clientId ?? byEmail?.id ?? null,
            customerName: linked ? formatClientLabel(linked) : (appt.customerName ?? ""),
            customerPhone: linked?.phone ?? appt.customerPhone ?? "",
            customerEmail: linked?.email ?? appt.customerEmail ?? "",
            serviceId: appt.serviceId ?? services[0]?.id ?? "",
            teamMemberId: appt.teamMemberId ?? teamMembers[0]?.id ?? "",
            startTime: toLocalInput(appt.startTime),
            endTime: toLocalInput(appt.endTime),
            status: appt.status ?? "PENDING",
            paymentStatus: appt.paymentStatus ?? "NOT_PAID",
            holdPaymentIntentId: appt.holdPaymentIntentId ?? null,
            holdAmount: appt.holdAmount ?? null,
            notes: appt.notes ?? "",
            externalLocation: appt.externalLocation ?? null,
            rebookReminderOn: appt.rebookReminderOn ?? null,
            cancellationReason: appt.cancellationReason ?? "",
            cancelledByClient: appt.cancelledByClient ?? null,
            cancellationFeeOutstanding: appt.cancellationFeeOutstanding ?? null,
            cancellationFeeAmount: appt.cancellationFeeAmount ?? null,
            rescheduleStatus: appt.rescheduleStatus ?? "NONE",
            proposedStartTime: appt.proposedStartTime ?? null,
            proposedEndTime: appt.proposedEndTime ?? null,
            seriesId: appt.seriesId ?? null,
            recurrenceFreq: appt.recurrenceFreq ?? null,
            paymentType: appt.paymentType ?? 'PARTICULAR',
            healthPlanId: appt.healthPlanId != null ? String(appt.healthPlanId) : null,
            copayAmount: appt.copayAmount ?? null,
        };
        setDraft(d);
        // Seed the copay editor from the stored value (CONVENIO only).
        setCopayInput(appt.copayAmount != null && appt.copayAmount > 0 ? String(appt.copayAmount) : '');
        // Snapshot the loaded draft so the observer can tell "untouched" from
        // "user edited" before auto-reloading on an outside change.
        openSnapshotRef.current = { ...d };
        // Seed the reschedule editor from the current slot (so the team only
        // tweaks the time), and collapse the panel by default.
        setRescheduleStart(toLocalInput(appt.startTime));
        setRescheduleEnd(toLocalInput(appt.endTime));
        setShowReschedule(false);
        // Baseline for the open-appointment observer (detect outside changes).
        syncedRef.current = { startTime: appt.startTime, endTime: appt.endTime, status: appt.status };
        setExternalUpdate(null);
        // Load the audit-log timeline for the History tab.
        if (appt.id) {
            getAccessTokenSilently()
                .then((token) => getAppointmentHistory(appt.id as string, token))
                .then(setDrawerHistory)
                .catch(() => setDrawerHistory([]));
        } else {
            setDrawerHistory([]);
        }
        // Seed the availability picker from the appointment's day.
        {
            const sYmd = toLocalInput(appt.startTime).slice(0, 10) || ymdOf(new Date());
            setSlotDay(sYmd);
            setSlotWeekStart(startOfWeekMon(new Date(`${sYmd}T12:00:00`)));
        }
        // Load the full series (for the recurring-banner summary).
        if (appt.seriesId) {
            getAccessTokenSilently()
                .then((token) => getAppointmentsBySeries(appt.seriesId as string, token))
                .then(setSeriesOccurrences)
                .catch(() => setSeriesOccurrences([]));
        } else {
            setSeriesOccurrences([]);
        }
        setShowClientDropdown(false);
        setActiveApptTab('details');
        setManualSplit({});
        setGiftCardCode('');
        // Reset old data first so the tab badge doesn't flash a stale state
        // before the new fetch completes.
        setApptTransactions([]);
        // Hydrate the discount editor from the appointment's stored value.
        const stored = appt.extraDiscount ?? 0;
        setStoredExtraDiscount(stored > 0 ? stored : 0);
        setExtraDiscount({
            kind: 'AMOUNT',
            value: stored > 0 ? String(stored) : '',
        });
        // Seed the variable-price editor: the stored per-appointment price
        // when present, otherwise the current service's default price.
        const editSvc = services.find((s) => s.id === appt.serviceId);
        const seededPrice =
            appt.price != null ? appt.price : editSvc?.price ?? null;
        setPriceInput(seededPrice != null ? String(seededPrice) : '');
        if (appt.id) {
            loadAppointmentTransactions(appt.id);
        }
        setShowSidebar(true);
        // Keep the service/member validation in sync with Team-screen edits.
        refreshCatalog();
        // Queue a linked-client snapshot refresh (runs once `clients` is loaded).
        pendingClientSyncRef.current = appt;
    };

    /**
     * Refresh the appointment's denormalized customer snapshot from the linked
     * client. The name/phone/email are copied onto the appointment at booking;
     * if the client is edited afterwards (e.g. renamed), the agenda keeps showing
     * the old value. When the editor opens we re-read the current client and, if
     * it drifted, (1) update the open form and (2) persist a full re-save in the
     * background so the calendar and the client record agree — no manual Save.
     *
     * The re-save resends the appointment's existing values for every other field
     * (price, discount, notes, times, status…), so the mapper's null-overwrite
     * never wipes anything; only the three contact fields change. Status is
     * unchanged, so no client notification is triggered.
     */
    const syncLinkedClientSnapshot = async (appt: Appointment) => {
        if (!appt.id || !appt.clientId || !businessIdStr) return;
        const client = clients.find((c) => String(c.id) === String(appt.clientId));
        if (!client) return;
        const freshName = formatClientLabel(client);
        const freshEmail = client.email ?? appt.customerEmail ?? null;
        const freshPhone = client.phone ?? appt.customerPhone ?? null;
        const drifted =
            freshName !== (appt.customerName ?? "") ||
            (freshEmail ?? "") !== (appt.customerEmail ?? "") ||
            (freshPhone ?? "") !== (appt.customerPhone ?? "");
        if (!drifted) return;

        // 1) reflect immediately in the open editor (and its baseline snapshot).
        setDraft((prev) =>
            prev && String(prev.id) === String(appt.id)
                ? { ...prev, customerName: freshName, customerEmail: freshEmail ?? "", customerPhone: freshPhone ?? "" }
                : prev
        );
        if (openSnapshotRef.current && String(openSnapshotRef.current.id) === String(appt.id)) {
            openSnapshotRef.current = {
                ...openSnapshotRef.current,
                customerName: freshName,
                customerEmail: freshEmail ?? "",
                customerPhone: freshPhone ?? "",
            };
        }

        // 2) persist a complete re-save (only the contact fields differ).
        try {
            const token = await getAccessTokenSilently();
            await saveAppointment(
                {
                    id: appt.id,
                    businessId: businessIdStr,
                    teamMemberId: appt.teamMemberId,
                    serviceId: appt.serviceId,
                    clientId: appt.clientId,
                    customerName: freshName,
                    customerPhone: freshPhone,
                    customerEmail: freshEmail,
                    startTime: withSeconds(toLocalInput(appt.startTime)),
                    endTime: withSeconds(toLocalInput(appt.endTime)),
                    status: appt.status,
                    extraDiscount: appt.extraDiscount ?? 0,
                    price: appt.price ?? null,
                    // Preserve convênio data — this background re-save sends a
                    // complete input, so omitting these would wipe the columns.
                    paymentType: appt.paymentType ?? 'PARTICULAR',
                    healthPlanId: appt.healthPlanId != null ? String(appt.healthPlanId) : null,
                    copayAmount: appt.copayAmount ?? null,
                    notes: appt.notes ?? null,
                    externalLocation: appt.externalLocation ?? null,
                    cancellationReason:
                        appt.status === "CANCELLED" || appt.status === "DECLINED"
                            ? appt.cancellationReason ?? null
                            : null,
                },
                token
            );
            // Refresh the agenda so the row shows the up-to-date name.
            reloadAppointments();
        } catch {
            /* best-effort: the snapshot is retried next time the editor opens */
        }
    };

    // Run the queued snapshot sync once per open, as soon as the client list is ready.
    useEffect(() => {
        const appt = pendingClientSyncRef.current;
        if (!appt || !draft?.id || String(appt.id) !== String(draft.id)) return;
        if (!appt.clientId) { pendingClientSyncRef.current = null; return; }
        const client = clients.find((c) => String(c.id) === String(appt.clientId));
        if (!client) return; // linked client not loaded yet → wait for `clients`
        pendingClientSyncRef.current = null;
        void syncLinkedClientSnapshot(appt);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft?.id, clients]);

    const closeSidebar = () => {
        setShowSidebar(false);
        setDraft(null);
        setShowClientDropdown(false);
    };

    /**
     * Past appointments (with notes) for the currently-linked client.
     * Used to surface "previous note" context above the Notes textarea
     * so the team member sees what was discussed last time.
     */
    const [pastClientNotes, setPastClientNotes] = useState<Appointment[]>([]);

    useEffect(() => {
        const cid = draft?.clientId;
        if (!cid) {
            setPastClientNotes([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const data = await getAppointmentsByClient(String(cid), token);
                if (cancelled) return;
                // Past + non-empty notes, newest first. Exclude the appointment
                // being edited (you don't want to read your own draft back).
                const now = Date.now();
                const filtered = data
                    .filter(
                        (a) =>
                            new Date(a.endTime).getTime() < now &&
                            a.notes &&
                            a.notes.trim().length > 0 &&
                            (!draft?.id || String(a.id) !== String(draft.id))
                    )
                    .sort(
                        (a, b) =>
                            new Date(b.startTime).getTime() -
                            new Date(a.startTime).getTime()
                    );
                setPastClientNotes(filtered);
            } catch {
                if (!cancelled) setPastClientNotes([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [draft?.clientId, draft?.id, getAccessTokenSilently]);

    // If the drawer opened before the client list loaded (or the list changed),
    // link an unlinked draft to the business's client with the same e-mail.
    useEffect(() => {
        if (!draft || draft.id == null || draft.clientId) return;
        const email = (draft.customerEmail ?? '').trim().toLowerCase();
        if (!email) return;
        const match = clients.find((c) => (c.email ?? '').trim().toLowerCase() === email);
        if (match) setDraft((prev) => (prev && !prev.clientId ? { ...prev, clientId: match.id } : prev));
    }, [clients, draft?.id, draft?.clientId, draft?.customerEmail]);

    /** The full Client row linked to the current draft, if any. */
    const linkedClient = useMemo<Client | null>(() => {
        if (!draft?.clientId) return null;
        return clients.find((c) => c.id === draft.clientId) ?? null;
    }, [draft?.clientId, clients]);

    /** The linked client's carteirinha for the appointment's convênio plan (or null). */
    const convenioCard = useMemo(() => {
        if (draft?.paymentType !== 'CONVENIO' || !draft?.healthPlanId) return null;
        return (linkedClient?.convenios ?? []).find(
            (cv) => String(cv.healthPlanId) === String(draft.healthPlanId)
        ) ?? null;
    }, [draft?.paymentType, draft?.healthPlanId, linkedClient]);

    /** Favorite/unfavorite the linked client straight from the appointment. */
    const toggleLinkedClientFavorite = async () => {
        if (!linkedClient) return;
        const next = !linkedClient.favorite;
        try {
            const token = await getAccessTokenSilently();
            const saved = await setClientFavorite(String(linkedClient.id), next, token);
            setClients((prev) => prev.map((x) => (String(x.id) === String(linkedClient.id) ? { ...x, favorite: saved.favorite } : x)));
            setAlert("success", next ? t('calendar.alerts.clientFavorited') : t('calendar.alerts.clientUnfavorited'));
        } catch (e) {
            setAlert("danger", e instanceof Error ? e.message : t('calendar.alerts.failFavorite'));
        }
    };

    /** Which contact fields the linked client is missing AND the user has now filled. */
    const missingClientFields = useMemo(() => {
        if (!linkedClient || !draft) return { phone: false, email: false };
        return {
            phone: !linkedClient.phone && !!draft.customerPhone.trim(),
            email: !linkedClient.email && !!draft.customerEmail.trim(),
        };
    }, [linkedClient, draft]);

    const hasMissingClientInfo =
        missingClientFields.phone || missingClientFields.email;

    /**
     * The linked client HAS a registered phone, but the one typed on the
     * appointment differs (comparing digits only, so masks like "(11) 99999-9999"
     * vs "11999999999" count as equal). Surfaced as a red hint + an "update"
     * action next to the Phone field.
     */
    const phoneMismatch = useMemo(() => {
        if (!linkedClient || !draft) return false;
        const typed = onlyDigits(draft.customerPhone);
        const stored = onlyDigits(linkedClient.phone ?? "");
        return !!typed && !!stored && typed !== stored;
    }, [linkedClient, draft]);

    /**
     * Price breakdown for the current draft: service base price minus the
     * linked client's discount (if any). `null` when no service is picked
     * or its price is unavailable.
     */
    const priceBreakdown = useMemo(() => {
        if (!draft?.serviceId) return null;
        const svc = services.find((s) => s.id === draft.serviceId);
        if (!svc) return null;

        // CONVENIO: base is the plan's negotiated table value (what the plan
        // owes — the receivable), never discounted. The copay is the client's
        // out-of-pocket and is tracked separately.
        if (draft.paymentType === 'CONVENIO') {
            const planRow = (svc.acceptedPlans ?? []).find(
                (p) => String(p.healthPlanId) === String(draft.healthPlanId)
            );
            // Table value = the plan's nominal value (reference). base = the
            // REPASSE: what the plan actually pays the business (the receivable).
            const tableValue = planRow?.negotiatedValue != null ? Number(planRow.negotiatedValue) : 0;
            let base = planRow ? effectiveRepasse(planRow) : 0;
            // Defense-in-depth: the repasse never exceeds the service price.
            if (typeof svc.price === 'number' && base > svc.price) base = svc.price;
            let copay = parseFloat(copayInput.replace(',', '.'));
            if (isNaN(copay) || copay < 0) copay = 0;
            copay = +copay.toFixed(2);
            const planName = businessHealthPlans.find(
                (p) => String(p.id) === String(draft.healthPlanId)
            )?.name ?? '';
            return {
                base,
                pct: 0,
                clientDiscount: 0,
                extraDiscount: 0,
                discount: 0,
                total: base,
                maxDiscountRate: maxDiscountRate ?? null,
                discountCapped: false,
                currency: currencySymbol(selectedBusiness?.currency),
                currencyCode: currencyCode(selectedBusiness?.currency),
                serviceName: svc.name,
                isVariable: false,
                /** True for a health-plan (convênio) appointment. */
                isConvenio: true,
                /** Selected health_plan id. */
                planId: draft.healthPlanId ?? null,
                /** Selected plan display name. */
                planName,
                /** The plan's table value (reference; base is the repasse/receivable). */
                tableValue,
                /** Client copay (coparticipação) for this appointment. */
                copay,
            };
        }

        const isVariable = svc.priceType === 'VARIABLE';
        // VARIABLE services take the user-entered price (falling back to the
        // service default); FIXED services always use the service's own price.
        let base: number;
        if (isVariable) {
            const parsed = parseFloat(priceInput.replace(',', '.'));
            base = !isNaN(parsed) && parsed >= 0
                ? parsed
                : (typeof svc.price === 'number' ? svc.price : 0);
        } else {
            if (typeof svc.price !== 'number') return null;
            base = svc.price;
        }
        const pctRaw = linkedClient?.discountPercent ?? 0;
        const pct = Math.max(0, Math.min(100, Number(pctRaw) || 0));
        const clientDiscount = +(base * pct / 100).toFixed(2);

        // Manual extra discount from the Payment tab. Capped at the
        // remainder so total never goes negative.
        const subtotalAfterClient = base - clientDiscount;
        let extra = parseFloat(extraDiscount.value.replace(',', '.'));
        if (isNaN(extra) || extra < 0) extra = 0;
        let extraAmount: number;
        if (extraDiscount.kind === 'PERCENT') {
            const pctClamped = Math.max(0, Math.min(100, extra));
            extraAmount = +(subtotalAfterClient * pctClamped / 100).toFixed(2);
        } else {
            extraAmount = +Math.min(extra, subtotalAfterClient).toFixed(2);
        }

        // Cap by the caller's discount tier. The total discount (client +
        // manual) can't exceed maxDiscountRate% of the base — this mirrors the
        // backend validation in payment-service so the UI never lets the user
        // build a payment the server will reject. null = not loaded → no cap.
        const tierMaxAmount = maxDiscountRate != null
            ? +(base * maxDiscountRate / 100).toFixed(2)
            : Infinity;
        // The tier caps what the caller may GRANT now — never what is already
        // stored on the appointment (e.g. a discount given by another member or
        // from the mobile app): clamping it here hid the discount and showed the
        // undiscounted total while the Appointments screen showed the real one.
        const storedExtra = Math.max(0, storedExtraDiscount || 0);
        const allowedExtra = Math.max(0, +(tierMaxAmount - clientDiscount).toFixed(2), storedExtra);
        const discountCapped = extraAmount > allowedExtra;
        if (discountCapped) extraAmount = allowedExtra;

        const discount = +(clientDiscount + extraAmount).toFixed(2);
        const total = +(base - discount).toFixed(2);
        return {
            base,
            pct,
            clientDiscount,
            extraDiscount: extraAmount,
            discount,
            total,
            /** Caller's max discount rate (%) — the tier cap on manual discount. */
            maxDiscountRate: maxDiscountRate ?? null,
            /** True when the requested manual discount was clamped to the tier cap. */
            discountCapped,
            /** Short symbol (e.g. "R$") derived from the business's currency. */
            currency: currencySymbol(selectedBusiness?.currency),
            /** ISO code (e.g. "BRL") used by Stripe + recordManualPayment. */
            currencyCode: currencyCode(selectedBusiness?.currency),
            serviceName: svc.name,
            /** True when the selected service is VARIABLE-priced (editable). */
            isVariable,
            /** True for a health-plan (convênio) appointment. */
            isConvenio: false,
            planId: null as string | null,
            planName: '',
            tableValue: 0,
            copay: 0,
        };
    }, [draft?.serviceId, draft?.paymentType, draft?.healthPlanId, services, linkedClient, selectedBusiness?.currency, extraDiscount, priceInput, copayInput, businessHealthPlans, maxDiscountRate, storedExtraDiscount]);

    /** Read-only ESTIMATED profit for the open appointment:
     *  price − member commission − resolved tax. Estimate only (commission varies
     *  by member; the real tax rate varies). null when data isn't ready. */
    const profitEstimate = useMemo(() => {
        if (!priceBreakdown || !draft?.serviceId) return null;
        const svc = services.find((s) => s.id === draft.serviceId);
        const price = priceBreakdown.base;
        if (!(price > 0)) return null;
        const ov = memberCommission?.overrides?.find((o) => String(o.serviceId) === String(draft.serviceId));
        const commissionRate = ov?.rate ?? memberCommission?.defaultRate ?? 0;
        const taxRate = (svc?.taxRate ?? companyTaxRate) ?? 0;
        const commission = +(price * commissionRate / 100).toFixed(2);
        const tax = +(price * taxRate / 100).toFixed(2);
        const profit = +(price - commission - tax).toFixed(2);
        return { price, commission, commissionRate, tax, taxRate, profit, currency: priceBreakdown.currency };
    }, [priceBreakdown, draft?.serviceId, services, memberCommission, companyTaxRate]);

    /**
     * Appointment-level payment state.
     *
     * The appointment-level payment status comes straight from the
     * persisted {@code appointment.paymentStatus} (kept in sync by
     * payment-service via GraphQL mutation). We don't derive it here.
     *
     * We still compute `collected` / `remaining` locally because the
     * inline split-payment editor needs them to validate the input
     * amount against what's actually left to charge.
     */
    const paidSummary = useMemo(() => {
        const total = priceBreakdown?.total ?? 0;
        let collected = 0;
        for (const t of apptTransactions) {
            const refunded = t.refundedAmount ?? 0;
            if (t.status === 'SUCCEEDED' || t.status === 'PARTIALLY_REFUNDED') {
                collected += (t.amount ?? 0) - refunded;
            }
        }
        collected = Math.round(collected * 100) / 100;
        const remaining = Math.max(0, Math.round((total - collected) * 100) / 100);
        const state: AppointmentPaymentStatus =
            draft?.paymentStatus ?? 'NOT_PAID';
        return { collected, remaining, total, state };
    }, [apptTransactions, priceBreakdown?.total, draft?.paymentStatus]);

    /** Minimal shape the picker needs — satisfied by both the full Client and the
     *  contact-only BookingClient. */
    type ClientPick = { id: string; firstName: string; lastName?: string | null; phone?: string | null; email?: string | null };

    // When the full client list isn't available (member without CLIENTS), search
    // the server by the typed name (debounced, contact-only, CALENDAR-gated).
    useEffect(() => {
        if (clients.length > 0) { setBookingMatches([]); return; } // full list present → client-side filter
        const q = (draft?.customerName ?? "").trim();
        if (!businessIdStr || q.length < 2) { setBookingMatches([]); return; }
        let cancelled = false;
        const h = setTimeout(async () => {
            try {
                const token = await getAccessTokenSilently();
                const res = await bookingClientLookup(businessIdStr, q, token).catch(() => []);
                if (!cancelled) setBookingMatches(res);
            } catch { if (!cancelled) setBookingMatches([]); }
        }, 300);
        return () => { cancelled = true; clearTimeout(h); };
    }, [draft?.customerName, clients.length, businessIdStr, getAccessTokenSilently]);

    /** Top-N clients matching the typed customerName / email / phone. Uses the
     *  pre-loaded list when available (CLIENTS view), else the server booking
     *  search (member without CLIENTS). */
    const clientMatches = useMemo<ClientPick[]>(() => {
        if (!draft) return [];
        const q = draft.customerName.trim().toLowerCase();
        if (!q) return [];
        if (clients.length === 0) return bookingMatches.slice(0, 8);
        return clients
            .filter((c) => {
                const name = formatClientLabel(c).toLowerCase();
                const email = (c.email ?? "").toLowerCase();
                const phone = c.phone ?? "";
                return name.includes(q) || email.includes(q) || phone.includes(q);
            })
            .slice(0, 8);
    }, [draft, clients, bookingMatches]);

    const pickClient = (c: ClientPick) => {
        setDraft((prev) =>
            prev
                ? {
                      ...prev,
                      clientId: c.id,
                      customerName: formatClientLabel(c),
                      customerPhone: c.phone ?? "",
                      customerEmail: c.email ?? "",
                  }
                : prev
        );
        setShowClientDropdown(false);
    };

    const clearLinkedClient = () => {
        setDraft((prev) => (prev ? { ...prev, clientId: null } : prev));
    };

    const handleSaveAsNewClient = async () => {
        if (!draft) return;
        if (!draft.customerName.trim()) {
            setAlert("danger", t('calendar.alerts.typeNameFirst'));
            return;
        }
        if (!businessIdStr) {
            setAlert("danger", t('calendar.alerts.noBusiness'));
            return;
        }
        // Same e-mail as an existing client of this business → link it instead of
        // creating a duplicate (the backend rejects duplicate e-mails as well).
        const email = draft.customerEmail?.trim().toLowerCase() ?? '';
        const existing = email
            ? clients.find((c) => (c.email ?? '').trim().toLowerCase() === email)
            : undefined;
        if (existing) {
            setDraft((prev) =>
                prev
                    ? {
                          ...prev,
                          clientId: existing.id,
                          customerName: formatClientLabel(existing),
                          customerPhone: existing.phone ?? prev.customerPhone,
                          customerEmail: existing.email ?? prev.customerEmail,
                      }
                    : prev
            );
            setAlert("success", t('calendar.alerts.clientLinked', { name: formatClientLabel(existing) }));
            return;
        }
        try {
            setSavingNewClient(true);
            const token = await getAccessTokenSilently();
            const { firstName, lastName } = splitFullName(draft.customerName);
            const saved = await saveClient(
                {
                    businessId: businessIdStr,
                    firstName,
                    lastName: lastName || null,
                    email: draft.customerEmail?.trim() || null,
                    phone: draft.customerPhone?.trim() || null,
                },
                token
            );
            setClients((prev) =>
                [...prev, saved].sort((a, b) =>
                    formatClientLabel(a).localeCompare(formatClientLabel(b))
                )
            );
            setDraft((prev) =>
                prev
                    ? {
                          ...prev,
                          clientId: saved.id,
                          customerName: formatClientLabel(saved),
                          customerPhone: saved.phone ?? "",
                          customerEmail: saved.email ?? "",
                      }
                    : prev
            );
            setAlert("success", t('calendar.alerts.clientCreated', { name: formatClientLabel(saved) }));
        } catch (e) {
            setAlert("danger", e instanceof Error ? e.message : t('calendar.alerts.failCreateClient'));
        } finally {
            setSavingNewClient(false);
        }
    };

    /** Overwrite the linked client's registered phone with the one typed here. */
    const handleUpdateClientPhone = async () => {
        if (!draft || !linkedClient || !businessIdStr) return;
        const newPhone = draft.customerPhone.trim();
        if (!newPhone) return;
        try {
            setUpdatingClientPhone(true);
            const token = await getAccessTokenSilently();
            const updated = await saveClient(
                {
                    id: linkedClient.id,
                    businessId: businessIdStr,
                    firstName: linkedClient.firstName,
                    lastName: linkedClient.lastName ?? null,
                    email: linkedClient.email ?? null,
                    phone: newPhone,
                    notes: linkedClient.notes ?? null,
                    discountPercent: linkedClient.discountPercent ?? null,
                },
                token
            );
            setClients((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
            setAlert("success", t('calendar.alerts.clientPhoneUpdated', { name: formatClientLabel(updated) }));
        } catch (e) {
            setAlert("danger", e instanceof Error ? e.message : t('calendar.alerts.failUpdatePhone'));
        } finally {
            setUpdatingClientPhone(false);
        }
    };

    /** Suggest a new time to the customer (they approve/decline from the app). */
    const handleProposeReschedule = async () => {
        if (!draft?.id) return;
        if (!rescheduleStart || !rescheduleEnd) {
            setAlert("danger", t('calendar.alerts.rescheduleTimeRequired'));
            return;
        }
        const startDate = new Date(rescheduleStart);
        const endDate = new Date(rescheduleEnd);
        if (endDate <= startDate) {
            setAlert("danger", t('calendar.alerts.rescheduleEndAfterStart'));
            return;
        }
        // Don't suggest a slot outside the team member's working hours / break or
        // the business hours (the greyed-out zones). #60: the per-business setting
        // (+ BOOK_OUTSIDE_HOURS / admin) now overrides this guard for admin-side booking.
        const member = teamMembers.find((t) => String(t.id) === String(draft.teamMemberId));
        if (member && !canBookOutsideHours) {
            const whError = validateWorkingHours(startDate, endDate, member, t);
            if (whError) {
                setAlert("danger", whError);
                return;
            }
        }
        if (!canBookOutsideHours) {
            const bhError = validateBusinessHours(startDate, endDate, businessHours, t);
            if (bhError) {
                setAlert("danger", bhError);
                return;
            }
        }
        try {
            setProposing(true);
            const token = await getAccessTokenSilently();
            const updated = await proposeReschedule(
                draft.id,
                `${rescheduleStart}:00`.slice(0, 19),
                `${rescheduleEnd}:00`.slice(0, 19),
                token
            );
            setDraft((prev) =>
                prev
                    ? {
                          ...prev,
                          rescheduleStatus: updated.rescheduleStatus ?? "PROPOSED",
                          proposedStartTime: updated.proposedStartTime ?? null,
                          proposedEndTime: updated.proposedEndTime ?? null,
                      }
                    : prev
            );
            setShowReschedule(false);
            setAlert("success", t('calendar.alerts.proposalSent'));
            // Keep the baseline in sync so the observer doesn't flag our own change.
            syncedRef.current = {
                startTime: syncedRef.current?.startTime,
                endTime: syncedRef.current?.endTime,
                status: updated.status,
            };
            getAppointmentHistory(draft.id, token).then(setDrawerHistory).catch(() => {});
            await reloadAppointments();
        } catch (e) {
            setAlert("danger", e instanceof Error ? e.message : t('calendar.alerts.failProposal'));
        } finally {
            setProposing(false);
        }
    };

    /**
     * Business accepts/declines the whole recurring series the open appointment
     * belongs to. Accept confirms every pending occurrence; decline cancels them.
     */
    const handleRespondSeries = async (accept: boolean) => {
        if (!draft?.seriesId) return;
        try {
            setRespondingSeries(true);
            const token = await getAccessTokenSilently();
            const affected = await respondSeries(
                draft.seriesId,
                accept,
                token,
                accept ? null : draft.cancellationReason || null
            );
            setAlert(
                "success",
                accept
                    ? t('calendar.alerts.seriesAccepted', { count: affected.length, plural: affected.length === 1 ? "" : "s" })
                    : t('calendar.alerts.seriesDeclined', { count: affected.length, plural: affected.length === 1 ? "" : "s", pluralPast: affected.length === 1 ? "" : "s" })
            );
            closeSidebar();
            await reloadAppointments();
        } catch (e) {
            setAlert("danger", e instanceof Error ? e.message : t('calendar.alerts.failRespondSeries'));
        } finally {
            setRespondingSeries(false);
        }
    };

    const handleField = <K extends keyof Draft>(field: K, value: Draft[K]) => {
        setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
    };

    /** Changing the START auto-fills the END so the user never sets it by hand:
     *  end = start + the selected service's duration; with no service, the previous
     *  start→end gap is preserved (default 60 min). The end DATE therefore always
     *  follows the start date automatically. */
    const handleStartChange = (value: string) => {
        setDraft((prev) => {
            if (!prev) return prev;
            const next: Draft = { ...prev, startTime: value };
            const startMs = value ? new Date(value).getTime() : NaN;
            if (!Number.isNaN(startMs)) {
                const svc = services.find((s) => s.id === prev.serviceId);
                let durationMin =
                    svc?.totalDuration && svc.totalDuration > 0 ? svc.totalDuration : 0;
                if (!durationMin && prev.startTime && prev.endTime) {
                    const gap =
                        (new Date(prev.endTime).getTime() - new Date(prev.startTime).getTime()) / 60000;
                    durationMin = gap > 0 ? gap : 60;
                }
                if (!durationMin) durationMin = 60;
                next.endTime = toLocalIso(new Date(startMs + durationMin * 60 * 1000));
            }
            return next;
        });
    };

    /** When the service changes, recompute endTime from totalDuration if available. */
    const handleServiceChange = (serviceId: string) => {
        const svc = services.find((s) => s.id === serviceId);
        // Reset the variable-price editor to the newly-picked service's
        // default price (it stays editable for VARIABLE services).
        setPriceInput(svc?.price != null ? String(svc.price) : '');
        // If the new service doesn't accept convênio, fall back to PARTICULAR.
        const acceptsConvenio = (svc?.acceptedPlans?.length ?? 0) > 0;
        setDraft((prev) => {
            if (!prev) return prev;
            const next: Draft = { ...prev, serviceId };
            if (!acceptsConvenio) {
                next.paymentType = 'PARTICULAR';
                next.healthPlanId = null;
                next.copayAmount = null;
            }
            if (svc?.totalDuration && prev.startTime) {
                const start = new Date(prev.startTime);
                const end = new Date(start.getTime() + svc.totalDuration * 60 * 1000);
                next.endTime = toLocalIso(end);
            }
            return next;
        });
        if (!acceptsConvenio) setCopayInput('');
    };

    /** Switch an appointment between PARTICULAR and CONVENIO. Picking CONVENIO
     *  defaults to the service's first accepted plan; PARTICULAR clears plan/copay. */
    const handlePaymentTypeChange = (type: AppointmentPaymentType) => {
        const svc = services.find((s) => s.id === draft?.serviceId);
        const plans = svc?.acceptedPlans ?? [];
        setDraft((prev) => {
            if (!prev) return prev;
            if (type === 'CONVENIO') {
                const planId = prev.healthPlanId
                    ?? (plans.length > 0 ? String(plans[0].healthPlanId) : null);
                return { ...prev, paymentType: 'CONVENIO', healthPlanId: planId };
            }
            return { ...prev, paymentType: 'PARTICULAR', healthPlanId: null, copayAmount: null };
        });
        if (type === 'PARTICULAR') setCopayInput('');
    };

    /** When the team member changes, reset the service if not in their allowed list. */
    const handleTeamMemberChange = (teamMemberId: string) => {
        setDraft((prev) => {
            if (!prev) return prev;
            const tm = teamMembers.find((t) => String(t.id) === String(teamMemberId));
            // No fallback: the new member only offers their assigned services.
            const allowed = tm ? services.filter((s) => (tm.serviceIds ?? []).includes(s.id)) : [];
            const stillAllowed = allowed.some((s) => s.id === prev.serviceId);
            const nextServiceId = stillAllowed
                ? prev.serviceId
                : (allowed[0]?.id ?? "");
            const next: Draft = { ...prev, teamMemberId, serviceId: nextServiceId };
            // If the auto-picked service changed, re-derive the end from its duration so
            // the end doesn't keep a stale length from the previous service/member.
            const svc = allowed.find((s) => s.id === nextServiceId);
            if (nextServiceId !== prev.serviceId && svc?.totalDuration && prev.startTime) {
                const start = new Date(prev.startTime);
                next.endTime = toLocalIso(new Date(start.getTime() + svc.totalDuration * 60 * 1000));
            }
            return next;
        });
    };

    /**
     * Compute free time intervals (minute-of-day) for `member` on
     * `candidateDate`: intersection of working hours with business hours,
     * minus the unbookable break (if any), minus existing non-cancelled
     * appointments, clamped to "now" if the candidate is today.
     * Returns `null` when the day is closed / off / has no usable window.
     */
    const computeFreeIntervals = (
        candidateDate: Date,
        member: TeamMember
    ): [number, number][] | null => {
        const dayKey = dayOfWeekOf(candidateDate);
        const wh = (member.workingHours ?? []).find((w) => w.dayOfWeek === dayKey);
        if (!wh) return null;

        let dayStart = timeToMinutes(wh.startTime);
        let dayEnd = timeToMinutes(wh.endTime);
        if (businessHours.length > 0) {
            const bizDay = businessHours.find((b) => b.dayOfWeek === dayKey);
            if (!bizDay) return null;
            dayStart = Math.max(dayStart, timeToMinutes(bizDay.openTime));
            dayEnd = Math.min(dayEnd, timeToMinutes(bizDay.closeTime));
            if (dayStart >= dayEnd) return null;
        }

        let intervals: [number, number][];
        if (!wh.allowBookingOnIntervalTime && wh.startIntervalTime && wh.endIntervalTime) {
            const bStart = timeToMinutes(wh.startIntervalTime);
            const bEnd = timeToMinutes(wh.endIntervalTime);
            const cbStart = Math.max(bStart, dayStart);
            const cbEnd = Math.min(bEnd, dayEnd);
            intervals = [];
            if (dayStart < cbStart) intervals.push([dayStart, cbStart]);
            if (cbEnd < dayEnd) intervals.push([cbEnd, dayEnd]);
            if (intervals.length === 0) intervals = [[dayStart, dayEnd]];
        } else {
            intervals = [[dayStart, dayEnd]];
        }

        const candidateDayStr = candidateDate.toDateString();
        const dayApts = appointments.filter(
            (a) =>
                String(a.teamMemberId) === String(member.id) &&
                new Date(a.startTime).toDateString() === candidateDayStr &&
                a.status !== "CANCELLED" &&
                // When editing, the appointment being edited shouldn't block itself.
                (!draft?.id || String(a.id) !== String(draft.id))
        );

        // Time blocks affecting this member that day (member-specific OR
        // business-wide). Treated exactly like a busy appointment so the slot
        // picker / "find next slot" never offer a blocked time.
        const dayBlocks = timeBlocks.filter(
            (b) =>
                (!b.teamMemberId || String(b.teamMemberId) === String(member.id)) &&
                new Date(b.startTime).toDateString() === candidateDayStr
        );

        // Each existing appointment reserves its own preparation (before) and
        // cleanup (after) buffer too — unless that service explicitly allows
        // bookings during them. Time blocks have no buffer. The reserved span is
        // [start - before, end + after] so a new booking can't be glued on top
        // of another pro's prep/cleanup time.
        const busy: { start: string; end: string; before: number; after: number }[] = [
            ...dayApts.map((a) => {
                // Inline lookup (NOT serviceById) — serviceById is declared later
                // in the component, and the slot-picker memos that call this run
                // before that line → would hit a temporal-dead-zone ReferenceError.
                const svc = services.find((s) => s.id === a.serviceId);
                const before =
                    svc?.startPreparationDuration && !svc.allowBookingDuringPreparationDuration
                        ? svc.startPreparationDuration
                        : 0;
                const after =
                    svc?.endAfterDuration && !svc.allowBookingDuringAfterDuration
                        ? svc.endAfterDuration
                        : 0;
                return { start: a.startTime, end: a.endTime, before, after };
            }),
            ...dayBlocks.map((b) => ({
                start: b.startTime,
                end: b.endTime,
                before: 0,
                after: 0,
            })),
        ];

        for (const { start: bs, end: be, before, after } of busy) {
            const aStart = minutesOfDay(new Date(bs)) - before;
            const aEnd = minutesOfDay(new Date(be)) + after;
            const next: [number, number][] = [];
            for (const [s, e] of intervals) {
                if (aEnd <= s || aStart >= e) {
                    next.push([s, e]);
                } else {
                    if (aStart > s) next.push([s, aStart]);
                    if (aEnd < e) next.push([aEnd, e]);
                }
            }
            intervals = next;
        }

        const now = new Date();
        const earliest =
            candidateDate.toDateString() === now.toDateString() ? minutesOfDay(now) : 0;
        return intervals
            .map(([s, e]) => [Math.max(s, earliest), e] as [number, number])
            .filter(([s, e]) => e > s);
    };

    /**
     * Min lead time (booking window) in minutes for the service being booked:
     * the service's own value, falling back to the business default, then 0.
     */
    const minLeadMinutes = (): number => {
        const svcW = selectedService?.bookingWindow;
        if (svcW != null) return svcW;
        return bizSettings?.bookingWindow ?? 0;
    };

    /** Preparation (before) / cleanup (after) buffer of the service being booked,
     *  honoring its allow-booking-during flags (a flag set → that side is 0). */
    const newServicePrep = (): { before: number; after: number } => ({
        before:
            selectedService?.startPreparationDuration &&
            !selectedService.allowBookingDuringPreparationDuration
                ? selectedService.startPreparationDuration
                : 0,
        after:
            selectedService?.endAfterDuration &&
            !selectedService.allowBookingDuringAfterDuration
                ? selectedService.endAfterDuration
                : 0,
    });

    /** Earliest bookable minute-of-day on `candidateDate` given the booking
     *  window. Returns null when the whole day falls before the lead window. */
    const earliestStartMinute = (candidateDate: Date): number | null => {
        const earliestAbs = new Date(Date.now() + minLeadMinutes() * 60 * 1000);
        const candY = ymdOf(candidateDate);
        const leadY = ymdOf(earliestAbs);
        if (candY < leadY) return null; // entire day is too soon
        if (candY === leadY) return minutesOfDay(earliestAbs);
        return 0;
    };

    /**
     * First open slot of length `durationMin` on the given day, or `null`.
     * Respects the booking window (min lead time) and the service's own prep/
     * cleanup buffers: the slot at `t` reserves [t - before, t + dur + after].
     */
    const findNextSlotOnDay = (
        candidateDate: Date,
        durationMin: number,
        member: TeamMember
    ): { start: Date; end: Date } | null => {
        const intervals = computeFreeIntervals(candidateDate, member);
        if (!intervals) return null;
        const earliest = earliestStartMinute(candidateDate);
        if (earliest === null) return null;
        const { before, after } = newServicePrep();
        const span = before + durationMin + after;
        // Align the suggested start to the booking-increment grid, exactly like the
        // slot picker (findSlotsOnDay). Without this, "find next slot" returned an
        // off-grid time (e.g. 09:52 = now+lead) that isn't an offered slot — it must
        // round UP to the next grid step (e.g. 10:00).
        const incrementMin =
            bizSettings?.bookingIncrement && bizSettings.bookingIncrement > 0
                ? bizSettings.bookingIncrement
                : 15;
        const roundUp = (m: number) => Math.ceil(m / incrementMin) * incrementMin;
        for (const [s, e] of intervals) {
            // valid start t needs t - before >= s and t >= earliest, snapped to grid
            const t = roundUp(Math.max(s + before, earliest));
            if (t - before >= s && t + durationMin + after <= e && e - s >= span) {
                const start = new Date(candidateDate);
                start.setHours(Math.floor(t / 60), t % 60, 0, 0);
                const end = new Date(start.getTime() + durationMin * 60 * 1000);
                return { start, end };
            }
        }
        return null;
    };

    /**
     * Multiple open slots of length `durationMin` on `candidateDate`, walking
     * each free interval in `incrementMin` steps. Caps at `max` results.
     * Respects the booking window + the service's own prep/cleanup buffers.
     */
    const findSlotsOnDay = (
        candidateDate: Date,
        durationMin: number,
        member: TeamMember,
        max: number,
        incrementMin: number = 15
    ): { start: Date; end: Date }[] => {
        const intervals = computeFreeIntervals(candidateDate, member);
        if (!intervals) return [];
        const earliest = earliestStartMinute(candidateDate);
        if (earliest === null) return [];
        const { before, after } = newServicePrep();
        const roundUp = (m: number) => Math.ceil(m / incrementMin) * incrementMin;
        // Mirror the consumer engine (discovery): clean-grid body, PLUS the exact
        // shift opening (so a pro who starts at e.g. 10:12 is bookable at 10:12,
        // matching the setup) and the latest-fitting start as edge slots.
        const out: { start: Date; end: Date }[] = [];
        const seen = new Set<number>();
        for (const [s, e] of intervals) {
            const earliestStart = Math.max(s + before, earliest);
            const add = (t: number) => {
                if (t < earliestStart || t + durationMin + after > e || seen.has(t)) return;
                seen.add(t);
                const start = new Date(candidateDate);
                start.setHours(Math.floor(t / 60), t % 60, 0, 0);
                out.push({ start, end: new Date(start.getTime() + durationMin * 60 * 1000) });
            };
            add(earliestStart);                              // exact opening (honors off-grid start)
            for (let t = roundUp(earliestStart); t + durationMin + after <= e; t += incrementMin) add(t);
            add(e - durationMin - after);                    // latest-fitting start
        }
        out.sort((a, b) => a.start.getTime() - b.start.getTime());
        return out.slice(0, max);
    };

    /* ---- Availability picker (week navigation + per-day slots) ---- */

    /** The team member + service duration the picker computes slots for. */
    const slotMember = useMemo(
        () => teamMembers.find((t) => String(t.id) === String(draft?.teamMemberId)),
        [teamMembers, draft?.teamMemberId]
    );
    const slotDuration =
        selectedService?.totalDuration && selectedService.totalDuration > 0
            ? selectedService.totalDuration
            : 60;

    /** Slot-grid step: business booking increment when set, else 15 min. Mirrors
     *  the consumer availability engine so admin-offered times line up with what
     *  clients see online. */
    const slotGridStep =
        bizSettings?.bookingIncrement && bizSettings.bookingIncrement > 0
            ? bizSettings.bookingIncrement
            : 15;

    /** The 7 days of the shown week, each flagged today/past and whether it has
     *  at least one free slot for the chosen team member + service. */
    const slotWeekDays = useMemo(() => {
        if (!slotWeekStart) return [];
        const todayY = ymdOf(new Date());
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(slotWeekStart);
            d.setDate(slotWeekStart.getDate() + i);
            const y = ymdOf(d);
            const hasSlots =
                slotMember && y >= todayY
                    ? findSlotsOnDay(d, slotDuration, slotMember, 1, slotGridStep).length > 0
                    : false;
            return {
                date: d,
                ymd: y,
                label: d.toLocaleDateString(undefined, { weekday: "short" }),
                dayNum: d.getDate(),
                isToday: y === todayY,
                isPast: y < todayY,
                hasSlots,
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slotWeekStart, slotMember, slotDuration, slotGridStep, selectedService, bizSettings, services, appointments, businessHours, timeBlocks]);

    /** Free slots on the selected day (empty for past days / no member). */
    const selectedDaySlots = useMemo(() => {
        if (!slotDay || !slotMember) return [];
        if (slotDay < ymdOf(new Date())) return [];
        return findSlotsOnDay(new Date(`${slotDay}T12:00:00`), slotDuration, slotMember, 32, slotGridStep);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slotDay, slotMember, slotDuration, slotGridStep, selectedService, bizSettings, services, appointments, businessHours, timeBlocks]);

    /** "12 – 18 jun 2026" label for the shown week. */
    const slotRangeLabel = useMemo(() => {
        if (!slotWeekStart) return "";
        const last = new Date(slotWeekStart);
        last.setDate(last.getDate() + 6);
        const mo = (d: Date) => d.toLocaleDateString(undefined, { month: "short" }).replace(".", "");
        return slotWeekStart.getMonth() === last.getMonth()
            ? `${slotWeekStart.getDate()} – ${last.getDate()} ${mo(last)} ${last.getFullYear()}`
            : `${slotWeekStart.getDate()} ${mo(slotWeekStart)} – ${last.getDate()} ${mo(last)} ${last.getFullYear()}`;
    }, [slotWeekStart]);

    const canSlotPrev = slotWeekStart
        ? ymdOf(slotWeekStart) > ymdOf(startOfWeekMon(new Date()))
        : false;

    const shiftSlotWeek = (deltaDays: number) =>
        setSlotWeekStart((prev) => {
            if (!prev) return prev;
            const x = new Date(prev);
            x.setDate(x.getDate() + deltaDays);
            return x;
        });

    /** Move the booking's Start/End to a given day, preserving the time-of-day
     *  and duration — so picking a day (or "Hoje") keeps the Start/End fields in
     *  sync and the user can't accidentally save a date that doesn't match the
     *  day they're looking at. */
    const syncDraftToDay = (ymd: string) => {
        if (!ymd) return;
        setDraft((prev) => {
            if (!prev) return prev;
            const timePart =
                prev.startTime && prev.startTime.length >= 16
                    ? prev.startTime.slice(11, 16)
                    : "09:00";
            const newStart = `${ymd}T${timePart}`;
            const durMs =
                prev.startTime && prev.endTime
                    ? Math.max(0, new Date(prev.endTime).getTime() - new Date(prev.startTime).getTime())
                    : 60 * 60 * 1000;
            const newEnd = toLocalIso(new Date(new Date(newStart).getTime() + durMs));
            return { ...prev, startTime: newStart, endTime: newEnd };
        });
    };

    const slotWeekToday = () => {
        const t = new Date();
        setSlotWeekStart(startOfWeekMon(t));
        setSlotDay(ymdOf(t));
        syncDraftToDay(ymdOf(t));
    };

    const pickSlotDate = (ymd: string) => {
        if (!ymd) return;
        setSlotDay(ymd);
        setSlotWeekStart(startOfWeekMon(new Date(`${ymd}T12:00:00`)));
        syncDraftToDay(ymd);
    };

    /* ---------- Time blocks (Bloqueio / time-off) ---------- */

    /** Reset the block-recurrence picker to its defaults (off). */
    const resetBlockRecur = () => {
        setBlockRecurEnabled(false);
        setBlockRecurFreq('WEEKLY');
        setBlockRecurEnd('count');
        setBlockRecurCount(4);
        setBlockRecurMonths(1);
        setBlockRecurUntil('');
        setBlockRecurWeekdays([]);
    };

    const closeBlock = () => {
        setShowBlock(false);
        setBlockDraft(null);
        resetBlockRecur();
    };

    const openBlockCreate = (startIso?: string) => {
        if (!canEditCalendar) return; // view-only: no time blocks
        resetBlockRecur();
        const base = startIso ? new Date(startIso) : new Date();
        base.setSeconds(0, 0);
        if (!startIso) {
            base.setMinutes(0);
            base.setHours(base.getHours() + 1); // default: next full hour
        }
        const end = new Date(base.getTime() + 60 * 60 * 1000);
        setBlockDraft({
            // Own-scoped users can only block their own time (not business-wide).
            teamMemberId: restrictOwn && myMemberId ? myMemberId : "",
            startTime: toLocalIso(base),
            endTime: toLocalIso(end),
            title: "",
        });
        setShowBlock(true);
    };

    const openBlockEdit = (b: TimeBlock) => {
        if (!canEditCalendar) return; // view-only: blocks aren't editable
        resetBlockRecur(); // recurrence applies to NEW blocks only
        setBlockDraft({
            id: b.id,
            seriesId: b.seriesId ?? null,
            teamMemberId: b.teamMemberId ? String(b.teamMemberId) : "",
            startTime: toLocalInput(b.startTime),
            endTime: toLocalInput(b.endTime),
            title: b.title ?? "",
        });
        setShowBlock(true);
    };

    const handleSaveBlock = async () => {
        if (!blockDraft || !businessIdStr) return;
        if (!canEditCalendar) {
            setAlert("danger", t('calendar.alerts.noPermissionEdit'));
            return;
        }
        if (!blockDraft.startTime || !blockDraft.endTime) {
            setAlert("danger", t('calendar.alerts.timesRequired'));
            return;
        }
        if (new Date(blockDraft.endTime) <= new Date(blockDraft.startTime)) {
            setAlert("danger", t('calendar.alerts.endAfterStart'));
            return;
        }
        const input: TimeBlockInput = {
            id: blockDraft.id ?? null,
            businessId: businessIdStr,
            teamMemberId: blockDraft.teamMemberId || null,
            startTime: withSeconds(blockDraft.startTime),
            endTime: withSeconds(blockDraft.endTime),
            title: blockDraft.title.trim() || null,
        };
        // Recurrence is create-only. When enabled, expand into a series of blocks.
        const isRecurring = !blockDraft.id && blockRecurEnabled;
        if (isRecurring && blockRecurFreq === 'WEEKLY_DAYS' && blockRecurWeekdays.length === 0) {
            setAlert("danger", t('calendar.form.weekdayRequired'));
            return;
        }
        try {
            setSavingBlock(true);
            const token = await getAccessTokenSilently();
            if (isRecurring) {
                const recurrence: RecurrenceInput = { freq: blockRecurFreq };
                if (blockRecurEnd === 'count') recurrence.count = blockRecurCount;
                else if (blockRecurEnd === 'date') recurrence.untilDate = blockRecurUntil || null;
                else recurrence.months = blockRecurMonths;
                if (blockRecurFreq === 'WEEKLY_DAYS') recurrence.weekdays = blockRecurWeekdays;
                const result = await createRecurringTimeBlock(input, recurrence, token);
                const made = result.created.length;
                const skipped = result.skipped.length;
                setAlert(made > 0 ? "success" : "danger",
                    t('calendar.alerts.blockSeriesSaved', { count: made, skipped }));
            } else {
                await saveTimeBlock(input, token);
                setAlert("success", t('calendar.alerts.blockSaved'));
            }
            closeBlock();
            await reloadAppointments();
        } catch (e) {
            // Backend rejects when an appointment already lives in the period.
            setAlert("danger", e instanceof Error ? e.message : t('calendar.alerts.failBlock'));
        } finally {
            setSavingBlock(false);
        }
    };

    const handleDeleteBlock = async () => {
        if (!blockDraft?.id) {
            closeBlock();
            return;
        }
        if (!canEditCalendar) {
            setAlert("danger", t('calendar.alerts.noPermissionEdit'));
            return;
        }
        // Recurring block → ask whether to remove the whole series or just this one.
        let wholeSeries = false;
        if (blockDraft.seriesId) {
            const all = await confirmDialog({
                title: t('common.confirmTitle'),
                message: t('calendar.alerts.deleteBlockSeries'),
                danger: true,
                confirmLabel: t('calendar.alerts.deleteBlockSeriesAll'),
                cancelLabel: t('calendar.alerts.deleteBlockSeriesJustThis'),
            });
            if (all) {
                wholeSeries = true;
            } else if (!(await confirmDialog({
                title: t('common.confirmTitle'),
                message: t('calendar.alerts.deleteBlockSeriesOne'),
                danger: true,
                confirmLabel: t('common.delete'),
            }))) {
                return; // user aborted
            }
        }
        try {
            setSavingBlock(true);
            const token = await getAccessTokenSilently();
            if (wholeSeries && blockDraft.seriesId) {
                await deleteTimeBlockSeries(blockDraft.seriesId, token);
                setAlert("success", t('calendar.alerts.blockSeriesRemoved'));
            } else {
                await deleteTimeBlock(blockDraft.id, token);
                setAlert("success", t('calendar.alerts.blockRemoved'));
            }
            closeBlock();
            await reloadAppointments();
        } catch (e) {
            setAlert("danger", e instanceof Error ? e.message : t('calendar.alerts.failRemoveBlock'));
        } finally {
            setSavingBlock(false);
        }
    };

    const handleSuggestSlot = () => {
        if (!draft) return;
        const member = teamMembers.find((tm) => String(tm.id) === String(draft.teamMemberId));
        if (!member) {
            setAlert("danger", t('calendar.alerts.noMemberSelected'));
            return;
        }
        const svc = services.find((s) => s.id === draft.serviceId);
        const duration = svc?.totalDuration ?? 60;

        // Always search from TODAY (not the draft's possibly-future day) so we find
        // the EARLIEST available slot — a draft sitting on a later day must not hide
        // free slots on earlier days. Search up to 14 days ahead.
        const startSearchFrom = new Date();
        for (let offset = 0; offset < 14; offset++) {
            const candidate = new Date(startSearchFrom);
            candidate.setDate(candidate.getDate() + offset);
            const slot = findNextSlotOnDay(candidate, duration, member);
            if (slot) {
                setDraft((prev) =>
                    prev ? { ...prev, startTime: toLocalIso(slot.start), endTime: toLocalIso(slot.end) } : prev
                );
                // Jump the availability picker to the found slot's day.
                setSlotDay(ymdOf(slot.start));
                setSlotWeekStart(startOfWeekMon(slot.start));
                setAlert(
                    "success",
                    t('calendar.alerts.suggested', { date: slot.start.toLocaleDateString(), time: formatClock(slot.start, timeFormat) })
                );
                return;
            }
        }
        setAlert("danger", t('calendar.alerts.noSlotsInDays', { name: member.firstName }));
    };

    /**
     * Persist just the extra discount onto the appointment without touching
     * other fields. Used by the "Apply" button on the Payment tab so the
     * user can save a discount without switching tabs.
     */
    const handleSaveExtraDiscount = async () => {
        if (!draft?.id || !businessIdStr || !priceBreakdown) return;
        if ((maxDiscountRate ?? 0) <= 0) {
            // Defense-in-depth: editor is hidden at 0% tier, but never persist a
            // discount the caller isn't allowed to give. Backend re-validates.
            setAlert('danger', t('calendar.alerts.noPermissionDiscount'));
            return;
        }
        try {
            setSaving(true);
            const token = await getAccessTokenSilently();
            const input: AppointmentInput = {
                id: draft.id,
                businessId: businessIdStr,
                teamMemberId: draft.teamMemberId,
                serviceId: draft.serviceId,
                clientId: draft.clientId,
                customerName: draft.customerName.trim(),
                customerPhone: draft.customerPhone || null,
                customerEmail: draft.customerEmail || null,
                startTime: withSeconds(draft.startTime),
                endTime: withSeconds(draft.endTime),
                status: draft.status,
                extraDiscount: priceBreakdown.extraDiscount,
                // Preserve convênio fields (this path is particular-only, but the
                // complete-input mapper would otherwise wipe them).
                paymentType: draft.paymentType ?? 'PARTICULAR',
                healthPlanId: draft.paymentType === 'CONVENIO' ? (draft.healthPlanId ?? null) : null,
                copayAmount: draft.paymentType === 'CONVENIO' ? (draft.copayAmount ?? null) : null,
                notes: draft.notes || null,
                externalLocation: draft.externalLocation || null,
                rebookReminderOn: draft.rebookReminderOn || null,
            };
            await saveAppointment(input, token);
            setAlert('success', t('calendar.alerts.discountSaved'));
            await reloadAppointments();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('calendar.alerts.failDiscount'));
        } finally {
            setSaving(false);
        }
    };

    /** True once a CONVENIO receivable has been recorded for this appointment
     *  (avoids recording the plan portion twice). */
    const hasConvenioReceivable = useMemo(
        () => apptTransactions.some((tx) => tx.method === 'CONVENIO' && !tx.deleted),
        [apptTransactions]
    );

    /**
     * Record the convênio receivable: a PENDING transaction (method CONVENIO)
     * for the plan's negotiated table value. The plan owes it; it's excluded
     * from realized faturamento until marked received (contas a receber).
     */
    const recordConvenioReceivable = async () => {
        if (!draft?.id || !priceBreakdown?.isConvenio || !businessIdStr) return;
        const amount = priceBreakdown.base;
        if (!(amount > 0)) {
            setAlert('danger', t('calendar.convenio.noTableValue'));
            return;
        }
        const total = amount + (priceBreakdown.copay || 0);
        try {
            setConvenioBusy(true);
            const token = await getAccessTokenSilently();
            await recordManualPayment({
                businessId: businessIdStr,
                appointmentId: draft.id,
                appointmentTotal: total,
                clientId: draft.clientId ?? null,
                customerName: draft.customerName?.trim() || null,
                amount,
                currency: priceBreakdown.currencyCode,
                method: 'CONVENIO',
                description: `${t('calendar.convenio.receivableDesc')} — ${priceBreakdown.planName || priceBreakdown.serviceName || ''}`.trim(),
            }, token);
            setAlert('success', t('calendar.convenio.receivableRecorded'));
            await loadAppointmentTransactions(draft.id);
            await reloadAppointments();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('calendar.convenio.recordError'));
        } finally {
            setConvenioBusy(false);
        }
    };

    /** Record the client copay (coparticipação) as a normal SUCCEEDED payment. */
    const recordConvenioCopay = async () => {
        if (!draft?.id || !priceBreakdown?.isConvenio || !businessIdStr) return;
        const amount = priceBreakdown.copay;
        if (!(amount > 0)) return;
        const total = priceBreakdown.base + amount;
        try {
            setConvenioBusy(true);
            const token = await getAccessTokenSilently();
            await recordManualPayment({
                businessId: businessIdStr,
                appointmentId: draft.id,
                appointmentTotal: total,
                clientId: draft.clientId ?? null,
                customerName: draft.customerName?.trim() || null,
                amount,
                currency: priceBreakdown.currencyCode,
                method: copayMethod,
                description: `${t('calendar.convenio.copayDesc')} — ${priceBreakdown.serviceName || ''}`.trim(),
            }, token);
            setAlert('success', t('calendar.convenio.copayRecorded'));
            await loadAppointmentTransactions(draft.id);
            await reloadAppointments();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('calendar.convenio.recordError'));
        } finally {
            setConvenioBusy(false);
        }
    };

    const handleSave = async () => {
        if (!draft) return;
        if (!canEditCalendar) {
            // Defense-in-depth: Save is hidden in view-only mode. Backend enforces.
            setAlert("danger", t('calendar.alerts.noPermissionEdit'));
            return;
        }
        if (!draft.customerName.trim()) {
            setAlert("danger", t('calendar.alerts.customerRequired'));
            return;
        }
        if (!draft.serviceId || !draft.teamMemberId) {
            setAlert("danger", t('calendar.alerts.serviceAndMemberRequired'));
            return;
        }
        if (!draft.startTime || !draft.endTime) {
            setAlert("danger", t('calendar.alerts.timesRequired'));
            return;
        }
        const startDate = new Date(draft.startTime);
        const endDate = new Date(draft.endTime);
        if (endDate <= startDate) {
            setAlert("danger", t('calendar.alerts.endAfterStart'));
            return;
        }
        if (!businessIdStr) {
            setAlert("danger", t('calendar.alerts.noBusiness'));
            return;
        }

        // Cross-aggregate validations (client-side until we move to backend).
        const member = teamMembers.find(
            (t) => String(t.id) === String(draft.teamMemberId)
        );
        if (member) {
            const serviceMatchError = validateServiceMatch(draft.serviceId, member, t);
            if (serviceMatchError) {
                setAlert("danger", serviceMatchError);
                return;
            }
            // #60: skip the hours guards when booking outside hours is allowed for this caller.
            if (!canBookOutsideHours) {
                const whError = validateWorkingHours(startDate, endDate, member, t);
                if (whError) {
                    setAlert("danger", whError);
                    return;
                }
            }
        }
        if (!canBookOutsideHours) {
            const bhError = validateBusinessHours(startDate, endDate, businessHours, t);
            if (bhError) {
                setAlert("danger", bhError);
                return;
            }
        }

        const phoneCountry = resolvePhoneCountry(undefined, selectedBusiness?.currency);
        if (!isPhoneComplete(draft.customerPhone, phoneCountry)) {
            setAlert("danger", t('common.invalidPhone'));
            return;
        }

        const input: AppointmentInput = {
            id: draft.id ?? null,
            businessId: businessIdStr,
            teamMemberId: draft.teamMemberId,
            serviceId: draft.serviceId,
            clientId: draft.clientId,
            customerName: draft.customerName.trim(),
            customerPhone: draft.customerPhone || null,
            customerEmail: draft.customerEmail || null,
            startTime: withSeconds(draft.startTime),
            endTime: withSeconds(draft.endTime),
            status: draft.status,
            // Convênio never applies a discount.
            extraDiscount: priceBreakdown?.isConvenio ? 0 : (priceBreakdown?.extraDiscount ?? 0),
            // Persist the per-appointment price for VARIABLE services and for
            // CONVENIO (the plan's negotiated table value); FIXED particular
            // keeps deriving from the service price (null).
            price: (priceBreakdown?.isVariable || priceBreakdown?.isConvenio)
                ? priceBreakdown.base
                : null,
            paymentType: draft.paymentType ?? 'PARTICULAR',
            healthPlanId: draft.paymentType === 'CONVENIO' ? (draft.healthPlanId ?? null) : null,
            copayAmount: draft.paymentType === 'CONVENIO' ? (draft.copayAmount ?? null) : null,
            notes: draft.notes || null,
            // House-call address set by the client at booking — preserve on admin edits.
            externalLocation: draft.externalLocation || null,
            rebookReminderOn: draft.rebookReminderOn || null,
            // Only meaningful when cancelling; harmless otherwise.
            cancellationReason:
                draft.status === "CANCELLED" || draft.status === "DECLINED"
                    ? draft.cancellationReason || null
                    : null,
        };

        try {
            setSaving(true);
            const token = await getAccessTokenSilently();

            // Optionally update the linked client with the newly typed phone/email
            // (only fills blanks — never overwrites a value the client already had).
            if (
                draft.clientId &&
                linkedClient &&
                updateLinkedClient &&
                hasMissingClientInfo
            ) {
                try {
                    const updatedClient = await saveClient(
                        {
                            id: linkedClient.id,
                            businessId: businessIdStr,
                            firstName: linkedClient.firstName,
                            lastName: linkedClient.lastName ?? null,
                            email: missingClientFields.email
                                ? draft.customerEmail.trim()
                                : linkedClient.email ?? null,
                            phone: missingClientFields.phone
                                ? draft.customerPhone.trim()
                                : linkedClient.phone ?? null,
                            notes: linkedClient.notes ?? null,
                        },
                        token
                    );
                    setClients((prev) =>
                        prev.map((c) => (c.id === updatedClient.id ? updatedClient : c))
                    );
                } catch (e) {
                    // Don't block the appointment save if the client patch fails.
                    setAlert(
                        "danger",
                        `Failed to update client record: ${
                            e instanceof Error ? e.message : "unknown error"
                        }`
                    );
                }
            }

            // Recurring series (create only). Books the free occurrences and
            // reports the ones skipped for agenda conflicts.
            if (!draft.id && recurEnabled && selectedServiceAllowsRecurring) {
                const recurrence: RecurrenceInput = { freq: recurFreq };
                if (recurEnd === 'count') recurrence.count = recurCount;
                else if (recurEnd === 'date') recurrence.untilDate = recurUntil || null;
                else recurrence.months = recurMonths;
                if (recurFreq === 'WEEKLY_DAYS') {
                    // Drop closed days only when hours are configured; otherwise
                    // keep what the user picked (don't block when hours unknown).
                    const days = openWeekdays.size > 0
                        ? recurWeekdays.filter((d) => openWeekdays.has(d))
                        : recurWeekdays;
                    if (days.length === 0) {
                        setAlert("danger", t('calendar.alerts.noWeekdaySelected'));
                        setSaving(false);
                        return;
                    }
                    recurrence.weekdays = days;
                }

                // The business is creating this series itself (not an online
                // client request), so the occurrences are already CONFIRMED —
                // no accept/decline step needed.
                const result = await createRecurringAppointment(
                    { ...input, status: "CONFIRMED" },
                    recurrence,
                    token
                );
                const made = result.created.length;
                const skipped = result.skipped.length;
                if (made === 0) {
                    setAlert(
                        "danger",
                        skipped > 0
                            ? t('calendar.alerts.bookingAllSkipped', { count: skipped })
                            : t('calendar.alerts.bookingNone')
                    );
                } else if (skipped > 0) {
                    setAlert(
                        "success",
                        t('calendar.alerts.bookingPartial', {
                            created: made, createdPlural: made > 1 ? "s" : "",
                            skipped, skippedPlural: skipped > 1 ? "s" : ""
                        }) +
                            "\n" +
                            result.skipped.map((s) => `• ${formatProposedTime(s)}`).join("\n")
                    );
                } else {
                    setAlert("success", t('calendar.alerts.bookingCreated', { count: made, plural: made > 1 ? "s" : "" }));
                }
                closeSidebar();
                await reloadAppointments();
                return;
            }

            const saved = await saveAppointment(input, token);
            setAlert("success", t('calendar.alerts.saved', { name: saved.customerName }));
            closeSidebar();
            await reloadAppointments();
        } catch (e) {
            setAlert("danger", e instanceof Error ? e.message : t('calendar.alerts.failSave'));
        } finally {
            setSaving(false);
        }
    };

    // (Stripe Checkout is now invoked inline from `handleRecordManualPayment` —
    // when the user puts an amount on the CARD_STRIPE split row.)

    /**
     * Record the current split as 1+ Transaction rows.
     * Splits are read from `manualSplit` state (one row per method, amount as string).
     * Each non-card line is written immediately via recordManualPayment.
     * If a CARD_STRIPE line is present, Stripe Checkout opens for that amount
     * (the webhook will create the Transaction when the customer pays).
     *
     * When every line is non-card (no async wait), we also flip the appointment
     * status to COMPLETED so the books and the calendar agree.
     */
    const handleRecordManualPayment = async () => {
        if (!draft || !draft.id || !priceBreakdown || !businessIdStr) return;
        if (!canCheckout) {
            // Defense-in-depth: the editor is hidden without CHECKOUT, but never
            // call the mutation if the grant says no. Backend enforces too.
            setAlert('danger', t('calendar.alerts.noPermissionPayment'));
            return;
        }

        // Build the list of {method, amount} lines from the input state.
        const lines: { method: PaymentMethod; amount: number }[] = [];
        (Object.keys(manualSplit) as PaymentMethod[]).forEach((m) => {
            const raw = (manualSplit[m] ?? '').trim();
            if (!raw) return;
            const v = parseFloat(raw.replace(',', '.'));
            if (!isNaN(v) && v > 0) lines.push({ method: m, amount: v });
        });

        if (lines.length === 0) {
            setAlert('danger', t('calendar.alerts.failPayment'));
            return;
        }

        const sum = lines.reduce((s, l) => s + l.amount, 0);
        // Remaining balance when partially paid; otherwise full service price.
        const total = paidSummary.remaining > 0
            ? paidSummary.remaining
            : priceBreakdown.total;
        // Allow underpayment (will stay as Partial Paid) but never overpayment.
        if (sum > total + 0.01) {
            setAlert(
                'danger',
                `Split total (${priceBreakdown.currency}${sum.toFixed(2)}) is more than what's due (${priceBreakdown.currency}${total.toFixed(2)}).`
            );
            return;
        }
        const fullyCovered = Math.abs(sum - total) <= 0.01;

        // Gift card: the printed code is required at charge time (backend enforces too).
        if (lines.some((l) => l.method === 'GIFT_CARD') && !giftCardCode.trim()) {
            setAlert('danger', t('payments.giftCard.codeRequired'));
            return;
        }

        const cardLine = lines.find((l) => l.method === 'CARD_STRIPE');
        const manualLines = lines.filter((l) => l.method !== 'CARD_STRIPE');

        try {
            setRecordingManual(true);
            const token = await getAccessTokenSilently();
            // ISO code (USD, BRL, EUR…) lifted off the breakdown so Stripe
            // and recordManualPayment both bill in the business's currency.
            const isoCurrency = priceBreakdown.currencyCode;

            // 1. Write each manual line first (Cash/PIX/etc.) — these are atomic.
            //    Pro-rate the breakdown discount so each row carries its share.
            for (const l of manualLines) {
                const proRatedDiscount =
                    total > 0 ? (priceBreakdown.discount * l.amount) / total : 0;
                await recordManualPayment(
                    {
                        businessId: businessIdStr,
                        appointmentId: draft.id,
                        /* Snapshot of the full appointment total — lets
                           payment-service distinguish PARTIAL_PAID vs PAID
                           when it computes the rollup. */
                        appointmentTotal: priceBreakdown.total,
                        clientId: draft.clientId ?? null,
                        customerName: draft.customerName,
                        amount: l.amount,
                        currency: isoCurrency,
                        discountAmount: Number(proRatedDiscount.toFixed(2)),
                        method: l.method,
                        giftCardCode: l.method === 'GIFT_CARD' ? giftCardCode.trim() : null,
                        description: priceBreakdown.serviceName || 'Service payment',
                    },
                    token
                );
            }

            // 2. If there's a card portion, open Stripe Checkout for that amount.
            //    The webhook eventually writes the Transaction + status update.
            let stripeOpened = false;
            if (cardLine) {
                const proRatedDiscount =
                    total > 0 ? (priceBreakdown.discount * cardLine.amount) / total : 0;
                const successUrl = `${window.location.origin}/home?stripe_payment=success&appointment=${draft.id}`;
                const cancelUrl = `${window.location.origin}/home?stripe_payment=cancel&appointment=${draft.id}`;
                const result = await createStripeCheckoutSession(
                    {
                        businessId: businessIdStr,
                        appointmentId: draft.id,
                        appointmentTotal: priceBreakdown.total,
                        clientId: draft.clientId ?? null,
                        customerName: draft.customerName,
                        customerEmail: draft.customerEmail || null,
                        amount: cardLine.amount,
                        currency: isoCurrency,
                        discountAmount: Number(proRatedDiscount.toFixed(2)),
                        description:
                            priceBreakdown.serviceName ||
                            `Service charge for ${draft.customerName}`,
                        successUrl,
                        cancelUrl,
                    },
                    token
                );
                window.open(result.url, '_blank', 'noopener,noreferrer');
                stripeOpened = true;
            }

            // 3. If we covered the full total with manual lines only, mark the
            //    appointment COMPLETED. With a card line, we leave it as-is — the
            //    webhook (or the next manual edit) will bump status when paid.
            const allManual = !cardLine;
            // Only mark COMPLETED when this payment closes the balance —
            // partial manual payments leave the appointment in its current
            // state so the user can come back and finish it later.
            if (allManual && fullyCovered && draft.status !== 'COMPLETED') {
                try {
                    const input: AppointmentInput = {
                        id: draft.id,
                        businessId: businessIdStr,
                        teamMemberId: draft.teamMemberId,
                        serviceId: draft.serviceId,
                        clientId: draft.clientId,
                        customerName: draft.customerName.trim(),
                        customerPhone: draft.customerPhone || null,
                        customerEmail: draft.customerEmail || null,
                        startTime: withSeconds(draft.startTime),
                        endTime: withSeconds(draft.endTime),
                        status: 'COMPLETED',
                        // Complete input — preserve money/convênio fields so the
                        // auto-complete re-save never wipes them.
                        extraDiscount: priceBreakdown?.isConvenio ? 0 : (priceBreakdown?.extraDiscount ?? 0),
                        price: (priceBreakdown?.isVariable || priceBreakdown?.isConvenio)
                            ? priceBreakdown.base
                            : null,
                        paymentType: draft.paymentType ?? 'PARTICULAR',
                        healthPlanId: draft.paymentType === 'CONVENIO' ? (draft.healthPlanId ?? null) : null,
                        copayAmount: draft.paymentType === 'CONVENIO' ? (draft.copayAmount ?? null) : null,
                        externalLocation: draft.externalLocation || null,
                        notes: draft.notes || null,
                        rebookReminderOn: draft.rebookReminderOn || null,
                    };
                    const saved = await saveAppointment(input, token);
                    setDraft((prev) =>
                        prev ? { ...prev, status: saved.status ?? 'COMPLETED' } : prev
                    );
                } catch (e) {
                    // Status bump failed but the payment is fine — surface a softer notice.
                    // eslint-disable-next-line no-console
                    console.warn('Failed to mark appointment as COMPLETED:', e);
                }
            }

            setManualSplit({});
            setGiftCardCode('');
        setGiftCardCode('');

            // 4. Compose a success message tailored to the mix.
            if (stripeOpened && manualLines.length > 0) {
                setAlert(
                    'success',
                    t('calendar.alerts.stripeAndManual', {
                        count: manualLines.length, plural: manualLines.length === 1 ? '' : 's',
                        noun: manualLines.length === 1 ? 'payment' : 'payments',
                        currency: priceBreakdown.currency, amount: cardLine!.amount.toFixed(2)
                    })
                );
            } else if (stripeOpened) {
                setAlert(
                    'success',
                    t('calendar.alerts.stripeOnly')
                );
            } else if (lines.length === 1) {
                setAlert(
                    'success',
                    t('calendar.alerts.manualSingle', { method: t(`payments.method.${lines[0].method}`) })
                );
            } else {
                setAlert(
                    'success',
                    t('calendar.alerts.manualSplit', { count: lines.length })
                );
            }

            if (allManual) {
                // Refresh calendar so the new status shows up.
                await reloadAppointments();
            }
            // Re-pull the transactions + refresh the appointment so the
            // tab badge, "Already paid" block and Total due all reflect
            // the new state immediately (no close-and-reopen needed).
            if (draft.id) {
                await loadAppointmentTransactions(draft.id);
                try {
                    const fresh = await getAppointmentById(draft.id, token);
                    if (fresh) {
                        setDraft((prev) =>
                            prev ? { ...prev, paymentStatus: fresh.paymentStatus } : prev
                        );
                    }
                } catch {
                    /* non-critical — banner refresh */
                }
            }
        } catch (e) {
            setAlert(
                'danger',
                e instanceof Error ? e.message : t('calendar.alerts.failPayment')
            );
        } finally {
            setRecordingManual(false);
        }
    };

    // Reset the hold panel when switching appointments.
    useEffect(() => {
        setHoldResolved(false);
    }, [draft?.id]);

    /**
     * Settle the pre-authorization hold (attendant action on the payment screen):
     * - 'process': capture the hold + charge the remainder on the card, then log it.
     * - 'noshow':  capture the held amount only (no-show / partial), then log it.
     * - 'cancel':  release the hold (attendant receives another way below).
     * The Stripe op moves the money; recordManualPayment(CARD_STRIPE) records the
     * Transaction + syncs the appointment paymentStatus (reuses the tested path).
     */
    const settleHold = async (mode: "process" | "noshow" | "cancel") => {
        if (!draft?.id || !draft.holdPaymentIntentId || !priceBreakdown) return;
        const pi = draft.holdPaymentIntentId;
        const holdAmt = draft.holdAmount ?? 0;
        const due = paidSummary.remaining > 0 ? paidSummary.remaining : priceBreakdown.total;
        const iso = priceBreakdown.currencyCode;
        const sym = priceBreakdown.currency;
        try {
            setHoldBusy(true);
            const token = await getAccessTokenSilently();
            if (mode === "cancel") {
                await cancelClientHold(businessIdStr, pi, token);
                setAlert("success", t('calendar.alerts.holdCancelled'));
            } else if (mode === "noshow") {
                const cap = await captureClientHold(businessIdStr, pi, holdAmt > 0 ? holdAmt : null, token);
                const amt = cap.amount ?? holdAmt;
                await recordManualPayment(
                    {
                        businessId: businessIdStr, appointmentId: draft.id,
                        appointmentTotal: priceBreakdown.total, clientId: draft.clientId ?? null,
                        customerName: draft.customerName, amount: amt, currency: iso,
                        method: "CARD_STRIPE", description: "No-show / pré-autorização",
                    },
                    token
                );
                setAlert("success", t('calendar.alerts.holdNoshow', { currency: sym, amount: amt.toFixed(2) }));
            } else {
                // process: capture (up to the hold) + charge any remainder
                const captureAmt = Math.min(holdAmt, due);
                await captureClientHold(businessIdStr, pi, captureAmt > 0 ? captureAmt : null, token);
                const chargeAmt = Math.max(0, Number((due - holdAmt).toFixed(2)));
                if (chargeAmt > 0 && draft.clientId != null) {
                    await chargeClientCard(businessIdStr, draft.clientId, chargeAmt, iso, token);
                }
                await recordManualPayment(
                    {
                        businessId: businessIdStr, appointmentId: draft.id,
                        appointmentTotal: priceBreakdown.total, clientId: draft.clientId ?? null,
                        customerName: draft.customerName, amount: due, currency: iso,
                        method: "CARD_STRIPE",
                        description: priceBreakdown.serviceName || "Pagamento (cartão)",
                    },
                    token
                );
                setAlert("success", t('calendar.alerts.holdCharged', { currency: sym, amount: due.toFixed(2) }));
            }
            setHoldResolved(true);
            await reloadAppointments();
            if (draft.id) {
                await loadAppointmentTransactions(draft.id);
                try {
                    const fresh = await getAppointmentById(draft.id, token);
                    if (fresh) {
                        setDraft((prev) => (prev ? { ...prev, paymentStatus: fresh.paymentStatus,
                            holdPaymentIntentId: fresh.holdPaymentIntentId, holdAmount: fresh.holdAmount } : prev));
                    }
                } catch { /* non-critical */ }
            }
        } catch (e) {
            setAlert("danger", e instanceof Error ? e.message : t('calendar.alerts.failHold'));
        } finally {
            setHoldBusy(false);
        }
    };

    /** Sum of all entered amounts (NaN inputs counted as 0). */
    const manualSplitTotal = useMemo(() => {
        return (Object.keys(manualSplit) as PaymentMethod[]).reduce((s, m) => {
            const v = parseFloat((manualSplit[m] ?? '').replace(',', '.'));
            return s + (isNaN(v) || v < 0 ? 0 : v);
        }, 0);
    }, [manualSplit]);

    /** Convenience helper for the "Pay full in X" quick actions. */
    const setManualSplitTo = (method: PaymentMethod, amount: number) => {
        setManualSplit({ [method]: amount.toFixed(2) });
    };

    const handleDelete = async () => {
        if (!draft?.id) return;
        if (!canEditCalendar) {
            setAlert("danger", t('calendar.alerts.noPermissionEdit'));
            return;
        }
        // Recurring occurrence → ask whether to delete the whole series or just this one.
        let wholeSeries = false;
        if (draft.seriesId) {
            const all = await confirmDialog({
                title: t('common.confirmTitle'),
                message: t('calendar.alerts.deleteSeries'),
                danger: true,
                confirmLabel: t('calendar.alerts.deleteSeriesAll'),
                cancelLabel: t('calendar.alerts.deleteSeriesJustThis'),
            });
            if (all) {
                wholeSeries = true;
            } else if (!(await confirmDialog({
                title: t('common.confirmTitle'),
                message: t('calendar.alerts.deleteSeriesOne'),
                danger: true,
                confirmLabel: t('common.delete'),
            }))) {
                return; // user aborted
            }
        } else if (!(await confirmDialog({
            title: t('common.confirmTitle'),
            message: t('calendar.alerts.deleteOne', { name: draft.customerName }),
            danger: true,
            confirmLabel: t('common.delete'),
        }))) {
            return;
        }
        try {
            setSaving(true);
            const token = await getAccessTokenSilently();
            if (wholeSeries && draft.seriesId) {
                await deleteSeries(draft.seriesId, token);
                setAlert("success", t('calendar.alerts.seriesDeleted'));
            } else {
                await deleteAppointment(draft.id, token);
                setAlert("success", t('calendar.alerts.deleted'));
            }
            closeSidebar();
            await reloadAppointments();
        } catch (e) {
            setAlert("danger", e instanceof Error ? e.message : t('calendar.alerts.failDelete'));
        } finally {
            setSaving(false);
        }
    };

    const toggleTeamMemberVisibility = (id: string) => {
        setHiddenTeamMembers((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const serviceById = (id: string) => services.find((s) => s.id === id);
    const teamMemberById = (id: string) =>
        teamMembers.find((t) => String(t.id) === String(id));

    /**
     * Color for a team member by id — uses their configured `color`, falling
     * back to a palette color picked by their position in `teamMembers`.
     * Keeps calendar events, Show filter buttons and the appointment select
     * visually in sync.
     */
    const teamMemberColorById = (id: string | null | undefined): string | null => {
        if (!id) return null;
        const idx = teamMembers.findIndex((t) => String(t.id) === String(id));
        if (idx < 0) return null;
        return resolveTeamMemberColor(teamMembers[idx], idx);
    };

    /** Map visible appointments to FullCalendar events, filtered by team-member visibility. */
    const apptEvents: EventInput[] = appointments
        .filter((a) => !HIDDEN_FROM_CALENDAR.includes(a.status))
        .filter((a) => !hiddenTeamMembers.has(String(a.teamMemberId)))
        .filter((a) => !hiddenServices.has(String(a.serviceId)))
        .map((a) => {
            const svc = serviceById(a.serviceId);
            const tm = teamMemberById(a.teamMemberId);
            // House-call (external) appointments stand out: red fill, bold text
            // and a pin so the team member knows they travel to the client.
            const isExternal = !!(a.externalLocation && a.externalLocation.trim());
            // Client cancelled but a fee is owed and wasn't auto-charged → staff
            // must collect it (pix/other). Make it impossible to miss on the grid.
            const feeDue = !!a.cancellationFeeOutstanding;
            const title =
                (feeDue ? "⚠️ " : "") +
                (isExternal ? "📍 " : "") +
                (svc ? `${a.customerName} — ${svc.name ?? ""}` : a.customerName) +
                (tm ? ` (${tm.firstName})` : "");
            const tmColor = teamMemberColorById(a.teamMemberId);
            const bg = feeDue
                ? "#f59e0b"
                : isExternal ? "#d32f2f" : (tmColor || STATUS_COLOR[a.status] || "#0d6efd");
            // Same color for fill and border so the team member color also
            // drives the bullet in List view and the compact pill in Month
            // view, not just the week/day grids.
            return {
                id: a.id,
                title,
                start: a.startTime,
                end: a.endTime,
                backgroundColor: bg,
                borderColor: feeDue ? "#b45309" : (isExternal ? "#9a0007" : bg),
                textColor: feeDue ? "#3a2a00" : (isExternal ? "#ffffff" : readableTextColor(bg)),
                // Status still surfaces via classNames (CSS can strikethrough
                // CANCELLED, dim NO_SHOW, etc) without stealing the color slot.
                classNames: [
                    `apt-status-${a.status.toLowerCase()}`,
                    ...(isExternal ? ["apt-external-event"] : []),
                    ...(feeDue ? ["apt-fee-due"] : []),
                ],
                extendedProps: { appointment: a },
            };
        });

    /** Blocks render as striped grey events (clickable to edit/remove). A block
     *  with no team member (business-wide) is always shown; a member-specific
     *  one respects the team-member visibility filter. */
    const blockEvents: EventInput[] = timeBlocks
        .filter((b) => !b.teamMemberId || !hiddenTeamMembers.has(String(b.teamMemberId)))
        .map((b) => {
            const tm = b.teamMemberId ? teamMemberById(b.teamMemberId) : null;
            const label = b.title?.trim() || t('calendar.block.blockedLabel');
            return {
                id: `block-${b.id}`,
                title: `${label}${tm ? ` (${tm.firstName})` : ""}`,
                start: b.startTime,
                end: b.endTime,
                backgroundColor: "#6b7280",
                borderColor: "#4b5563",
                textColor: "#ffffff",
                classNames: ["time-block-event"],
                extendedProps: { block: b },
            };
        });

    /** Light-grey, non-interactive "preparation"/"cleanup" blocks glued before
     *  and after an appointment, sized from the service's configured prep/cleanup
     *  durations. Purely visual — they carry no extendedProps so clicking them is
     *  a no-op (handleEventClick ignores events with neither block nor appt). */
    const prepEvents: EventInput[] = appointments
        .filter((a) => !HIDDEN_FROM_CALENDAR.includes(a.status))
        .filter((a) => !hiddenTeamMembers.has(String(a.teamMemberId)))
        .filter((a) => !hiddenServices.has(String(a.serviceId)))
        .flatMap((a) => {
            const svc = serviceById(a.serviceId);
            if (!svc) return [];
            const before = svc.startPreparationDuration ?? 0;
            const after = svc.endAfterDuration ?? 0;
            const out: EventInput[] = [];
            const common = {
                backgroundColor: "#eef0f3",
                borderColor: "#e0e3e8",
                textColor: "#9aa1ac",
                editable: false,
                classNames: ["apt-prep-event"],
                display: "block" as const,
            };
            if (before > 0) {
                const start = new Date(new Date(a.startTime).getTime() - before * 60000);
                out.push({
                    ...common,
                    id: `prep-before-${a.id}`,
                    title: t('calendar.prep.preparation'),
                    start: start.toISOString(),
                    end: a.startTime,
                });
            }
            if (after > 0) {
                const end = new Date(new Date(a.endTime).getTime() + after * 60000);
                out.push({
                    ...common,
                    id: `prep-after-${a.id}`,
                    title: t('calendar.prep.cleanup'),
                    start: a.endTime,
                    end: end.toISOString(),
                });
            }
            return out;
        });

    // Prep blocks first so the real appointment renders on top of any overlap.
    // Hold off until the team/service bundle is loaded so appointments paint in
    // their final team colors on the first frame (no status-color → team-color
    // flash). Time-blocks are team-independent (gray), so they can show right away.
    /** Group events (workshops) as FC events — distinct purple with a group icon. */
    const eventFcEvents: EventInput[] = groupEvents
        .filter((ev) => ev.status !== "CANCELLED")
        .map((ev) => ({
            id: `event-${ev.id}`,
            title: `\u{1F465} ${ev.title}`,
            start: ev.startTime,
            end: ev.endTime,
            backgroundColor: "#7266e8",
            borderColor: "#5b4fd6",
            textColor: "#ffffff",
            classNames: ["group-event-event"],
            extendedProps: { groupEvent: ev },
        }));

    const events: EventInput[] = coreLoaded
        ? [...prepEvents, ...apptEvents, ...blockEvents, ...eventFcEvents]
        : blockEvents;

    const handleEventClick = async (info: EventClickArg) => {
        const gev = info.event.extendedProps.groupEvent as GroupEvent | undefined;
        if (gev) {
            setEventDrawerId(gev.id);
            setEventDrawerStart(null);
            setEventDrawerOpen(true);
            return;
        }
        const blk = info.event.extendedProps.block as TimeBlock | undefined;
        if (blk) {
            openBlockEdit(blk);
            return;
        }
        const a = info.event.extendedProps.appointment as Appointment | undefined;
        if (!a) return;
        // Open immediately with the cached event, then refresh from the server so
        // any outside change (e.g. an accepted reschedule) is reflected right away.
        openEdit(a);
        try {
            const token = await getAccessTokenSilently();
            const fresh = await getAppointmentById(a.id, token);
            if (fresh) openEdit(fresh);
        } catch {
            /* keep the cached view if the refresh fails */
        }
    };

    const handleDateClick = (info: DateClickArg) => {
        if (!canEditCalendar) return; // view-only: clicking a slot does nothing
        openCreate(toLocalIso(info.date));
    };

    return (
        <div className="calendar-wrapper" data-cy="calendar-page">

            <div className="calendar-container">
                <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    {canEditCalendar ? (
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-primary add-event-btn"
                                type="button"
                                data-cy="calendar-add-appt-btn"
                                onClick={() => openCreate(toLocalIso(new Date()))}
                            >
                                {t('calendar.toolbar.addAppointment')}
                            </button>
                            {eventTypes.length > 0 && (
                                <button
                                    className="btn btn-primary add-event-btn"
                                    type="button"
                                    data-cy="calendar-add-event-btn"
                                    onClick={() => {
                                        setEventDrawerId(null);
                                        setEventDrawerStart(toLocalIso(new Date()));
                                        setEventDrawerOpen(true);
                                    }}
                                >
                                    {t('calendar.toolbar.addEvent')}
                                </button>
                            )}
                        </div>
                    ) : (
                        <span />
                    )}

                    <div className="d-flex align-items-center gap-2">
                        {scopedServices.length > 0 && (
                            <div className="cal-team-picker cal-svc-picker" ref={svcPickerRef} data-cy="calendar-service-filter">
                                <button
                                    type="button"
                                    className="cal-team-picker__trigger"
                                    data-cy="calendar-service-picker-btn"
                                    onClick={() => setSvcPickerOpen((v) => !v)}
                                    aria-expanded={svcPickerOpen}
                                >
                                    <FontAwesomeIcon icon={faTag} className="cal-team-picker__icon" />
                                    <span className="cal-team-picker__label">{servicePickerLabel}</span>
                                    <span className="cal-team-picker__chev">▾</span>
                                </button>
                                {svcPickerOpen && (
                                    <div className="cal-team-picker__menu" role="menu">
                                        {/* Quick toggle row — All / None (mirrors the team picker). */}
                                        <div className="cal-team-picker__row cal-team-picker__row--quick">
                                            <button
                                                type="button"
                                                className="cal-team-picker__quick"
                                                onClick={() => setAllServicesVisible(true)}
                                            >
                                                {t('common.all')}
                                            </button>
                                            <button
                                                type="button"
                                                className="cal-team-picker__quick"
                                                onClick={() => setAllServicesVisible(false)}
                                            >
                                                {t('calendar.toolbar.none')}
                                            </button>
                                        </div>
                                        <div className="cal-team-picker__search">
                                            <FontAwesomeIcon icon={faSearch} />
                                            <input
                                                type="text"
                                                placeholder={t('common.search') + '…'}
                                                value={svcPickerSearch}
                                                onChange={(e) => setSvcPickerSearch(e.target.value)}
                                            />
                                        </div>
                                        <div className="cal-team-picker__section-title">{t('services.servicesTitle')}</div>
                                        <div className="cal-team-picker__list">
                                            {sortedServicesForFilter
                                                .filter((s) => {
                                                    const q = svcPickerSearch.trim().toLowerCase();
                                                    if (!q) return true;
                                                    return (s.name ?? '').toLowerCase().includes(q);
                                                })
                                                .map((s) => {
                                                    const id = String(s.id);
                                                    const checked = !hiddenServices.has(id);
                                                    return (
                                                        <label
                                                            key={s.id}
                                                            className={`cal-team-picker__item ${checked ? 'is-checked' : ''}`}
                                                        >
                                                            <span
                                                                className="cal-svc-picker__dot"
                                                                style={{ background: s.color ?? 'var(--gs-primary, #7266e8)' }}
                                                            />
                                                            <span className="cal-team-picker__name">{s.name}</span>
                                                            <span className="cal-team-picker__check">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={checked}
                                                                    onChange={() => toggleServiceVisibility(id)}
                                                                />
                                                                {checked && <FontAwesomeIcon icon={faCheck} />}
                                                            </span>
                                                        </label>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {teamMembers.length > 0 && (
                            <div className="cal-team-picker" ref={teamPickerRef}>
                                <button
                                    type="button"
                                    className="cal-team-picker__trigger"
                                    data-cy="calendar-team-picker-btn"
                                    onClick={() => setTeamPickerOpen((v) => !v)}
                                    aria-expanded={teamPickerOpen}
                                >
                                    <FontAwesomeIcon icon={faUsers} className="cal-team-picker__icon" />
                                    <span className="cal-team-picker__label">{teamPickerLabel}</span>
                                    <span className="cal-team-picker__chev">▾</span>
                                </button>
                                {teamPickerOpen && (
                                    <div className="cal-team-picker__menu" role="menu">
                                        {/* Quick toggle row — All / None. */}
                                        <div className="cal-team-picker__row cal-team-picker__row--quick">
                                            <button
                                                type="button"
                                                className="cal-team-picker__quick"
                                                onClick={() => setAllTeamVisible(true)}
                                            >
                                                {t('common.all')}
                                            </button>
                                            <button
                                                type="button"
                                                className="cal-team-picker__quick"
                                                onClick={() => setAllTeamVisible(false)}
                                            >
                                                {t('calendar.toolbar.none')}
                                            </button>
                                        </div>
                                        <div className="cal-team-picker__search">
                                            <FontAwesomeIcon icon={faSearch} />
                                            <input
                                                type="text"
                                                placeholder={t('common.search') + '…'}
                                                value={teamPickerSearch}
                                                onChange={(e) => setTeamPickerSearch(e.target.value)}
                                            />
                                        </div>
                                        <div className="cal-team-picker__section-title">{t('calendar.toolbar.team')}</div>
                                        <div className="cal-team-picker__list">
                                            {scopedTeamMembers
                                                .filter((tm) => {
                                                    const q = teamPickerSearch.trim().toLowerCase();
                                                    if (!q) return true;
                                                    const name = `${tm.firstName ?? ''} ${tm.lastName ?? ''}`.toLowerCase();
                                                    return name.includes(q);
                                                })
                                                .map((tm, idx) => {
                                                    const id = String(tm.id);
                                                    const checked = !hiddenTeamMembers.has(id);
                                                    const color = resolveTeamMemberColor(tm, idx);
                                                    return (
                                                        <label
                                                            key={tm.id}
                                                            className={`cal-team-picker__item ${checked ? 'is-checked' : ''}`}
                                                        >
                                                            <span
                                                                className="cal-team-picker__avatar"
                                                                style={{ background: color, color: readableTextColor(color) }}
                                                            >
                                                                {(tm.firstName ?? '').charAt(0)}
                                                                {(tm.lastName ?? '').charAt(0)}
                                                            </span>
                                                            <span className="cal-team-picker__name">
                                                                {tm.firstName} {tm.lastName}
                                                            </span>
                                                            <span className="cal-team-picker__check">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={checked}
                                                                    onChange={() => toggleTeamMemberVisibility(id)}
                                                                />
                                                                {checked && <FontAwesomeIcon icon={faCheck} />}
                                                            </span>
                                                        </label>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="cal-slot-settings" ref={slotSettingsRef}>
                            <button
                                type="button"
                                className="cal-slot-settings__trigger"
                                data-cy="calendar-settings-btn"
                                onClick={() => setSlotSettingsOpen((v) => !v)}
                                title="Calendar settings"
                                aria-label="Calendar settings"
                            >
                                <FontAwesomeIcon icon={faCog} />
                            </button>
                            {slotSettingsOpen && (
                                <div className="cal-slot-settings__menu">
                                    <div className="cal-slot-settings__title">{t('calendar.toolbar.visibleHours')}</div>
                                    {hasAnyBusinessHours ? (
                                        <div className="cal-slot-settings__days">
                                            {businessHoursByDay.map((d) => (
                                                <div className="cal-slot-settings__day" key={d.day}>
                                                    <span className="cal-slot-settings__day-name">{t(`settings.calendar.days.${d.day}`)}</span>
                                                    <span className={`cal-slot-settings__day-hours${d.open ? '' : ' cal-slot-settings__day-hours--closed'}`}>
                                                        {d.open && d.close
                                                            ? `${formatHHmm(d.open, timeFormat)}–${formatHHmm(d.close, timeFormat)}`
                                                            : t('calendar.toolbar.dayClosed')}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="cal-slot-settings__biz cal-slot-settings__biz--muted">
                                            {t('calendar.toolbar.noBusinessHours')}
                                        </div>
                                    )}
                                    <div className="cal-slot-settings__hint">
                                        {t('calendar.toolbar.visibleHoursHint')}
                                    </div>
                                    <div className="cal-slot-settings__row">
                                        <label>
                                            {t('calendar.toolbar.slotStart')}
                                            {/* Empty = use business-hours default. Same searchable
                                                dropdown as Settings → Calendar / Team working hours. */}
                                            <TimeSelect
                                                allowEmpty
                                                value={customSlotMin ? customSlotMin.slice(0, 5) : ''}
                                                onChange={(v) => applySlotMin(v)}
                                            />
                                        </label>
                                        <label>
                                            {t('calendar.toolbar.slotEnd')}
                                            <TimeSelect
                                                allowEmpty
                                                value={customSlotMax ? customSlotMax.slice(0, 5) : ''}
                                                onChange={(v) => applySlotMax(v)}
                                            />
                                        </label>
                                    </div>
                                    <div className="cal-slot-settings__footer">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-link"
                                            onClick={() => { applySlotMin(''); applySlotMax(''); }}
                                            disabled={!customSlotMin && !customSlotMax}
                                        >
                                            {t('calendar.toolbar.resetBusinessHours')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Until the core data (hours/slot bounds, colors) is in, keep the
                    grid invisible under a spinner: FullCalendar's first paint +
                    the slot-bounds rebuild all happen off-screen, so the user
                    sees ONE finished grid instead of 2-3 "blinks". */}
                <div
                    className={`calendar-scroll-container${coreLoaded ? '' : ' is-booting'}`}
                    data-cy="calendar-grid"
                    ref={scrollContainerRef}
                >
                    {!coreLoaded && (
                        <div className="cal-boot-overlay" data-cy="calendar-booting">
                            <div className="spinner-border text-primary" role="status" />
                        </div>
                    )}
                    <FullCalendar
                        /* Remount when the time format flips so FullCalendar re-applies
                           slotLabelFormat/eventTimeFormat (it doesn't update them on a
                           plain prop change). */
                        key={`fc-${timeFormat}`}
                        ref={calendarRef}
                        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                        initialView="timeGridWeek"
                        locale={i18n.language === 'pt' ? ptBrLocale : 'en'}
                        headerToolbar={{
                            // "Bloquear" (create a time block) is a write action —
                            // only offer it to members who can edit the calendar.
                            left: canEditCalendar ? "prev,next today blockBtn" : "prev,next today",
                            center: "title",
                            right: "dayGridMonth,timeGridWeek,timeGridDay,listMonth",
                        }}
                        customButtons={canEditCalendar ? {
                            blockBtn: {
                                text: t('calendar.block.blockBtn'),
                                click: () => openBlockCreate(),
                            },
                        } : {}}
                        events={events}
                        businessHours={businessHoursForCalendar}
                        slotMinTime={slotBounds.min}
                        slotMaxTime={slotBounds.max}
                        nowIndicator={true}
                        /* Axis + event times follow the business time-format setting
                           (24h "13:00" or 12h "1:00 pm"). */
                        slotLabelFormat={fcTimeFormat(timeFormat) as any}
                        eventTimeFormat={fcTimeFormat(timeFormat) as any}
                        eventClick={handleEventClick}
                        dateClick={handleDateClick}
                        datesSet={handleDatesSet}
                        allDaySlot={false}
                        contentHeight="auto"
                        editable={false}
                    />
                </div>
            </div>

            <Drawer
                open={showSidebar && !!draft}
                onClose={closeSidebar}
                title={draft?.id ? t('calendar.drawer.editTitle', { id: draft.id }) : t('calendar.drawer.newTitle')}
                size="md"
                footer={
                    draft && (
                        <>
                            {/* Save/Cancel only apply to the Details tab. On the
                             *  Payment tab the user uses the "Record payment"
                             *  button in the body — Save here would be confusing
                             *  ("did my split save?" — no, only the appointment). */}
                            {activeApptTab === 'details' && (
                                canEditCalendar ? (
                                    <>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            data-cy="appt-save-btn"
                                            onClick={handleSave}
                                            disabled={saving || allowedServicesForDraft.length === 0}
                                        >
                                            {saving ? t('calendar.drawer.saving') : t('common.save')}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            data-cy="appt-cancel-btn"
                                            onClick={closeSidebar}
                                            disabled={saving}
                                        >
                                            {t('common.cancel')}
                                        </button>
                                    </>
                                ) : (
                                    // View-only: no Save, just close the read-only drawer.
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={closeSidebar}
                                    >
                                        {t('common.close')}
                                    </button>
                                )
                            )}
                            {(activeApptTab === 'payment' || activeApptTab === 'history') && (
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={closeSidebar}
                                    disabled={saving || recordingManual}
                                >
                                    {t('common.close')}
                                </button>
                            )}
                            {draft.id && canEditCalendar && (
                                <button
                                    type="button"
                                    className="btn btn-outline-danger ms-auto"
                                    data-cy="appt-delete-btn"
                                    onClick={handleDelete}
                                    disabled={saving || recordingManual}
                                >
                                    {t('common.delete')}
                                </button>
                            )}
                        </>
                    )
                }
            >
                {draft && (
                    <div data-cy="appt-drawer">
                        {/* Tab strip — only meaningful for existing appointments
                            (you can't pay before you save). */}
                        {draft.id && (
                            <div className="gs-tab-strip">
                                <button
                                    type="button"
                                    data-cy="appt-tab-details"
                                    className={`gs-tab ${activeApptTab === 'details' ? 'gs-tab--active' : ''}`}
                                    onClick={() => setActiveApptTab('details')}
                                >
                                    {t('calendar.drawer.tabs.details')}
                                </button>
                                <button
                                    type="button"
                                    data-cy="appt-tab-payment"
                                    className={`gs-tab ${activeApptTab === 'payment' ? 'gs-tab--active' : ''}`}
                                    onClick={() => setActiveApptTab('payment')}
                                >
                                    {t('calendar.drawer.tabs.payment')}
                                    {/* Tab badge — reads the persisted appointment
                                        payment status straight from the backend. */}
                                    {(() => {
                                        const meta = APPOINTMENT_PAYMENT_LABEL[paidSummary.state];
                                        return (
                                            <span className={`gs-tab-badge gs-tab-badge--${meta.tone}`}>
                                                {paidSummary.state === 'PAID' && '✓ '}
                                                {t('appointments.paymentStatus.' + paidSummary.state, { defaultValue: meta.label })}
                                            </span>
                                        );
                                    })()}
                                </button>
                                <button
                                    type="button"
                                    data-cy="appt-tab-history"
                                    className={`gs-tab ${activeApptTab === 'history' ? 'gs-tab--active' : ''}`}
                                    onClick={() => setActiveApptTab('history')}
                                >
                                    {t('calendar.drawer.tabs.history')}
                                </button>
                            </div>
                        )}

                        {/* ========== HISTORY TAB ========== */}
                        {draft.id && activeApptTab === 'history' && (
                            <div className="appt-history">
                                {drawerHistory.length === 0 ? (
                                    <div className="text-muted small" style={{ padding: '0.5rem 0' }}>
                                        {t('calendar.history.empty')}
                                    </div>
                                ) : (
                                    <ul className="appt-history__items">
                                        {drawerHistory.map((h) => (
                                            <li key={h.id}>
                                                <div className="appt-history__event">{histEventLabel(h.eventType)}</div>
                                                {h.payload && (
                                                    <div className="appt-history__payload">
                                                        {histPayloadLabel(h.eventType, h.payload)}
                                                    </div>
                                                )}
                                                <div className="appt-history__meta">
                                                    {new Date(h.changedOn).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                                                    {h.changedBy && <> · {h.changedBy}</>}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}

                        {/* ========== PAYMENT TAB ========== */}
                        {draft.id && activeApptTab === 'payment' && (
                            <div className="appt-payment">
                                {priceBreakdown && priceBreakdown.isConvenio ? (
                                    <>
                                        {/* ---- Convênio payment panel ---- */}
                                        <div className="gs-price-breakdown">
                                            <div className="gs-price-breakdown__row">
                                                <span>
                                                    {t('calendar.convenio.plan')}
                                                </span>
                                                <span>{priceBreakdown.planName || '—'}</span>
                                            </div>
                                            <div className="gs-price-breakdown__row">
                                                <span>{t('calendar.convenio.card')}</span>
                                                <span>
                                                    {convenioCard?.cardNumber
                                                        ? convenioCard.cardNumber
                                                              + (convenioCard.holderType === 'DEPENDENTE'
                                                                  ? ` · ${t('calendar.convenio.dependent')}`
                                                                  : '')
                                                        : (
                                                            <small className="text-danger">
                                                                {draft.clientId
                                                                    ? t('calendar.convenio.cardMissing')
                                                                    : t('calendar.convenio.cardNoClient')}
                                                            </small>
                                                        )}
                                                </span>
                                            </div>
                                            <div className="gs-price-breakdown__row">
                                                <span>{t('calendar.convenio.tableValue')}</span>
                                                <span>
                                                    {priceBreakdown.currency}{priceBreakdown.tableValue.toFixed(2)}
                                                </span>
                                            </div>
                                            <div className="gs-price-breakdown__row">
                                                <span>{t('calendar.convenio.repasse')}</span>
                                                <span>
                                                    {priceBreakdown.currency}{priceBreakdown.base.toFixed(2)}
                                                </span>
                                            </div>
                                            {priceBreakdown.copay > 0 && (
                                                <div className="gs-price-breakdown__row">
                                                    <span>{t('calendar.convenio.copay')}</span>
                                                    <span>
                                                        {priceBreakdown.currency}{priceBreakdown.copay.toFixed(2)}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="gs-price-breakdown__row gs-price-breakdown__row--total">
                                                <span>{t('calendar.convenio.businessGets')}</span>
                                                <span>
                                                    {priceBreakdown.currency}
                                                    {(priceBreakdown.base + priceBreakdown.copay).toFixed(2)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Recorded transactions for this appointment. */}
                                        {apptTransactions.filter((tx) => !tx.deleted).length > 0 && (
                                            <div className="appt-payment__txns" style={{ marginTop: '0.75rem' }}>
                                                {apptTransactions.filter((tx) => !tx.deleted).map((txn) => (
                                                    <div
                                                        key={txn.id}
                                                        className="gs-price-breakdown__row"
                                                        style={{ fontSize: '0.85rem' }}
                                                    >
                                                        <span>
                                                            {t(`payments.method.${txn.method}`)}
                                                            {txn.giftCardCode ? ` (🎁 ${txn.giftCardCode})` : ''}
                                                            {' · '}
                                                            <small className="text-muted">
                                                                {t(`payments.status.${txn.status}` as any, txn.status)}
                                                            </small>
                                                        </span>
                                                        <span>
                                                            {priceBreakdown.currency}{(txn.amount ?? 0).toFixed(2)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Receivable (plan owes the table value). */}
                                        <div className="appt-payment__split" style={{ marginTop: '0.85rem' }}>
                                            {hasConvenioReceivable ? (
                                                <div className="gs-inline-note" style={{ color: 'var(--gs-text-muted)', fontSize: '0.85rem' }}>
                                                    ✓ {t('calendar.convenio.receivableDone')}
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="btn btn-primary w-100"
                                                    data-cy="convenio-record-receivable-btn"
                                                    onClick={recordConvenioReceivable}
                                                    disabled={convenioBusy || !(priceBreakdown.base > 0) || !canEditCalendar}
                                                >
                                                    {convenioBusy
                                                        ? t('common.working')
                                                        : t('calendar.convenio.recordReceivable', {
                                                            amount: `${priceBreakdown.currency}${priceBreakdown.base.toFixed(2)}`,
                                                        })}
                                                </button>
                                            )}

                                            {/* Copay (client pays now). */}
                                            {priceBreakdown.copay > 0 && (
                                                <div style={{ marginTop: '0.6rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                                    <select
                                                        className="form-select"
                                                        style={{ maxWidth: 160 }}
                                                        value={copayMethod}
                                                        onChange={(e) => setCopayMethod(e.target.value as PaymentMethod)}
                                                        data-cy="convenio-copay-method"
                                                    >
                                                        {(['CASH', 'PIX', 'ETRANSFER', 'BANK_TRANSFER', 'GIFT_CARD', 'OTHER'] as PaymentMethod[]).map((m) => (
                                                            <option key={m} value={m}>{t(`payments.method.${m}`)}</option>
                                                        ))}
                                                    </select>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary"
                                                        data-cy="convenio-record-copay-btn"
                                                        onClick={recordConvenioCopay}
                                                        disabled={convenioBusy || !canEditCalendar}
                                                    >
                                                        {t('calendar.convenio.recordCopay', {
                                                            amount: `${priceBreakdown.currency}${priceBreakdown.copay.toFixed(2)}`,
                                                        })}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ) : priceBreakdown ? (
                                    <>
                                        <div className="gs-price-breakdown">
                                            <div className="gs-price-breakdown__row">
                                                <span>{t('calendar.payment.servicePrice')}</span>
                                                <span>
                                                    {priceBreakdown.currency}
                                                    {priceBreakdown.base.toFixed(2)}
                                                </span>
                                            </div>
                                            {priceBreakdown.pct > 0 && (
                                                <div className="gs-price-breakdown__row gs-price-breakdown__row--discount">
                                                    <span>
                                                        {t('calendar.payment.clientDiscount')}
                                                        <small>
                                                            {' '}({priceBreakdown.pct}% — {linkedClient?.firstName}
                                                            {linkedClient?.lastName ? ` ${linkedClient.lastName}` : ''})
                                                        </small>
                                                    </span>
                                                    <span>
                                                        −{priceBreakdown.currency}
                                                        {priceBreakdown.clientDiscount.toFixed(2)}
                                                    </span>
                                                </div>
                                            )}
                                            {priceBreakdown.extraDiscount > 0 && (
                                                <div className="gs-price-breakdown__row gs-price-breakdown__row--discount">
                                                    <span>
                                                        {t('calendar.payment.extraDiscount')}
                                                        <small> ({t('calendar.payment.appliedNow')})</small>
                                                    </span>
                                                    <span>
                                                        −{priceBreakdown.currency}
                                                        {priceBreakdown.extraDiscount.toFixed(2)}
                                                    </span>
                                                </div>
                                            )}
                                            {paidSummary.collected > 0 && (
                                                <div className="gs-price-breakdown__row gs-price-breakdown__row--discount">
                                                    <span>{t('calendar.payment.alreadyPaid')}</span>
                                                    <span>
                                                        −{priceBreakdown.currency}
                                                        {paidSummary.collected.toFixed(2)}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="gs-price-breakdown__row gs-price-breakdown__row--total">
                                                <span>{t('calendar.payment.totalDue')}</span>
                                                <span>
                                                    {priceBreakdown.currency}
                                                    {paidSummary.remaining.toFixed(2)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Estimated profit — read-only, informational. */}
                                        {profitEstimate && (
                                            <div className="gs-profit-estimate" data-cy="appt-profit-estimate">
                                                <div className="gs-profit-estimate__row">
                                                    <span>{t('calendar.payment.estimatedProfit')}</span>
                                                    <strong>{profitEstimate.currency}{profitEstimate.profit.toFixed(2)}</strong>
                                                </div>
                                                <div className="gs-profit-estimate__sub">
                                                    {t('calendar.payment.estimatedProfitSub', {
                                                        commission: `${profitEstimate.currency}${profitEstimate.commission.toFixed(2)}`,
                                                        commissionRate: profitEstimate.commissionRate,
                                                        tax: `${profitEstimate.currency}${profitEstimate.tax.toFixed(2)}`,
                                                        taxRate: profitEstimate.taxRate,
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Inline extra discount editor — only for users
                                            whose DISCOUNT tier allows a discount > 0%.
                                            Members with "Nenhum" (max rate 0) can't apply
                                            any discount, so we hide the editor entirely.
                                            Backend (payment-service) re-validates the rate. */}
                                        {(maxDiscountRate ?? 0) > 0 && paidSummary.state !== 'PAID' && (
                                            <div className="appt-payment__extra-discount">
                                                <span className="appt-payment__extra-discount__label">
                                                    {t('calendar.payment.applyExtraDiscount')}
                                                </span>
                                                <div className="appt-payment__extra-discount__row">
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        step={0.01}
                                                        className="form-control"
                                                        placeholder="0"
                                                        value={extraDiscount.value}
                                                        onChange={(e) =>
                                                            setExtraDiscount({
                                                                ...extraDiscount,
                                                                value: e.target.value,
                                                            })
                                                        }
                                                    />
                                                    <div
                                                        className="appt-payment__extra-discount__toggle"
                                                        role="group"
                                                        aria-label="Discount kind"
                                                    >
                                                        <button
                                                            type="button"
                                                            className={`gs-pill-toggle ${
                                                                extraDiscount.kind === 'AMOUNT'
                                                                    ? 'is-active'
                                                                    : ''
                                                            }`}
                                                            onClick={() =>
                                                                setExtraDiscount({
                                                                    ...extraDiscount,
                                                                    kind: 'AMOUNT',
                                                                })
                                                            }
                                                        >
                                                            {priceBreakdown.currency}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`gs-pill-toggle ${
                                                                extraDiscount.kind === 'PERCENT'
                                                                    ? 'is-active'
                                                                    : ''
                                                            }`}
                                                            onClick={() =>
                                                                setExtraDiscount({
                                                                    ...extraDiscount,
                                                                    kind: 'PERCENT',
                                                                })
                                                            }
                                                        >
                                                            %
                                                        </button>
                                                    </div>
                                                    {priceBreakdown.extraDiscount > 0 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-link btn-sm"
                                                            onClick={() =>
                                                                setExtraDiscount({
                                                                    kind: 'AMOUNT',
                                                                    value: '',
                                                                })
                                                            }
                                                        >
                                                            {t('common.clear')}
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary btn-sm"
                                                        onClick={handleSaveExtraDiscount}
                                                        disabled={saving}
                                                        title={t('calendar.payment.persistDiscountTitle')}
                                                    >
                                                        {saving ? t('calendar.drawer.saving') : t('calendar.payment.apply')}
                                                    </button>
                                                </div>
                                                <small className="appt-payment__extra-discount__hint">
                                                    {t('calendar.payment.extraDiscountHint')}
                                                    {priceBreakdown.maxDiscountRate != null && (
                                                        <> {t('calendar.payment.discountLimit', { rate: priceBreakdown.maxDiscountRate })}</>
                                                    )}
                                                </small>
                                                {priceBreakdown.discountCapped && (
                                                    <small className="appt-payment__extra-discount__hint text-danger">
                                                        {t('calendar.payment.discountCapped', { rate: priceBreakdown.maxDiscountRate })}
                                                    </small>
                                                )}
                                            </div>
                                        )}

                                        {/* Tier 0 ("Nenhum", or no discount rates configured for the
                                            business): the editor is hidden — say why instead of showing
                                            nothing. Same rule + hint in the Pro app. */}
                                        {maxDiscountRate != null && maxDiscountRate <= 0 && paidSummary.state !== 'PAID' && (
                                            <div className="appt-payment__extra-discount">
                                                <small className="appt-payment__extra-discount__hint">
                                                    {t('calendar.payment.noDiscountTier')}
                                                </small>
                                            </div>
                                        )}

                                        {/* Existing payments on this appointment — only render when
                                            there's something to show, otherwise the empty editor
                                            speaks for itself. */}
                                        {apptTransactions.length > 0 && (() => {
                                            const s = paidSummary.state;
                                            const tone = APPOINTMENT_PAYMENT_LABEL[s].tone;
                                            const headline: Record<typeof s, [string, string]> = {
                                                PAID:               ['✓', t('calendar.payment.paidInFull')],
                                                PARTIAL_PAID:       ['●', t('calendar.payment.partiallyPaid')],
                                                PARTIALLY_REFUNDED: ['●', t('calendar.payment.partiallyRefunded')],
                                                PENDING:            ['…', t('calendar.payment.paymentPending')],
                                                CONVENIO_PENDING:   ['⏳', t('appointments.paymentStatus.CONVENIO_PENDING')],
                                                REFUNDED:           ['↩', t('calendar.payment.refunded')],
                                                CANCELED:           ['—', t('calendar.payment.paymentCanceled')],
                                                NOT_PAID:           ['', ''], // unreachable
                                            };
                                            const [icon, title] = headline[s];
                                            return (
                                            <div
                                                className={`appt-payment__paid appt-payment__paid--${tone}`}
                                            >
                                                <div className="appt-payment__paid-header">
                                                    <span className="appt-payment__paid-icon">{icon}</span>
                                                    <strong>{title}</strong>
                                                </div>
                                                <ul className="appt-payment__paid-list">
                                                    {apptTransactions.map((txn) => (
                                                        <li key={txn.id}>
                                                            <span>
                                                                {t(`payments.method.${txn.method}`)}
                                                                {txn.giftCardCode ? ` (🎁 ${txn.giftCardCode})` : ''}
                                                                {txn.status === 'PENDING' && (
                                                                    <em> ({t('calendar.payment.pending')})</em>
                                                                )}
                                                                {txn.refundedAmount > 0 && (
                                                                    <em>
                                                                        {' '}
                                                                        (−{priceBreakdown.currency}
                                                                        {txn.refundedAmount.toFixed(2)} {t('calendar.payment.refundedSuffix')})
                                                                    </em>
                                                                )}
                                                            </span>
                                                            <span>
                                                                {priceBreakdown.currency}
                                                                {txn.amount.toFixed(2)}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                                <div className="appt-payment__paid-totals">
                                                    <div>
                                                        <span>{t('calendar.payment.collected')}</span>
                                                        <strong>
                                                            {priceBreakdown.currency}
                                                            {paidSummary.collected.toFixed(2)}
                                                        </strong>
                                                    </div>
                                                    {paidSummary.remaining > 0.01 && (
                                                        <div>
                                                            <span>{t('calendar.payment.remaining')}</span>
                                                            <strong>
                                                                {priceBreakdown.currency}
                                                                {paidSummary.remaining.toFixed(2)}
                                                            </strong>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            );
                                        })()}

                                        {/* Pre-authorization hold — attendant settles at the end. */}
                                        {canCheckout && draft.holdPaymentIntentId && !holdResolved && paidSummary.remaining > 0.01 && (
                                            <div className="appt-hold-panel">
                                                <div className="appt-hold-panel__title">
                                                    💳 {t('calendar.payment.hold.title', { currency: priceBreakdown.currency, amount: (draft.holdAmount ?? 0).toFixed(2) })}
                                                </div>
                                                <p className="appt-hold-panel__hint">
                                                    {t('calendar.payment.hold.hint')}
                                                </p>
                                                <div className="appt-hold-panel__actions">
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary btn-sm"
                                                        disabled={holdBusy}
                                                        onClick={() => settleHold("process")}
                                                    >
                                                        {holdBusy ? t('calendar.payment.hold.processing') : t('calendar.payment.hold.process')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-sm"
                                                        disabled={holdBusy}
                                                        onClick={() => settleHold("noshow")}
                                                    >
                                                        {t('calendar.payment.hold.noshow')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-sm"
                                                        disabled={holdBusy}
                                                        onClick={() => settleHold("cancel")}
                                                    >
                                                        {t('calendar.payment.hold.cancel')}
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Unified payment editor — CARD (Stripe) + manual methods
                                            live in the same split. Each line takes a partial amount;
                                            sum must equal the total due. Hidden when the appointment
                                            is fully covered (nothing left to charge) OR when the
                                            caller lacks CHECKOUT (recording payments). Backend
                                            re-checks CHECKOUT on recordManualPayment. */}
                                        {canCheckout && paidSummary.remaining > 0.01 && (() => {
                                            const allMethods = parseAcceptedMethods(
                                                selectedBusiness?.acceptedPaymentMethods,
                                                selectedBusiness?.country
                                            );
                                            if (allMethods.length === 0) {
                                                return (
                                                    <div className="empty-hint" style={{ padding: '0.75rem 0' }}>
                                                        {t('calendar.payment.noMethodsHint')}
                                                    </div>
                                                );
                                            }
                                            // When the appointment is partially paid we only ask for
                                            // the outstanding portion — using paidSummary.remaining
                                            // (= total - already collected).
                                            const target = paidSummary.remaining > 0
                                                ? paidSummary.remaining
                                                : priceBreakdown.total;
                                            const remaining = target - manualSplitTotal;
                                            const remainingAbs = Math.abs(remaining);
                                            const matches = remainingAbs <= 0.01;
                                            const nonZeroLineCount = Object.values(manualSplit).filter(
                                                (v) =>
                                                    v &&
                                                    parseFloat((v as string).replace(',', '.')) > 0
                                            ).length;
                                            const hasCard =
                                                !!manualSplit['CARD_STRIPE'] &&
                                                parseFloat(
                                                    (manualSplit['CARD_STRIPE'] as string).replace(
                                                        ',',
                                                        '.'
                                                    )
                                                ) > 0;
                                            return (
                                                <div className="appt-payment__split">
                                                    <div className="appt-payment__split-header">
                                                        <strong>{t('calendar.payment.recordPayment')}</strong>
                                                        <span className="appt-payment__split-sub">
                                                            {t('calendar.payment.splitHint')}
                                                        </span>
                                                    </div>

                                                    <div className="appt-payment__split-rows">
                                                        {allMethods.map((m) => {
                                                            const isCard = m === 'CARD_STRIPE';
                                                            return (
                                                                <div key={m} className="appt-payment__split-row">
                                                                    <label className="appt-payment__split-label">
                                                                        {t(`payments.method.${m}`)}
                                                                        {isCard && (
                                                                            <span className="appt-payment__split-badge">
                                                                                online
                                                                            </span>
                                                                        )}
                                                                    </label>
                                                                    <div className="appt-payment__split-input">
                                                                        <span className="appt-payment__split-currency">
                                                                            {priceBreakdown.currency}
                                                                        </span>
                                                                        <input
                                                                            type="number"
                                                                            inputMode="decimal"
                                                                            step="0.01"
                                                                            min="0"
                                                                            className="form-control"
                                                                            placeholder="0.00"
                                                                            value={manualSplit[m] ?? ''}
                                                                            onChange={(e) =>
                                                                                setManualSplit((prev) => ({
                                                                                    ...prev,
                                                                                    [m]: e.target.value,
                                                                                }))
                                                                            }
                                                                            disabled={recordingManual}
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-link btn-sm appt-payment__split-fill"
                                                                            onClick={() =>
                                                                                setManualSplitTo(m, target)
                                                                            }
                                                                            disabled={recordingManual}
                                                                            title={t('calendar.payment.payAllTitle', { method: t(`payments.method.${m}`) })}
                                                                        >
                                                                            {t('calendar.payment.payAll')}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>

                                                    {/* Receiving data for the methods being charged — appears once an
                                                        amount is typed for PIX / bank transfer, copyable to the client. */}
                                                    {Number(manualSplit['PIX'] ?? 0) > 0 && (
                                                        <PayoutInfoHint title={t('settings.payment.pixKeyLabel')}
                                                            value={selectedBusiness?.pixKey} dataCy="calendar-pix-hint" />
                                                    )}
                                                    {Number(manualSplit['BANK_TRANSFER'] ?? 0) > 0 && (
                                                        <PayoutInfoHint title={t('settings.payment.bankInfoLabel')}
                                                            value={selectedBusiness?.bankTransferInfo} dataCy="calendar-bank-hint" />
                                                    )}
                                                    {Number(manualSplit['GIFT_CARD'] ?? 0) > 0 && (
                                                        <div className="appt-payment__giftcard" data-cy="calendar-giftcard-field">
                                                            <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                                                                {t('payments.giftCard.codeLabel')} <span className="gs-required">*</span>
                                                            </label>
                                                            <input type="text" className="form-control" maxLength={80}
                                                                value={giftCardCode}
                                                                onChange={(e) => setGiftCardCode(e.target.value)}
                                                                placeholder={t('payments.giftCard.codePlaceholder')}
                                                                data-cy="calendar-giftcard-code" />
                                                            <small className="form-text text-muted">{t('payments.giftCard.codeHint')}</small>
                                                        </div>
                                                    )}

                                                    <div className={`appt-payment__split-summary ${matches ? 'is-ok' : 'is-warn'}`}>
                                                        <div>
                                                            <span>{t('calendar.payment.entered')}</span>
                                                            <strong>
                                                                {priceBreakdown.currency}
                                                                {manualSplitTotal.toFixed(2)}
                                                            </strong>
                                                        </div>
                                                        <div>
                                                            <span>{remaining >= 0 ? t('calendar.payment.remaining') : t('calendar.payment.overBy')}</span>
                                                            <strong>
                                                                {priceBreakdown.currency}
                                                                {remainingAbs.toFixed(2)}
                                                            </strong>
                                                        </div>
                                                    </div>

                                                    <div className="appt-payment__split-actions">
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={() => setManualSplit({})}
                                                            disabled={recordingManual || manualSplitTotal === 0}
                                                        >
                                                            {t('common.clear')}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary"
                                                            onClick={handleRecordManualPayment}
                                                            /* Enabled as soon as the user enters anything
                                                               valid (sum > 0 and ≤ what's due). Partial
                                                               payments are allowed and leave the appointment
                                                               in Partial Paid status. */
                                                            disabled={
                                                                saving ||
                                                                recordingManual ||
                                                                manualSplitTotal <= 0 ||
                                                                manualSplitTotal > target + 0.01
                                                            }
                                                        >
                                                            {recordingManual
                                                                ? t('calendar.payment.recording')
                                                                : hasCard
                                                                ? nonZeroLineCount > 1
                                                                    ? '💳 ' + t('calendar.payment.recordChargeCard')
                                                                    : '💳 ' + t('calendar.payment.chargeCard')
                                                                : nonZeroLineCount > 1
                                                                ? '💵 ' + t('calendar.payment.recordSplit')
                                                                : '💵 ' + t('calendar.payment.record')}
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </>
                                ) : (
                                    <div className="empty-hint" style={{ padding: '1rem' }}>
                                        {t('calendar.payment.noServicePrice')}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ========== DETAILS TAB (default) ========== */}
                        {(activeApptTab === 'details' || !draft.id) && (
                    <form className="event-form" data-cy="appt-form" onSubmit={(e) => e.preventDefault()}>
                        {externalUpdate && (
                            <div className="alert alert-warning d-flex align-items-center justify-content-between gap-2 py-2 mb-3">
                                <span className="small">🔄 {t('calendar.form.externalUpdate')}</span>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-warning"
                                    onClick={() => {
                                        const fresh = externalUpdate;
                                        setExternalUpdate(null);
                                        if (fresh) openEdit(fresh);
                                    }}
                                >
                                    {t('calendar.form.reload')}
                                </button>
                            </div>
                        )}
                        <div className="form-group">
                            <label>{t('calendar.form.status')}</label>
                            <select
                                className="form-select"
                                data-cy="appt-status-select"
                                value={draft.status}
                                onChange={(e) =>
                                    handleField("status", e.target.value as AppointmentStatus)
                                }
                            >
                                {APPOINTMENT_STATUS_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {t('appointments.status.' + o.value, { defaultValue: o.label })}
                                    </option>
                                ))}
                            </select>

                            {/* Reschedule-proposal hints, just under the status. */}
                            {draft.rescheduleStatus === "PROPOSED" && draft.proposedStartTime && (
                                <div className="small text-muted mt-1">
                                    ⏳ {t('calendar.statusHints.rescheduleProposed', { time: formatProposedTime(draft.proposedStartTime) })}
                                </div>
                            )}
                            {draft.rescheduleStatus === "DECLINED" && draft.proposedStartTime && (
                                <div className="small text-danger mt-1">
                                    ⚠ {t('calendar.statusHints.rescheduleDeclined', { time: formatProposedTime(draft.proposedStartTime) })}
                                </div>
                            )}

                            {/* Reason — appears when cancelling OR refusing a single
                                appointment (not part of the series-decline flow below). */}
                            {(draft.status === "CANCELLED" || draft.status === "DECLINED") &&
                                !(draft.id && draft.seriesId) && (
                                <div className="mt-2">
                                    <label className="gs-cancel-reason__label">
                                        {draft.status === "DECLINED"
                                            ? t('calendar.statusHints.declineReasonLabel')
                                            : t('calendar.statusHints.cancelReasonLabel')}
                                    </label>
                                    <textarea
                                        className="form-control gs-cancel-reason"
                                        data-cy="appt-cancel-reason-input"
                                        rows={2}
                                        placeholder={
                                            draft.status === "DECLINED"
                                                ? t('calendar.statusHints.declineReasonPlaceholder')
                                                : t('calendar.statusHints.cancelReasonPlaceholder')
                                        }
                                        value={draft.cancellationReason ?? ""}
                                        onChange={(e) =>
                                            handleField("cancellationReason", e.target.value)
                                        }
                                    />
                                </div>
                            )}

                            {/* Recurring series — accept/decline the WHOLE series at once. */}
                            {draft.id && draft.seriesId && (
                                <div className="gs-series-banner mt-2">
                                    <div className="gs-series-banner__title">
                                        🔁 {t('calendar.series.partOf')}
                                        {draft.recurrenceFreq
                                            ? ` — ${t('calendar.freq.' + draft.recurrenceFreq)}`
                                            : ""}
                                    </div>
                                    {seriesSummary && (
                                        <dl className="gs-series-detail">
                                            <div>
                                                <dt>{t('calendar.series.start')}</dt>
                                                <dd style={{ textTransform: "capitalize" }}>
                                                    {seriesSummary.firstStart
                                                        ? formatProposedTime(seriesSummary.firstStart)
                                                        : "—"}
                                                </dd>
                                            </div>
                                            <div>
                                                <dt>{t('calendar.series.time')}</dt>
                                                <dd>{seriesSummary.time || "—"}</dd>
                                            </div>
                                            <div>
                                                <dt>{t('calendar.series.occurrences')}</dt>
                                                <dd>
                                                    {t('calendar.series.occurrenceCount', { count: seriesSummary.total })}
                                                    {seriesSummary.cancelled > 0
                                                        ? ` (${t('calendar.series.cancelledCount', { count: seriesSummary.cancelled })})`
                                                        : ""}
                                                </dd>
                                            </div>
                                            <div>
                                                <dt>{t('calendar.series.ends')}</dt>
                                                <dd>{formatDateOnly(seriesSummary.lastStart) || "—"}</dd>
                                            </div>
                                        </dl>
                                    )}
                                    {draft.status === "PENDING" && (
                                        <>
                                            <div className="small text-muted">
                                                {t('calendar.series.acceptOrDeclineHint')}
                                            </div>
                                            <label className="gs-cancel-reason__label mt-2">
                                                {t('calendar.statusHints.cancelReasonLabel')}
                                            </label>
                                            <textarea
                                                className="form-control gs-cancel-reason"
                                                data-cy="appt-series-cancel-reason-input"
                                                rows={2}
                                                placeholder={t('calendar.series.cancelReasonPlaceholder')}
                                                value={draft.cancellationReason ?? ""}
                                                onChange={(e) =>
                                                    handleField("cancellationReason", e.target.value)
                                                }
                                            />
                                            <div className="d-flex gap-2 mt-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-success btn-sm"
                                                    data-cy="appt-confirm-btn"
                                                    onClick={() => handleRespondSeries(true)}
                                                    disabled={respondingSeries}
                                                >
                                                    {respondingSeries ? "…" : t('calendar.series.accept')}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-danger btn-sm"
                                                    data-cy="appt-decline-btn"
                                                    onClick={() => handleRespondSeries(false)}
                                                    disabled={respondingSeries}
                                                >
                                                    {respondingSeries ? "…" : t('calendar.series.decline')}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {draft.externalLocation && draft.externalLocation.trim() && (
                            <div
                                className="alert mb-3"
                                style={{
                                    background: "#fdecea",
                                    border: "1px solid #f5c2c0",
                                    color: "#9a0007",
                                    fontWeight: 700,
                                    borderRadius: 8,
                                    padding: "0.6rem 0.8rem",
                                }}
                            >
                                📍 {t('calendar.externalLocation.label')}
                                <div style={{ fontWeight: 600, marginTop: 2 }}>
                                    {draft.externalLocation}
                                </div>
                            </div>
                        )}

                        {draft.cancellationFeeOutstanding && (
                            <div
                                className="alert mb-3"
                                style={{
                                    background: "#fff7e6",
                                    border: "1px solid #f0c878",
                                    color: "#8a5a00",
                                    fontWeight: 700,
                                    borderRadius: 8,
                                    padding: "0.6rem 0.8rem",
                                }}
                            >
                                ⚠️ {t('calendar.cancellationFee.title')}
                                <div style={{ fontWeight: 600, marginTop: 2 }}>
                                    {draft.cancellationFeeAmount != null && draft.cancellationFeeAmount > 0
                                        ? t('calendar.cancellationFee.withAmount', { amount: draft.cancellationFeeAmount.toFixed(2) })
                                        : t('calendar.cancellationFee.noAmount')}
                                </div>
                            </div>
                        )}

                        <div className="form-group position-relative">
                            {/* Star is OUTSIDE the <label>: a label activates its first
                                labelable descendant on any click, so a button inside it
                                toggled favorite when clicking anywhere on the line. */}
                            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                <label style={{ margin: 0 }}>
                                    {t('calendar.form.customerName')} <span className="gs-required">*</span>
                                </label>
                                {linkedClient && (
                                    <button
                                        type="button"
                                        onClick={toggleLinkedClientFavorite}
                                        title={linkedClient.favorite ? "Remover dos favoritos" : "Favoritar cliente"}
                                        style={{
                                            background: "none", border: "none", cursor: "pointer",
                                            fontSize: "1.05rem", lineHeight: 1, padding: 0,
                                            color: linkedClient.favorite ? "#f5b301" : "#c9c6d6",
                                        }}
                                    >
                                        {linkedClient.favorite ? "★" : "☆"}
                                    </button>
                                )}
                            </div>
                            <input
                                type="text"
                                className="form-control"
                                data-cy="appt-customer-input"
                                value={draft.customerName}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setDraft((prev) => {
                                        if (!prev) return prev;
                                        // Editing the name unlinks the client unless the
                                        // typed value still matches the linked client label.
                                        let nextClientId = prev.clientId;
                                        if (prev.clientId) {
                                            const linked = clients.find(
                                                (c) => c.id === prev.clientId
                                            );
                                            if (!linked || formatClientLabel(linked) !== v) {
                                                nextClientId = null;
                                            }
                                        }
                                        return {
                                            ...prev,
                                            customerName: v,
                                            clientId: nextClientId,
                                        };
                                    });
                                    setShowClientDropdown(true);
                                }}
                                onFocus={() => setShowClientDropdown(true)}
                                onBlur={() => setTimeout(() => setShowClientDropdown(false), 150)}
                                placeholder={t('calendar.form.customerPlaceholder')}
                                autoComplete="off"
                            />

                            {showClientDropdown && clientMatches.length > 0 && (
                                <div className="client-autocomplete">
                                    {clientMatches.map((c) => (
                                        <button
                                            key={c.id}
                                            type="button"
                                            className="client-autocomplete-item"
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                pickClient(c);
                                            }}
                                        >
                                            <div className="fw-semibold">
                                                {formatClientLabel(c)}
                                            </div>
                                            {(c.email || c.phone) && (
                                                <div className="small text-muted">
                                                    {c.email}
                                                    {c.email && c.phone ? " · " : ""}
                                                    {c.phone}
                                                </div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {draft.clientId && (
                                <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                                    <span className="badge bg-success-subtle text-success-emphasis">
                                        ✓ {t('calendar.form.linkedToClient')}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0"
                                        onClick={clearLinkedClient}
                                    >
                                        {t('calendar.form.unlink')}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 ms-auto"
                                        onClick={() => {
                                            if (!draft.clientId) return;
                                            setPendingClient(
                                                String(draft.clientId),
                                                'appointments'
                                            );
                                            closeSidebar();
                                            setActiveMenu(MENU.CLIENT);
                                        }}
                                        title={t('calendar.form.viewHistoryTitle')}
                                    >
                                        📋 {t('calendar.form.viewHistory')}
                                    </button>
                                    {/* Prontuário: this button hangs off the SERVICE's health
                                        classification, not off the business's healthMode, so it
                                        would still appear for a business that classified a
                                        category as health data. The module flag closes it. */}
                                    {HEALTH_RECORDS_ENABLED && draft.id && services.find((sv) => sv.id === draft.serviceId)?.isHealthDataResolved === true && (
                                        <button
                                            type="button"
                                            className="btn btn-link btn-sm p-0 ms-2"
                                            onClick={() => setClinicalApptId(String(draft.id))}
                                            title={t('clinical.openFromAppointmentTitle')}
                                            data-cy="appt-clinical-btn"
                                        >
                                            🩺 {t('clinical.tab')}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Phone is fixed-length, e-mail is long → give e-mail 2× the
                            width so it isn't truncated. */}
                        <div className="gs-form-grid gs-form-grid--1-2">
                            <div className="form-group">
                                <label>{t('calendar.form.phone')}</label>
                                <input
                                    type="tel"
                                    className="form-control"
                                    data-cy="appt-phone-input"
                                    value={draft.customerPhone}
                                    maxLength={16}
                                    onChange={(e) => {
                                        const country = resolvePhoneCountry(undefined, selectedBusiness?.currency);
                                        handleField("customerPhone", formatPhone(e.target.value, country));
                                    }}
                                />
                                {!isPhoneComplete(draft.customerPhone, resolvePhoneCountry(undefined, selectedBusiness?.currency)) &&
                                 draft.customerPhone !== '' && (
                                    <span className="form-text text-danger small">{t('common.invalidPhone')}</span>
                                )}
                                {phoneMismatch && linkedClient && (
                                    <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                                        <span className="text-danger small">
                                            ⚠ {t('calendar.form.phoneMismatch', { phone: linkedClient.phone })}
                                        </span>
                                        <button
                                            type="button"
                                            className="btn btn-link btn-sm p-0"
                                            onClick={handleUpdateClientPhone}
                                            disabled={updatingClientPhone}
                                            title={t('calendar.form.updatePhoneTitle', { name: formatClientLabel(linkedClient), phone: draft.customerPhone })}
                                        >
                                            {updatingClientPhone ? t('calendar.form.updatingPhone') : t('calendar.form.updatePhone')}
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="form-group">
                                <label>{t('calendar.form.email')}</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    data-cy="appt-email-input"
                                    value={draft.customerEmail}
                                    onChange={(e) => handleField("customerEmail", e.target.value)}
                                />
                            </div>
                        </div>

                        {!draft.clientId && draft.customerName.trim().length >= 2 && (
                            <div className="form-group">
                                <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={handleSaveAsNewClient}
                                    disabled={savingNewClient}
                                >
                                    {savingNewClient ? t('calendar.drawer.saving') : t('calendar.form.saveAsNewClient')}
                                </button>
                                <div className="form-text">
                                    {t('calendar.form.saveAsNewClientHint')}
                                </div>
                            </div>
                        )}

                        {hasMissingClientInfo && linkedClient && (
                            <div className="form-group">
                                <div className="alert alert-info py-2 small mb-0">
                                    <label
                                        htmlFor="updateLinkedClient"
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.5rem",
                                            cursor: "pointer",
                                            margin: 0,
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            id="updateLinkedClient"
                                            checked={updateLinkedClient}
                                            onChange={(e) =>
                                                setUpdateLinkedClient(e.target.checked)
                                            }
                                            style={{
                                                width: "16px",
                                                minWidth: "16px",
                                                height: "16px",
                                                margin: 0,
                                                appearance: "auto",
                                                WebkitAppearance: "checkbox",
                                                MozAppearance: "checkbox",
                                                accentColor: "#7266e8",
                                                flexShrink: 0,
                                            }}
                                        />
                                        <span>
                                            {t('calendar.form.alsoAddFields', {
                                                fields: [
                                                    missingClientFields.phone && t('calendar.form.fieldPhone'),
                                                    missingClientFields.email && t('calendar.form.fieldEmail'),
                                                ]
                                                    .filter(Boolean)
                                                    .join(` ${t('common.and')} `),
                                                name: formatClientLabel(linkedClient),
                                            })}
                                        </span>
                                    </label>
                                </div>
                            </div>
                        )}

                        <div className="form-group">
                            <label>{t('calendar.form.teamMember')} <span className="gs-required">*</span></label>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                }}
                            >
                                {(() => {
                                    const c = teamMemberColorById(draft.teamMemberId);
                                    return c ? (
                                        <span
                                            style={{
                                                width: 18,
                                                height: 18,
                                                minWidth: 18,
                                                borderRadius: "50%",
                                                background: c,
                                                border: "1px solid #ccc",
                                                flexShrink: 0,
                                            }}
                                            aria-hidden
                                        />
                                    ) : null;
                                })()}
                                <select
                                    className="form-select"
                                    data-cy="appt-teammember-select"
                                    value={draft.teamMemberId}
                                    onChange={(e) => handleTeamMemberChange(e.target.value)}
                                    style={{ flex: 1 }}
                                >
                                    {appointmentMemberOptions.length === 0 ? (
                                        <option value="">{t('calendar.form.noTeamMembers')}</option>
                                    ) : (
                                        <option value="">{t('calendar.form.selectTeamMember')}</option>
                                    )}
                                    {appointmentMemberOptions.map((t, idx) => {
                                        const c = resolveTeamMemberColor(t, idx);
                                        return (
                                            <option
                                                key={t.id}
                                                value={t.id}
                                                style={{
                                                    backgroundColor: c,
                                                    color: readableTextColor(c),
                                                }}
                                            >
                                                {t.firstName} {t.lastName}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>{t('calendar.form.service')} <span className="gs-required">*</span></label>
                            <select
                                className="form-select"
                                data-cy="appt-service-select"
                                value={draft.serviceId}
                                onChange={(e) => handleServiceChange(e.target.value)}
                            >
                                {allowedServicesForDraft.length === 0 && (
                                    <option value="">
                                        {draft.teamMemberId
                                            ? t('calendar.form.noServices')
                                            : t('calendar.form.selectMemberFirst')}
                                    </option>
                                )}
                                {allowedServicesForDraft.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.name}
                                        {s.totalDuration ? ` (${s.totalDuration} min)` : ""}
                                    </option>
                                ))}
                            </select>
                            {allowedServicesForDraft.length === 0 && (
                                <small className="text-danger">
                                    {draft.teamMemberId
                                        ? t('calendar.form.noServicesHint')
                                        : t('calendar.form.selectMemberFirst')}
                                </small>
                            )}
                        </div>

                        {/* Convênio (health plan) — only for services that accept one. */}
                        {(() => {
                            const svc = services.find((s) => s.id === draft.serviceId);
                            const acceptedPlans = svc?.acceptedPlans ?? [];
                            if (acceptedPlans.length === 0) return null;
                            const isConvenio = draft.paymentType === 'CONVENIO';
                            return (
                                <div className="form-group">
                                    <label>{t('calendar.form.paymentType')}</label>
                                    <div className="gs-segmented" role="group">
                                        <button
                                            type="button"
                                            className={`gs-segmented__btn${!isConvenio ? ' gs-segmented__btn--on' : ''}`}
                                            onClick={() => handlePaymentTypeChange('PARTICULAR')}
                                            data-cy="appt-paytype-particular"
                                        >
                                            {t('calendar.form.payTypeParticular')}
                                        </button>
                                        <button
                                            type="button"
                                            className={`gs-segmented__btn${isConvenio ? ' gs-segmented__btn--on' : ''}`}
                                            onClick={() => handlePaymentTypeChange('CONVENIO')}
                                            data-cy="appt-paytype-convenio"
                                        >
                                            {t('calendar.form.payTypeConvenio')}
                                        </button>
                                    </div>

                                    {isConvenio && (
                                        <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                                            <div>
                                                <label className="small text-muted mb-1">{t('calendar.form.healthPlan')}</label>
                                                <select
                                                    className="form-select"
                                                    data-cy="appt-healthplan-select"
                                                    value={draft.healthPlanId ?? ''}
                                                    onChange={(e) => handleField('healthPlanId', e.target.value || null)}
                                                >
                                                    {acceptedPlans.map((p) => {
                                                        const name = businessHealthPlans.find(
                                                            (h) => String(h.id) === String(p.healthPlanId)
                                                        )?.name ?? `#${p.healthPlanId}`;
                                                        const val = p.negotiatedValue != null
                                                            ? ` — ${currencySymbol(selectedBusiness?.currency)}${Number(p.negotiatedValue).toFixed(2)}`
                                                            : '';
                                                        return (
                                                            <option key={p.healthPlanId} value={String(p.healthPlanId)}>
                                                                {name}{val}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="small text-muted mb-1">{t('calendar.form.copay')}</label>
                                                <MoneyInput
                                                    currency={selectedBusiness?.currency}
                                                    value={copayInput}
                                                    onChange={(v) => {
                                                        setCopayInput(v);
                                                        const n = parseFloat(v.replace(',', '.'));
                                                        handleField('copayAmount', !isNaN(n) && n > 0 ? +n.toFixed(2) : null);
                                                    }}
                                                    placeholder="0.00"
                                                />
                                                <small className="text-muted">{t('calendar.form.copayHint')}</small>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {priceBreakdown && priceBreakdown.isConvenio && (
                            <div className="form-group gs-price-breakdown">
                                <div className="gs-price-breakdown__row">
                                    <span>
                                        {t('calendar.form.tableValue')}
                                        {priceBreakdown.planName && (
                                            <small className="text-muted">{' '}({priceBreakdown.planName})</small>
                                        )}
                                    </span>
                                    <span>
                                        {priceBreakdown.currency}{priceBreakdown.tableValue.toFixed(2)}
                                    </span>
                                </div>
                                <div className="gs-price-breakdown__row">
                                    <span>{t('calendar.convenio.repasse')}</span>
                                    <span>
                                        {priceBreakdown.currency}{priceBreakdown.base.toFixed(2)}
                                    </span>
                                </div>
                                {priceBreakdown.copay > 0 && (
                                    <div className="gs-price-breakdown__row">
                                        <span>{t('calendar.form.copay')}</span>
                                        <span>
                                            {priceBreakdown.currency}{priceBreakdown.copay.toFixed(2)}
                                        </span>
                                    </div>
                                )}
                                <div className="gs-price-breakdown__row gs-price-breakdown__row--total">
                                    <span>{t('calendar.convenio.businessGets')}</span>
                                    <span>
                                        {priceBreakdown.currency}
                                        {(priceBreakdown.base + priceBreakdown.copay).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        )}

                        {priceBreakdown && !priceBreakdown.isConvenio && (
                            <div className="form-group gs-price-breakdown">
                                <div className="gs-price-breakdown__row">
                                    <span>
                                        {t('calendar.payment.servicePrice')}
                                        {priceBreakdown.isVariable && (
                                            <small className="text-muted">
                                                {' '}({t('calendar.form.variablePrice')})
                                            </small>
                                        )}
                                    </span>
                                    {priceBreakdown.isVariable ? (
                                        <MoneyInput
                                            currency={selectedBusiness?.currency}
                                            value={priceInput}
                                            onChange={setPriceInput}
                                            placeholder="0.00"
                                        />
                                    ) : (
                                        <span>
                                            {priceBreakdown.currency}
                                            {priceBreakdown.base.toFixed(2)}
                                        </span>
                                    )}
                                </div>
                                {priceBreakdown.pct > 0 && (
                                    <div className="gs-price-breakdown__row gs-price-breakdown__row--discount">
                                        <span>
                                            {t('calendar.form.discount')}
                                            <small>
                                                {' '}({priceBreakdown.pct}% — {linkedClient?.firstName}
                                                {linkedClient?.lastName ? ` ${linkedClient.lastName}` : ''})
                                            </small>
                                        </span>
                                        <span>
                                            −{priceBreakdown.currency}
                                            {priceBreakdown.discount.toFixed(2)}
                                        </span>
                                    </div>
                                )}
                                {priceBreakdown.extraDiscount > 0 && (
                                    <div className="gs-price-breakdown__row gs-price-breakdown__row--discount">
                                        <span>{t('calendar.payment.extraDiscount')}</span>
                                        <span>
                                            −{priceBreakdown.currency}
                                            {priceBreakdown.extraDiscount.toFixed(2)}
                                        </span>
                                    </div>
                                )}
                                <div className="gs-price-breakdown__row gs-price-breakdown__row--total">
                                    <span>{t('calendar.form.total')}</span>
                                    <span>
                                        {priceBreakdown.currency}
                                        {priceBreakdown.total.toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        )}

                        {slotMember && slotWeekStart && (
                            <div className="form-group gs-slotpick">
                                <label className="small text-muted mb-1">
                                    {t('calendar.form.availableSlots')}
                                </label>
                                <div className="gs-slotpick__head">
                                    <div className="gs-slotpick__nav">
                                        <button
                                            type="button"
                                            className="gs-slotpick__navbtn"
                                            onClick={() => shiftSlotWeek(-7)}
                                            disabled={!canSlotPrev}
                                            aria-label="Semana anterior"
                                        >
                                            ‹
                                        </button>
                                        <button
                                            type="button"
                                            className="gs-slotpick__navbtn"
                                            onClick={() => shiftSlotWeek(7)}
                                            aria-label="Próxima semana"
                                        >
                                            ›
                                        </button>
                                        <button
                                            type="button"
                                            className="gs-slotpick__today"
                                            onClick={slotWeekToday}
                                        >
                                            {t('calendar.toolbar.today')}
                                        </button>
                                    </div>
                                    <span className="gs-slotpick__range">{slotRangeLabel}</span>
                                    <label className="gs-slotpick__cal" title={t('calendar.form.chooseDateTitle')}>
                                        {t('calendar.form.date')}
                                        <input
                                            type="date"
                                            value={slotDay}
                                            min={ymdOf(new Date())}
                                            onChange={(e) => pickSlotDate(e.target.value)}
                                        />
                                    </label>
                                </div>

                                <div className="gs-slotpick__days">
                                    {slotWeekDays.map((d) => (
                                        <button
                                            key={d.ymd}
                                            type="button"
                                            disabled={d.isPast}
                                            className={`gs-slotpick__day${
                                                d.ymd === slotDay ? " is-selected" : ""
                                            }${d.isToday ? " is-today" : ""}${
                                                d.isPast ? " is-past" : ""
                                            }`}
                                            onClick={() => {
                                                setSlotDay(d.ymd);
                                                syncDraftToDay(d.ymd);
                                            }}
                                        >
                                            <span className="gs-slotpick__dlabel">{d.label}</span>
                                            <strong className="gs-slotpick__dnum">{d.dayNum}</strong>
                                            <span
                                                className={`gs-slotpick__dot${
                                                    d.hasSlots ? " on" : ""
                                                }`}
                                            />
                                        </button>
                                    ))}
                                </div>

                                <div className="gs-slotpick__slots">
                                    {selectedDaySlots.length === 0 ? (
                                        <div className="form-text">
                                            {t('calendar.form.noSlotsThisDay')}
                                        </div>
                                    ) : (
                                        selectedDaySlots.map((slot, i) => {
                                            const isSelected =
                                                draft.startTime === toLocalIso(slot.start);
                                            return (
                                                <button
                                                    key={i}
                                                    type="button"
                                                    className={`btn btn-sm ${
                                                        isSelected
                                                            ? "btn-primary"
                                                            : "btn-outline-primary"
                                                    }`}
                                                    onClick={() =>
                                                        setDraft((prev) =>
                                                            prev
                                                                ? {
                                                                      ...prev,
                                                                      startTime: toLocalIso(slot.start),
                                                                      endTime: toLocalIso(slot.end),
                                                                  }
                                                                : prev
                                                        )
                                                    }
                                                >
                                                    {formatClock(slot.start, timeFormat)}
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="row">
                            <div className="col-md-6 form-group">
                                <label>{t('calendar.form.start')} <span className="gs-required">*</span></label>
                                <DateTimeField
                                    data-cy="appt-date-input"
                                    value={draft.startTime}
                                    stepMinutes={bizSettings?.bookingIncrement ?? undefined}
                                    onChange={handleStartChange}
                                />
                            </div>
                            <div className="col-md-6 form-group">
                                <label>{t('calendar.form.end')} <span className="gs-required">*</span></label>
                                <DateTimeField
                                    data-cy="appt-time-input"
                                    value={draft.endTime}
                                    stepMinutes={bizSettings?.bookingIncrement ?? undefined}
                                    onChange={(v) => handleField("endTime", v)}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={handleSuggestSlot}
                                disabled={saving}
                            >
                                💡 {t('calendar.form.findNextSlot')}
                            </button>
                            <div className="form-text">
                                {t('calendar.form.findNextSlotHint')}
                            </div>
                        </div>

                        {/* Recurring booking — create only, and only when the chosen
                            service allows it. Books the free occurrences; the backend
                            reports any skipped for agenda conflicts. */}
                        {!draft.id && selectedServiceAllowsRecurring && (
                            <div className="form-group gs-recurrence">
                                <label className="gs-inline-check">
                                    <input
                                        type="checkbox"
                                        className="gs-inline-check__box"
                                        checked={recurEnabled}
                                        onChange={(e) => setRecurEnabled(e.target.checked)}
                                    />
                                    <span className="gs-inline-check__label">
                                        🔁 {t('calendar.form.repeatAppointment')}
                                    </span>
                                </label>

                                {recurEnabled && (
                                    <div className="gs-recurrence__body">
                                        <label className="mt-2">{t('calendar.form.frequency')}</label>
                                        <div className="gs-chip-row">
                                            {serviceRecurrenceFreqs.map((f) => (
                                                <button
                                                    type="button"
                                                    key={f}
                                                    className={`gs-chip${
                                                        recurFreq === f ? " gs-chip--on" : ""
                                                    }`}
                                                    onClick={() => setRecurFreq(f)}
                                                >
                                                    {t('calendar.freq.' + f)}
                                                </button>
                                            ))}
                                        </div>

                                        {recurFreq === 'WEEKLY_DAYS' && (
                                            <>
                                                <label className="mt-2">{t('calendar.form.weekdays')}</label>
                                                <div className="gs-chip-row">
                                                    {WEEKDAY_OPTIONS.map((d) => {
                                                        const on = recurWeekdays.includes(d.value);
                                                        // Only disable when we actually know the open
                                                        // days (hours configured); otherwise allow all.
                                                        const closed =
                                                            openWeekdays.size > 0 &&
                                                            !openWeekdays.has(d.value);
                                                        return (
                                                            <button
                                                                type="button"
                                                                key={d.value}
                                                                disabled={closed}
                                                                title={closed ? t('calendar.form.closedThisDay') : undefined}
                                                                className={`gs-chip${
                                                                    on ? " gs-chip--on" : ""
                                                                }${closed ? " gs-chip--off" : ""}`}
                                                                onClick={() =>
                                                                    setRecurWeekdays((prev) =>
                                                                        prev.includes(d.value)
                                                                            ? prev.filter((x) => x !== d.value)
                                                                            : [...prev, d.value]
                                                                    )
                                                                }
                                                            >
                                                                {d.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        )}

                                        <label className="mt-2">{t('calendar.form.ends')}</label>
                                        <select
                                            className="form-select"
                                            value={recurEnd}
                                            onChange={(e) =>
                                                setRecurEnd(
                                                    e.target.value as 'count' | 'months' | 'date'
                                                )
                                            }
                                        >
                                            <option value="count">{t('calendar.form.endsAfterCount')}</option>
                                            <option value="months">{t('calendar.form.endsAfterMonths')}</option>
                                            <option value="date">{t('calendar.form.endsOnDate')}</option>
                                        </select>

                                        {recurEnd === 'count' && (
                                            <input
                                                type="number"
                                                min={1}
                                                max={100}
                                                className="form-control mt-2"
                                                value={recurCount}
                                                onChange={(e) =>
                                                    setRecurCount(Math.max(1, Number(e.target.value)))
                                                }
                                            />
                                        )}
                                        {recurEnd === 'months' && (
                                            <select
                                                className="form-select mt-2"
                                                value={recurMonths}
                                                onChange={(e) =>
                                                    setRecurMonths(Number(e.target.value))
                                                }
                                            >
                                                {[1, 3, 6, 12]
                                                    .filter((m) => m <= serviceMaxMonths)
                                                    .map((m) => (
                                                        <option key={m} value={m}>
                                                            {t('calendar.form.monthCount', { count: m })}
                                                        </option>
                                                    ))}
                                            </select>
                                        )}
                                        {recurEnd === 'date' && (
                                            <input
                                                type="date"
                                                className="form-control mt-2"
                                                value={recurUntil}
                                                onChange={(e) => setRecurUntil(e.target.value)}
                                            />
                                        )}

                                        <div className="form-text">
                                            {t('calendar.form.recurrenceHint', {
                                                limit: t('calendar.form.monthCount', { count: serviceMaxMonths }),
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Reschedule proposal — only for saved appointments. Lets a
                            team member suggest a new time the customer must approve. */}
                        {draft.id && (
                            <div className="form-group">
                                {!showReschedule ? (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setShowReschedule(true)}
                                    >
                                        📅 {draft.rescheduleStatus === "PROPOSED"
                                            ? t('calendar.reschedule.suggestAnother')
                                            : t('calendar.reschedule.suggest')}
                                    </button>
                                ) : (
                                    <div className="gs-reschedule">
                                        <div className="row">
                                            <div className="col-md-6 form-group">
                                                <label>{t('calendar.reschedule.newStart')}</label>
                                                <DateTimeField
                                                    value={rescheduleStart}
                                                    stepMinutes={bizSettings?.bookingIncrement ?? undefined}
                                                    onChange={(v) => {
                                                        setRescheduleStart(v);
                                                        // Auto-fill the end from the service duration so the
                                                        // attendant only picks the start.
                                                        const dur = serviceById(draft.serviceId)?.totalDuration ?? 60;
                                                        if (v) setRescheduleEnd(addMinutesLocal(v, dur));
                                                    }}
                                                />
                                            </div>
                                            <div className="col-md-6 form-group">
                                                <label>{t('calendar.reschedule.newEnd')}</label>
                                                <DateTimeField
                                                    value={rescheduleEnd}
                                                    stepMinutes={bizSettings?.bookingIncrement ?? undefined}
                                                    onChange={(v) => setRescheduleEnd(v)}
                                                />
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                onClick={handleProposeReschedule}
                                                disabled={proposing}
                                            >
                                                {proposing ? t('calendar.reschedule.sending') : t('calendar.reschedule.send')}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => setShowReschedule(false)}
                                                disabled={proposing}
                                            >
                                                {t('common.cancel')}
                                            </button>
                                        </div>
                                        <div className="form-text">
                                            {t('calendar.reschedule.hint')}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {pastClientNotes.length > 0 && draft.clientId && (
                            <div className="form-group gs-past-note">
                                <div className="gs-past-note__head">
                                    <span className="gs-past-note__title">
                                        📋 {t('calendar.form.lastNote')} ·{" "}
                                        {new Date(
                                            pastClientNotes[0].startTime
                                        ).toLocaleDateString(undefined, {
                                            month: "short",
                                            day: "numeric",
                                            year:
                                                new Date(
                                                    pastClientNotes[0].startTime
                                                ).getFullYear() === new Date().getFullYear()
                                                    ? undefined
                                                    : "numeric",
                                        })}
                                    </span>
                                </div>
                                <div className="gs-past-note__body">
                                    {pastClientNotes[0].notes}
                                </div>
                                {pastClientNotes.length > 1 && (
                                    <button
                                        type="button"
                                        className="gs-past-note__more"
                                        onClick={() => {
                                            if (!draft.clientId) return;
                                            setPendingClient(
                                                String(draft.clientId),
                                                "notes"
                                            );
                                            closeSidebar();
                                            setActiveMenu(MENU.CLIENT);
                                        }}
                                    >
                                        + {t('calendar.form.morePastNotes', { count: pastClientNotes.length - 1 })} →
                                    </button>
                                )}
                            </div>
                        )}

                        <div className="form-group">
                            <label>{t('calendar.form.notes')}</label>
                            {/* Same story as the prontuário button above: keyed off the
                                service classification, so it needs the module flag too.
                                The hint tells staff to put clinical content in the
                                prontuário, which is advice that makes no sense while the
                                prontuário does not exist. */}
                            {HEALTH_RECORDS_ENABLED && services.find((sv) => sv.id === draft.serviceId)?.isHealthDataResolved === true && (
                                <div className="alert alert-warning py-1 px-2 mb-1" style={{ fontSize: '0.8rem' }} data-cy="appt-notes-health-hint">
                                    {t('calendar.form.healthNotesHint')}
                                </div>
                            )}
                            <textarea
                                className="form-control"
                                data-cy="appt-notes-input"
                                rows={2}
                                maxLength={NOTES_MAX}
                                value={draft.notes}
                                onChange={(e) => handleField("notes", e.target.value.slice(0, NOTES_MAX))}
                            />
                            <div
                                className="gs-char-count"
                                style={{
                                    textAlign: 'right',
                                    fontSize: '0.78rem',
                                    color: (draft.notes?.length ?? 0) >= NOTES_MAX ? 'var(--gs-danger, #dc3545)' : 'var(--gs-text-muted, #6c757d)',
                                }}
                                data-cy="appt-notes-count"
                            >
                                {(draft.notes?.length ?? 0)}/{NOTES_MAX}
                            </div>
                        </div>

                        {/* Rebook reminder (D5): optional date to nudge the client to
                           book again. Saving with a date schedules the "Que tal
                           remarcar?" notification for that day; clearing cancels it. */}
                        <div className="form-group">
                            <label htmlFor="appt-rebook-reminder">{t('calendar.form.rebookReminder')}</label>
                            <input
                                type="date"
                                id="appt-rebook-reminder"
                                className="form-control"
                                data-cy="appt-rebook-reminder-input"
                                value={draft.rebookReminderOn ?? ''}
                                min={ymdOf(new Date())}
                                onChange={(e) => handleField("rebookReminderOn", e.target.value || null)}
                            />
                            <small className="form-text text-muted">
                                {t('calendar.form.rebookReminderHint')}
                            </small>
                        </div>

                    </form>
                        )}
                    </div>
                )}
            </Drawer>

            {/* ---------- Block (time-off) drawer ---------- */}
            <Drawer
                open={showBlock && !!blockDraft}
                onClose={closeBlock}
                title={blockDraft?.id ? t('calendar.block.editTitle') : t('calendar.block.newTitle')}
                subtitle={t('calendar.block.subtitle')}
                size="sm"
                footer={
                    blockDraft && (
                        <div className="d-flex gap-2 justify-content-between w-100">
                            {blockDraft.id ? (
                                <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    data-cy="calendar-block-delete-btn"
                                    onClick={handleDeleteBlock}
                                    disabled={savingBlock}
                                >
                                    {t('calendar.block.remove')}
                                </button>
                            ) : (
                                <span />
                            )}
                            <div className="d-flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    data-cy="calendar-block-save-btn"
                                    onClick={handleSaveBlock}
                                    disabled={savingBlock}
                                >
                                    {savingBlock ? t('calendar.block.saving') : t('common.save')}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-light"
                                    onClick={closeBlock}
                                    disabled={savingBlock}
                                >
                                    {t('common.cancel')}
                                </button>
                            </div>
                        </div>
                    )
                }
            >
                {blockDraft && (
                    <div data-cy="calendar-block-drawer">
                        <div className="form-group">
                            <label>{t('calendar.block.titleLabel')}</label>
                            <input
                                type="text"
                                className="form-control"
                                data-cy="calendar-block-title-input"
                                placeholder={t('calendar.block.titlePlaceholder')}
                                value={blockDraft.title}
                                onChange={(e) =>
                                    setBlockDraft((p) => (p ? { ...p, title: e.target.value } : p))
                                }
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('calendar.block.professional')}</label>
                            <select
                                className="form-select"
                                data-cy="calendar-block-member-select"
                                value={blockDraft.teamMemberId}
                                onChange={(e) =>
                                    setBlockDraft((p) =>
                                        p ? { ...p, teamMemberId: e.target.value } : p
                                    )
                                }
                            >
                                {!restrictOwn && (
                                    <option value="">{t('calendar.block.allProfessionals')}</option>
                                )}
                                {scopedTeamMembers.map((tm) => (
                                    <option key={tm.id} value={String(tm.id)}>
                                        {tm.firstName} {tm.lastName ?? ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="row">
                            <div className="col-md-6 form-group">
                                <label>{t('calendar.block.start')}</label>
                                <DateTimeField
                                    data-cy="calendar-block-start-input"
                                    value={blockDraft.startTime}
                                    stepMinutes={bizSettings?.bookingIncrement ?? undefined}
                                    onChange={(v) =>
                                        setBlockDraft((p) => (p ? { ...p, startTime: v } : p))
                                    }
                                />
                            </div>
                            <div className="col-md-6 form-group">
                                <label>{t('calendar.block.end')}</label>
                                <DateTimeField
                                    data-cy="calendar-block-end-input"
                                    value={blockDraft.endTime}
                                    stepMinutes={bizSettings?.bookingIncrement ?? undefined}
                                    onChange={(v) =>
                                        setBlockDraft((p) => (p ? { ...p, endTime: v } : p))
                                    }
                                />
                            </div>
                        </div>
                        <div className="form-text">
                            {t('calendar.block.hint')}
                        </div>

                        {/* Recurring block — create only. Reuses the recurring-appointment
                            rule + UI; the backend expands it into a series of blocks. */}
                        {!blockDraft.id && (
                            <div className="form-group gs-recurrence">
                                <label className="gs-inline-check">
                                    <input
                                        type="checkbox"
                                        className="gs-inline-check__box"
                                        data-cy="calendar-block-repeat-check"
                                        checked={blockRecurEnabled}
                                        onChange={(e) => setBlockRecurEnabled(e.target.checked)}
                                    />
                                    <span className="gs-inline-check__label">
                                        🔁 {t('calendar.block.repeat')}
                                    </span>
                                </label>

                                {blockRecurEnabled && (
                                    <div className="gs-recurrence__body">
                                        <label className="mt-2">{t('calendar.form.frequency')}</label>
                                        <div className="gs-chip-row">
                                            {FREQ_KEYS.map((f) => (
                                                <button
                                                    type="button"
                                                    key={f}
                                                    className={`gs-chip${blockRecurFreq === f ? " gs-chip--on" : ""}`}
                                                    onClick={() => setBlockRecurFreq(f)}
                                                >
                                                    {t('calendar.freq.' + f)}
                                                </button>
                                            ))}
                                        </div>

                                        {blockRecurFreq === 'WEEKLY_DAYS' && (
                                            <>
                                                <label className="mt-2">{t('calendar.form.weekdays')}</label>
                                                <div className="gs-chip-row">
                                                    {WEEKDAY_OPTIONS.map((d) => {
                                                        const on = blockRecurWeekdays.includes(d.value);
                                                        const closed = openWeekdays.size > 0 && !openWeekdays.has(d.value);
                                                        return (
                                                            <button
                                                                type="button"
                                                                key={d.value}
                                                                disabled={closed}
                                                                title={closed ? t('calendar.form.closedThisDay') : undefined}
                                                                className={`gs-chip${on ? " gs-chip--on" : ""}${closed ? " gs-chip--off" : ""}`}
                                                                onClick={() =>
                                                                    setBlockRecurWeekdays((prev) =>
                                                                        prev.includes(d.value)
                                                                            ? prev.filter((x) => x !== d.value)
                                                                            : [...prev, d.value]
                                                                    )
                                                                }
                                                            >
                                                                {d.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        )}

                                        <label className="mt-2">{t('calendar.form.ends')}</label>
                                        <select
                                            className="form-select"
                                            value={blockRecurEnd}
                                            onChange={(e) => setBlockRecurEnd(e.target.value as 'count' | 'months' | 'date')}
                                        >
                                            <option value="count">{t('calendar.form.endsAfterCount')}</option>
                                            <option value="months">{t('calendar.form.endsAfterMonths')}</option>
                                            <option value="date">{t('calendar.form.endsOnDate')}</option>
                                        </select>

                                        {blockRecurEnd === 'count' && (
                                            <input
                                                type="number"
                                                min={1}
                                                max={100}
                                                className="form-control mt-2"
                                                value={blockRecurCount}
                                                onChange={(e) => setBlockRecurCount(Math.max(1, Number(e.target.value)))}
                                            />
                                        )}
                                        {blockRecurEnd === 'months' && (
                                            <select
                                                className="form-select mt-2"
                                                value={blockRecurMonths}
                                                onChange={(e) => setBlockRecurMonths(Number(e.target.value))}
                                            >
                                                {[1, 3, 6, 12].map((m) => (
                                                    <option key={m} value={m}>
                                                        {t('calendar.form.monthCount', { count: m })}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                        {blockRecurEnd === 'date' && (
                                            <input
                                                type="date"
                                                className="form-control mt-2"
                                                value={blockRecurUntil}
                                                onChange={(e) => setBlockRecurUntil(e.target.value)}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </Drawer>

            {/* Group event / workshop drawer — separate from the appointment one. */}
            <EventDrawer
                open={eventDrawerOpen}
                eventId={eventDrawerId}
                initialStart={eventDrawerStart}
                businessId={businessIdStr}
                currency={selectedBusiness?.currency}
                country={businessCountry}
                eventTypes={eventTypes}
                teamMembers={scopedTeamMembers}
                stepMinutes={bizSettings?.bookingIncrement ?? undefined}
                onClose={() => setEventDrawerOpen(false)}
                onChanged={reloadAppointments}
            />

                    {clinicalApptId && (
                <ClinicalRecordModal
                    businessId={Number(selectedBusiness?.id) || 0}
                    appointmentId={clinicalApptId}
                    onClose={() => setClinicalApptId(null)}
                />
            )}
            </div>
    );
};

export default CalendarComponent;
